/**
 * Sea of Solana client entry. Wires the network store, local-ship prediction,
 * the Three.js renderer and the touch HUD into one fixed-input game loop.
 */
import { AIM, AMMO, AmmoType, BASE, BOUNTY, CANNON, CARTOGRAPHER, CHEST, chestCapFor, chestRiskMultiplier, ClanOp, MarkOp, CONVOY, ECONOMY, EscortOp, INPUT_HZ, SHIP_CLASS, ShipClassId, SINK_FX, STORM, WORLD_EDGE, EventType, ShipFlag, FISHING, FishTier, fishSpeciesRarity, WRECK, GrappleEnd, MaterialId, RaidOutcome, materialById, buffOf, CONSUMABLE_IDS, BottleKind, WhaleState, KRAKEN, krakenStateAt, CALENDAR, WorldEventId, PORT, PortOp, WalkBtn, OUTPOST_RADIUS, CAPITAL, CasinoGame, RoomKind, HouseOp, HOUSE_OWN_DOCK, TREASURE_ISLAND_COLLIDE_FRAC, layoutForOutpost, isCapitalOutpost, type PortStation, type PortLayout, type LandmarkPiece, type RaidResultMsg } from '@piratas/protocol';
import type { Obstacle } from '@piratas/sim';
import { cargoSpeedMul } from '@piratas/sim';
import { Btn, applySeatOverride, topSpeedMs, cargoOf, cargoDeliveryValue, cargoDistanceMult, PUSH_OPTIN, amuletMinimapMul, type SeatConfig } from '@piratas/protocol';
import { CombatFx } from './combatfx.js';
import { DiveFx } from './divefx.js';
import { diffHoldGain, holdCounts } from './lootdiff.js';
import { solveIntercept, selectLeadTarget } from './aimlead.js';
import { PortScene } from './portscene.js';
import { HouseScene } from './housescene.js';
import { Coach } from './coach.js';
import { rejectPresentation } from './rejectui.js';
import { Primer } from './primer.js';
import { UxHints } from './uxhints.js';
import { Opportunities, type OppState } from './opportunities.js';
import { PortHud } from './porthud.js';
import { PortPrediction } from './portprediction.js';
import { HousePrediction } from './houseprediction.js';
import type { DecorateHandle } from './decorate.js';
// ── Post-spawn UI, LAZY-LOADED (see bootGameUi) ──────────────────────────────────────────────────────────────
// None of these are on the critical "render the sea + net + HUD" path: the Captain's Book + its chapters, the
// casino tables, and chat/voice are only reached by a discrete user action (tap 📖 / step into a port station /
// tap 💬) that can't happen in the first moment at sea. So they are imported for their TYPES only here and pulled
// as their OWN chunks by dynamic import() inside bootGameUi() — kept OUT of the boot bundle so the menu paints
// and the sea spawns sooner on slow 4G. The whole cluster streams in the background right after the loop starts.
import type { BasePanel } from './basepanel.js';
import type { ClanMenu } from './clanmenu.js';
import type { MarketMenu } from './marketmenu.js';
import type { ExchangeMenu } from './exchangemenu.js';
import type { PremiumMenu } from './premiummenu.js';
import type { DragaMenu } from './dragamenu.js';
import type { DragaScene } from './dragascene.js';
import { ReplayPlayer } from './replayplayer.js';
import { setKillcamStyle, getKillcamStyle, type StyleName as KillcamStyle } from './shotdirector.js';
import type { KillTape } from './killtape.js';
import type { StoresMenu } from './storesmenu.js';
import { ProfNotifier } from './proftoast.js';
import { holdSummaryText } from './carrystate.js';
import { isIconPath } from './itemicon.js';
import { seedMenuName, nameToSend, shouldPersistName } from './menuname.js';
import type { FreightMenu } from './freightmenu.js';
import type { AuctionMenu } from './auctionmenu.js';
import type { CasinoMenu } from './casinomenu.js';
import type { CaptainsBook } from './captainsbook.js';
import type { ChatPanel } from './chat.js';
import type { VoiceEngine } from './voice.js';
import { DawnReport } from './dawnreport.js';
import { localNotify, enablePush } from './pushclient.js';
import { shouldSchedulePushAsk } from './pushoptin.js';
import { auroraOverride, autoStart, resolveServerUrl, crewParam, withCrew, shareInvite, weatherOverride, whalePreview } from './config.js';
import { previewMarkerAt } from './whale.js';
import { detectWebview, mobileOs, androidIntentUrl, detectWalletBrowser, lightModeDecision, type WebviewVerdict, type WalletVerdict, type WalletProviders } from './platform.js';
import { initTelemetry, telemetryBoot, telemetryMenuShown, telemetryFirstSpawn, telemetryFirstKill, telemetryWebglUnsupported, telemetryFunnel, telemetryPrimerSeen, telemetryPrimerReopened, telemetryPrimerDismissed, telemetryTutStep, telemetryTutSkip, telemetryTutDone, telemetrySeaControl, telemetrySeaShot, telemetrySea60 } from './telemetry.js';
import { getSettings, getSetting, setSetting, onSettingChange, volumeGain } from './settings.js';
import { applyHudLayout } from './hudlayout.js';
import { Hud } from './hud.js';
import { autoSail } from './autosail.js';
import { Net, type InterpBall } from './net.js';
import { Prediction } from './prediction.js';
import { Renderer } from './render.js';
import { Audio } from './audio.js';

const INPUT_DT = 1 / INPUT_HZ;

// Install error tracking (window.onerror + unhandledrejection → `client_error` beacon) BEFORE anything else boots,
// so a crash while constructing the renderer/net/HUD below is still reported. initTelemetry() is idempotent — the
// game branch calls it again (with telemetryBoot()) further down; this early call only wires the crash handlers.
initTelemetry();

const canvas = document.getElementById('game') as HTMLCanvasElement;
const overlay = document.getElementById('overlay') as HTMLElement;

const net = new Net();
const prediction = new Prediction();

/* Parchment tokens for the boot dead-screens (inlined, NOT imported — this path must render on hardware that
 * just failed WebGL and must never throw, so it depends on nothing but the DOM). Mirrors parchment.ts's palette:
 * aged paper #efe1ba→#e4cf9b→#cdac71, ink #3b2712, gilt #c8a05a, brass #f0d48a→#c99a3a→#7a5416, wood frame.
 * Declared ABOVE the renderer try/catch so they're initialised before the catch can call showWebglFallback (a
 * const referenced in the temporal dead zone would throw inside the fallback and leave the black screen). */
const PARCH_BG = 'radial-gradient(120% 90% at 50% 10%,#5c3d1c 0%,#3a2410 60%,#241505 100%)';
const PARCH_CARD = 'background:linear-gradient(158deg,#efe1ba,#e4cf9b 52%,#cdac71);border:2px solid #c8a05a;box-shadow:0 0 0 1px rgba(58,40,16,.5),0 18px 44px rgba(0,0,0,.55),inset 0 1px 0 rgba(255,248,225,.5);border-radius:12px;';
const PARCH_H1 = "font-family:'Cinzel',Georgia,'Times New Roman',serif;font-weight:900;letter-spacing:.02em;font-size:clamp(21px,5.6vw,30px);line-height:1.1;color:#4a2f10;text-shadow:0 1px 0 rgba(255,248,225,.55);margin:0;";
const PARCH_P = "font-family:Georgia,'Times New Roman',serif;font-size:14.5px;line-height:1.5;color:#42280a;margin:0;";
const BTN_GREEN = 'display:inline-block;padding:14px 26px;border-radius:12px;border:0;background:linear-gradient(180deg,#00e006,#00a804);color:#032109;font-weight:800;font-size:16px;letter-spacing:.05em;font-family:"Cinzel",Georgia,serif;cursor:pointer;box-shadow:0 8px 22px rgba(0,0,0,.4);text-decoration:none;text-align:center;';
const BTN_BRASS = 'padding:12px 22px;border-radius:11px;border:1px solid #7a5416;background:linear-gradient(180deg,#f0d48a,#c99a3a 60%,#a5771f);color:#3a2410;font-weight:800;font-size:15px;letter-spacing:.03em;font-family:"Cinzel",Georgia,serif;cursor:pointer;box-shadow:0 6px 16px rgba(0,0,0,.35),inset 0 1px 0 rgba(255,248,225,.5);';

// TELA MORTA NUNCA MAIS: constructing the Three.js renderer creates the WebGL2 context, which THROWS on a device
// without WebGL2 / with a blacklisted GPU (the exact prod error "THREE.WebGLRenderer: Error creating WebGL context").
// Left uncaught this halts the module before the menu ever builds → a silent black screen. Catch it, show a friendly
// diegetic fallback page with real fixes, beacon the dedicated `webgl_unsupported` signal, then rethrow to stop boot
// (telemetry suppresses the follow-on cascade). No WebGL → no game, but never a dead screen with no explanation.
let renderer: Renderer;
try {
  renderer = new Renderer(canvas);
} catch (err) {
  const wv = currentWebview();
  telemetryWebglUnsupported(String((err as { message?: string })?.message || err || 'renderer-init-failed'), wv.webview);
  showWebglFallback(wv);
  throw err; // halt boot; bootHalted (set above) mutes the downstream error handlers
}

/** The in-app-webview verdict for THIS browser (UA + standalone/touch runtime signals). Computed on demand so
 *  both the dead-screen path and the "you're in an in-app browser" banner read one consistent answer. */
function currentWebview(): WebviewVerdict {
  try {
    const ua = navigator.userAgent || '';
    const standalone = !!(navigator as Navigator & { standalone?: boolean }).standalone
      || !!window.matchMedia?.('(display-mode: standalone)').matches;
    const touch = (navigator.maxTouchPoints || 0) > 1;
    return detectWebview(ua, { standalone, touch });
  } catch { return { webview: false, app: '' }; }
}

/** Read the injected web3 providers a crypto-wallet in-app browser exposes on `window` (Phantom injects
 *  `window.phantom` / `window.solana`; EVM wallets inject `window.ethereum` with an `is*` flag). Pure of throws. */
function walletProviders(): WalletProviders {
  try {
    const w = window as unknown as { phantom?: unknown; solana?: { isPhantom?: boolean }; ethereum?: Record<string, boolean>; trustwallet?: unknown };
    const eth = w.ethereum;
    return {
      phantom: !!w.phantom,
      solana: !!w.solana,
      ethereum: !!eth,
      isMetaMask: !!eth?.isMetaMask,
      isCoinbase: !!(eth?.isCoinbaseWallet || (w as { coinbaseWalletExtension?: unknown }).coinbaseWalletExtension),
      isTrust: !!(eth?.isTrust || w.trustwallet),
      isRainbow: !!eth?.isRainbow,
      isPhantomEth: !!eth?.isPhantom,
    };
  } catch { return {}; }
}

/** The crypto-wallet-browser verdict for THIS page (UA + injected providers). Computed on demand so the light-mode
 *  gate and the dismissible "open in Safari" banner read one consistent answer. */
function currentWallet(): WalletVerdict {
  try {
    const ua = navigator.userAgent || '';
    const standalone = !!(navigator as Navigator & { standalone?: boolean }).standalone
      || !!window.matchMedia?.('(display-mode: standalone)').matches;
    const touch = (navigator.maxTouchPoints || 0) > 1;
    return detectWalletBrowser(ua, { providers: walletProviders(), standalone, touch });
  } catch { return { wallet: false, app: '' }; }
}

// LIGHT MODE — decide ONCE at boot whether this client runs the memory-frugal profile (wallet in-app browser,
// iOS WKWebView, or a low-RAM phone). Applied to the sea renderer immediately (before the first frame, so there's
// no flash) and mirrored into the walkable port the moment it's constructed. Never fires on desktop.
const lightModeActive = (() => {
  try {
    const ua = navigator.userAgent || '';
    const touch = (navigator.maxTouchPoints || 0) > 1;
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    return lightModeDecision({
      walletWebview: currentWallet().wallet,
      webview: currentWebview().webview,
      deviceMemory,
      os: mobileOs(ua, { touch }),
    });
  } catch { return false; }
})();
if (lightModeActive) { try { renderer.setLowMemoryMode(true); } catch { /* never let the memory profile break boot */ } }

/**
 * The diegetic dead-screen — shown INSTEAD of a silent black screen when the renderer can't get a WebGL2 context.
 * TWO faces, both in the parchment chart language (owner law: never a dark-glass panel):
 *   • In an in-app webview (X/Telegram/Instagram/…): an "Open in your browser" escape hatch — an Android intent
 *     button that force-opens the system browser, or iOS instructions + a copy-link button. This is the single
 *     highest-leverage save for social traffic (the webview simply can't draw WebGL2).
 *   • A real browser without WebGL2 (old browser, blacklisted GPU, HW-accel off, locked-down device): actionable
 *     fixes (update / enable acceleration / try Chrome or Safari).
 * Pure DOM (no Three, no canvas), so it renders on the very hardware that just failed WebGL. Never throws.
 */
function showWebglFallback(wv: WebviewVerdict = { webview: false, app: '' }): void {
  try {
    document.getElementById('boot')?.remove();     // replace the loading splash
    const g = document.getElementById('game'); if (g) g.style.display = 'none';
    if (document.getElementById('webgl-fallback')) return; // idempotent
    const el = document.createElement('div');
    el.id = 'webgl-fallback';
    el.style.cssText = `position:fixed;inset:0;z-index:9500;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:6vh 20px;text-align:center;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;background:${PARCH_BG};overflow-y:auto;`;
    const os = mobileOs(navigator.userAgent || '', { touch: (navigator.maxTouchPoints || 0) > 1 });
    const via = wv.app && wv.app !== 'in-app browser' ? `inside <b>${wv.app}</b>` : 'inside an in-app browser';

    if (wv.webview) {
      // ── "Open in your browser" — the social-traffic escape hatch ────────────────────────────────────────
      const androidBtn = os === 'android'
        ? `<a id="wg-open" href="${androidIntentUrl(location.href)}" style="${BTN_GREEN}">▶ Open in Browser</a>`
        : '';
      const iosSteps = os === 'ios'
        ? `<div style="${PARCH_P};text-align:left;display:flex;flex-direction:column;gap:9px;">
             <div>1 · Tap the <b>•••</b> or <b>“Aa”</b> menu (top corner of this window).</div>
             <div>2 · Choose <b>“Open in Browser”</b> or <b>“Open in Safari”.</b></div>
           </div>`
        : '';
      el.innerHTML = `
        <div style="font-size:46px;filter:drop-shadow(0 3px 8px rgba(0,0,0,.5));">🧭</div>
        <div style="${PARCH_CARD}max-width:440px;width:100%;padding:24px 22px 22px;display:flex;flex-direction:column;align-items:center;gap:15px;">
          <h1 style="${PARCH_H1}">Open in your browser to set sail</h1>
          <p style="${PARCH_P}">You're viewing Sea of Solana ${via}. This little window can't draw the 3D seas — open it in your <b>browser</b> and the fleet awaits.</p>
          ${androidBtn}
          ${iosSteps}
          <button id="wg-copy" style="${BTN_BRASS}">🔗 Copy game link</button>
          <p style="${PARCH_P};font-size:12.5px;opacity:.85;">Then paste it into Chrome or Safari. Same captain, same waters.</p>
        </div>`;
      document.body.appendChild(el);
      wireCopyLink(el.querySelector('#wg-copy'));
    } else {
      // ── Real browser, no WebGL2 — actionable fixes ──────────────────────────────────────────────────────
      const chrome = /Chrome|CriOS/i.test(navigator.userAgent) && !/Edg/i.test(navigator.userAgent);
      const accelPath = chrome
        ? 'In Chrome: ⋮ menu → Settings → System → turn ON "Use graphics acceleration when available", then relaunch.'
        : 'Turn on hardware/graphics acceleration in your browser settings, then reload.';
      el.innerHTML = `
        <div style="font-size:46px;filter:drop-shadow(0 3px 8px rgba(0,0,0,.5));">🔭</div>
        <div style="${PARCH_CARD}max-width:460px;width:100%;padding:24px 22px 22px;display:flex;flex-direction:column;align-items:center;gap:14px;">
          <h1 style="${PARCH_H1}">Yer spyglass be too foggy, Captain</h1>
          <p style="${PARCH_P}">Sea of Solana sails on <b>WebGL2</b> — the 3D graphics your browser draws the ocean with. This device or browser isn't giving us one. A few ways to clear the fog:</p>
          <ul style="list-style:none;text-align:left;display:flex;flex-direction:column;gap:10px;${PARCH_P};font-size:13.5px;padding:0;margin:0;">
            <li>🧭 <b>Update your browser</b> to the latest (or a fresh Chrome / Edge / Firefox / Safari).</li>
            <li>⚙️ <b>Enable hardware acceleration.</b> ${accelPath}</li>
            <li>🖥️ <b>Try another browser or device.</b> Chrome and Edge on a modern phone or computer work best.</li>
            <li>🔒 On a <b>work or school device</b>, WebGL may be blocked by policy — try your own device.</li>
          </ul>
          <p style="font-family:'Cinzel',Georgia,serif;font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:#6a4c22;margin:2px 0 0;">Chrome · Edge · Firefox · Safari 15+</p>
          <button id="wg-retry" style="${BTN_GREEN}">⚓ Try again</button>
        </div>`;
      document.body.appendChild(el);
      el.querySelector('#wg-retry')?.addEventListener('click', () => { try { location.reload(); } catch { /* noop */ } });
    }
  } catch { /* the fallback page itself must never throw — worst case, the black screen remains */ }
}

/** Copy the current game URL to the clipboard (webview escape hatch). Gives quick brass-button feedback. */
function wireCopyLink(btn: Element | null): void {
  if (!(btn instanceof HTMLElement)) return;
  btn.addEventListener('click', () => {
    const done = (): void => { btn.textContent = '✓ Link copied — paste in your browser'; };
    try { void navigator.clipboard?.writeText(location.href).then(done).catch(() => { done(); }); }
    catch { done(); }
  });
}

/**
 * A discreet, DISMISSIBLE parchment banner for a webview that CAN render (WebGL2 works) — a gentle nudge that the
 * full-screen browser is nicer, without ever blocking play (owner rule: zero false-blocks). Shown once per device
 * (a dismissal sticks). No-op outside a webview, on the ?auto path, or once dismissed. Never throws.
 */
function maybeWebviewBanner(): void {
  try {
    if (autoStart().auto) return;
    // Onda wallet-fix: a crypto-wallet in-app browser (Phantom/MetaMask/…) gets a wallet-specific nudge toward
    // Safari; a plain social webview keeps the generic "open in your browser" copy. Either qualifies.
    const wallet = currentWallet();
    const isWebview = currentWebview().webview;
    if (!isWebview && !wallet.wallet) return;
    if (document.getElementById('wv-banner')) return;
    try { if (localStorage.getItem('sor:wvBannerDismissed') === '1') return; } catch { /* private mode → show it */ }
    const os = mobileOs(navigator.userAgent || '', { touch: (navigator.maxTouchPoints || 0) > 1 });
    const bar = document.createElement('div');
    bar.id = 'wv-banner';
    bar.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(env(safe-area-inset-bottom,0px) + 10px);z-index:40;display:flex;align-items:center;gap:10px;max-width:min(94vw,430px);padding:9px 12px;border-radius:11px;font-family:Georgia,serif;font-size:12.5px;line-height:1.3;color:#3a2410;background:linear-gradient(158deg,#efe1ba,#e4cf9b 55%,#cdac71);border:1px solid #c8a05a;box-shadow:0 8px 22px rgba(0,0,0,.45),inset 0 1px 0 rgba(255,248,225,.5);';
    // A wallet browser can't spare a full Safari's memory, so we steer to Safari with Copy link (there's no reliable
    // programmatic escape on iOS). A plain Android social webview keeps its one-tap Chrome intent.
    const action = os === 'android' && !wallet.wallet
      ? `<a href="${androidIntentUrl(location.href)}" style="flex:0 0 auto;padding:7px 12px;border-radius:9px;background:linear-gradient(180deg,#00e006,#00a804);color:#032109;font-weight:800;text-decoration:none;font-family:'Cinzel',Georgia,serif;font-size:12.5px;">Open ▸</a>`
      : `<button id="wv-copy" style="flex:0 0 auto;padding:7px 12px;border-radius:9px;border:1px solid #7a5416;background:linear-gradient(180deg,#f0d48a,#c99a3a 60%,#a5771f);color:#3a2410;font-weight:800;font-family:'Cinzel',Georgia,serif;font-size:12.5px;cursor:pointer;">Copy link</button>`;
    const label = wallet.wallet
      ? `⚓ You're in the <b>${wallet.app}</b> browser — for the smoothest sail, open Sea of Solana in <b>Safari</b>.`
      : `🧭 For the best voyage, open in your <b>browser</b>.`;
    bar.innerHTML = `<span style="flex:1 1 auto;">${label}</span>${action}<button id="wv-x" aria-label="Dismiss" style="flex:0 0 auto;width:26px;height:26px;border-radius:8px;border:0;background:rgba(58,40,16,.14);color:#3a2410;font-size:15px;cursor:pointer;">✕</button>`;
    document.body.appendChild(bar);
    wireCopyLink(bar.querySelector('#wv-copy'));
    bar.querySelector('#wv-x')?.addEventListener('click', () => {
      bar.remove();
      try { localStorage.setItem('sor:wvBannerDismissed', '1'); } catch { /* private mode */ }
    });
  } catch { /* the banner is a bonus — never let it break the boot */ }
}

/**
 * TELA AZUL NUNCA MAIS — the diegetic recovery card shown when the WebGL context is LOST (a WKWebView, e.g. the
 * Phantom in-app browser, ran out of memory). The renderer preventDefault()s the loss so the page survives, then
 * fires this: a parchment "ran low on memory — reload" card INSTEAD of the silent blue/white screen the wallet
 * webview would otherwise show. Pure DOM (the GL context is dead), EN copy, never throws. Reloading is the honest
 * recovery — a fresh boot re-enters light mode with the whole port fitting under the memory ceiling. Idempotent.
 */
function showMemoryReloadCard(): void {
  try {
    if (document.getElementById('mem-reload')) return; // idempotent — one card per lost context
    const el = document.createElement('div');
    el.id = 'mem-reload';
    el.style.cssText = `position:fixed;inset:0;z-index:9600;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:6vh 20px;text-align:center;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;background:${PARCH_BG};`;
    el.innerHTML = `
      <div style="font-size:46px;filter:drop-shadow(0 3px 8px rgba(0,0,0,.5));">🪸</div>
      <div style="${PARCH_CARD}max-width:440px;width:100%;padding:24px 22px 22px;display:flex;flex-direction:column;align-items:center;gap:15px;">
        <h1 style="${PARCH_H1}">The seas ran low on memory</h1>
        <p style="${PARCH_P}">Your in-app browser is short on memory, Captain. Tap below to hoist the sails again — or open Sea of Solana in <b>Safari</b> for the smoothest voyage.</p>
        <button id="mem-reload-btn" style="${BTN_GREEN}">⚓ Reload</button>
      </div>`;
    document.body.appendChild(el);
    el.querySelector('#mem-reload-btn')?.addEventListener('click', () => { try { location.reload(); } catch { /* noop */ } });
  } catch { /* the card is best-effort — worst case the reader still has the (frozen) canvas, never a crash here */ }
}
renderer.onContextLost = showMemoryReloadCard;

/** Fade + remove the instant boot splash (index.html #boot) once real UI — the menu, the game, or an editor — is
 *  up. Idempotent; safe to call from every entry path. */
function hideBootSplash(): void {
  const b = document.getElementById('boot');
  if (!b) return;
  b.classList.add('hide');
  setTimeout(() => b.remove(), 520);
}

/* ----------------------------- audio ----------------------------- */
const audio = new Audio();
const armAudio = (): void => { audio.resume(); audio.startAmbient(); }; // browsers need a gesture first
const muteBtn = document.createElement('button');
muteBtn.id = 'mutebtn'; // HUD Forge: a stable id so the layout editor can target it
muteBtn.textContent = '🔊';
// paired beside the 📖 Book toggle (the only two floating buttons at sea). Mute lives on document.body so it
// still works ashore, where the sea overlay (and the 📖 toggle) are hidden.
muteBtn.style.cssText = 'position:fixed;z-index:60;width:44px;height:44px;border-radius:12px;border:1px solid rgba(232,192,116,.34);background:linear-gradient(165deg,rgba(16,30,46,.86),rgba(6,14,26,.9));color:#eef6ff;font-size:18px;cursor:pointer;box-shadow:0 6px 18px rgba(0,8,16,.5),inset 0 1px 0 rgba(255,255,255,.07);';
// Floating dock (owner: "quanto menos informação, melhor pro PvP") — the sea keeps exactly TWO
// floaters: the labelled 📖 MENU pill (the Book is the hub for everything else — stores, mute lives
// in Settings, ranking/journal to follow) and the 💬 chat toggle. 🔊/🎒 shortcuts left the sea.
// One stylesheet, both orientations; landscape docks along the top edge with a real gap from the
// status card (owner: "a barrinha está em cima dos status").
// PORTRAIT: with the Sea Journal / Contracts / Top Captains panels now gone from the portrait sea
// (they moved into the Book), the left rail is free — so the row rises from +132 to +120 to sit almost
// glued under the status card (owner: "o MENU e o chat mais pra cima, quase colado no HUD principal").
// +120 is the tightest no-overlap line: the status card's own bottom is ~101px empty-handed and grows to
// ~120px the moment you pick up treasure (the chest chip appears), so the pill parks flush with that
// carrying state and never collides with the topbar chips at 390×844 or on ≤360px phones. Player HUD
// Forge offsets still win (they're inline styles set with higher priority by the editor).
{
  const st = document.createElement('style');
  st.id = 'float-dock';
  st.textContent = `
#mutebtn, #invbtn { display:none !important; }
/* left column GRID (owner: "menu e chat tortos"): the MENU pill + chat share the status card's exact
   left edge (10px + safe inset) and one row line; the chat slot starts where the pill's 110px lane ends. */
#bktoggle{left:calc(env(safe-area-inset-left,0px) + 10px);top:calc(env(safe-area-inset-top,8px) + 120px);}
.cc-chat-toggle{left:calc(env(safe-area-inset-left,0px) + 120px);top:calc(env(safe-area-inset-top,8px) + 120px);}
.cc-chat-peek{left:calc(env(safe-area-inset-left,0px) + 172px);top:calc(env(safe-area-inset-top,8px) + 126px);}
@media (orientation:landscape) and (max-height:540px){
  #bktoggle{left:calc(env(safe-area-inset-left,0px) + 208px);top:calc(env(safe-area-inset-top,8px) + 6px);}
  .cc-chat-toggle{left:calc(env(safe-area-inset-left,0px) + 318px);top:calc(env(safe-area-inset-top,8px) + 6px);}
  .cc-chat-peek{left:calc(env(safe-area-inset-left,0px) + 208px);top:calc(env(safe-area-inset-top,8px) + 56px);}
}
/* The Book modal lives INSIDE #overlay, whose position:fixed makes it a stacking context pinned at
   layer 0 — so ANY z-indexed body-level floater (event chip z35, Enter buttons z36, XP toast z8)
   paints OVER the open Book (the owner's screenshot). While the Book is open, world floaters hide. */
body.bk-open #eventchip, body.bk-open #enterportbtn, body.bk-open #enterhousebtn,
body.bk-open #portpopchip, body.bk-open #profxp { display:none !important; }`;
  document.head.appendChild(st);
}
// Apply all client settings — render quality, per-category audio gain, camera feel, mute. Runs once at boot
// (before the first frame, so there's no flash) and again on every live change from the Settings chapter.
// The settings store is the single source of truth; the sea-HUD 🔊 button just flips its `muted` flag.
const applySettings = (s = getSettings()): void => {
  renderer.setGraphicsPreset(s.graphics);
  renderer.setCameraSensitivity(s.camSensitivity);
  renderer.setInvertCameraDrag(s.invertCam);
  audio.setMasterVolume(volumeGain(s.volMaster));
  audio.setSfxVolume(volumeGain(s.volSfx));
  audio.setAmbianceVolume(volumeGain(s.volAmbiance));
  audio.setMusicVolume(volumeGain(s.volAmbiance)); // Ambiance & music share one slider
  audio.setVoiceVolume(volumeGain(s.volVoice)); // Onda Social: incoming voice-chat bus
  audio.setMuted(s.muted);
  muteBtn.textContent = s.muted ? '🔇' : '🔊';
};
applySettings();
onSettingChange((s) => applySettings(s));
const toggleMute = (): void => { armAudio(); setSetting('muted', !getSetting('muted')); };
muteBtn.addEventListener('pointerdown', (e) => { e.preventDefault(); toggleMute(); });
document.body.appendChild(muteBtn);
window.addEventListener('pointerdown', armAudio);

/* ----------------------------- PWA: installability + keep-awake ----------------------------- */
// Register the service worker — required for the Chrome/Android install prompt (the manifest rides in
// index.html). The sw only passes navigations through (no caching) and handles push; the push opt-in from
// Settings reuses this same registration.
// OFF THE CRITICAL PATH + ABORT-TOLERANT: the sw is not needed for the first paint, so we register it AFTER
// the first frame / when the main thread is idle. And register() returns a promise that REJECTS with an
// AbortError when the captain closes the tab before it finishes — a huge chunk of the prod `client_error`
// noise was exactly this (a captain leaving mid-boot, not a bug). We attach a `.catch` so that rejection is
// swallowed and never surfaces as an uncaught rejection / funnel error.
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  try {
    void navigator.serviceWorker.register('/sw.js').catch(() => { /* tab closed mid-register (AbortError) / unsupported — non-fatal */ });
  } catch { /* older browsers that throw synchronously */ }
}
{
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (ric) ric(() => registerServiceWorker(), { timeout: 3000 });
  else requestAnimationFrame(() => setTimeout(registerServiceWorker, 0)); // after first paint on browsers w/o rIC
}
// Screen Wake Lock — a phone that sleeps mid-voyage is a sunk ship. The lock auto-releases when the tab
// hides; re-acquired on return. Silent no-op where unsupported (old iOS) or on low battery.
let wakeLock: { release(): Promise<void>; addEventListener?: (t: string, cb: () => void) => void } | null = null;
async function armWakeLock(): Promise<void> {
  try {
    const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<NonNullable<typeof wakeLock>> } }).wakeLock;
    if (!wl || document.hidden || wakeLock) return;
    wakeLock = await wl.request('screen');
    wakeLock.addEventListener?.('release', () => { wakeLock = null; });
  } catch { /* denied (battery saver) or unsupported — fine */ }
}
void armWakeLock();
document.addEventListener('visibilitychange', () => { if (!document.hidden) void armWakeLock(); });
let vHeld = false; // desktop push-to-talk (V) held state
window.addEventListener('keydown', (e) => {
  armAudio();
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) return; // don't hijack typed keys
  const k = e.key.toLowerCase();
  if (k === 'm') toggleMute();
  else if (k === 'escape' && killcam?.isActive()) killcam.close(); // bail out of a kill-cam replay
  else if (k === 'h' && !killcam?.isActive()) killcam?.hotkey(net.yourId); // manual highlight capture (also the ★ button)
  else if (k === 'b') { if (!hudEditOpen) captainsBook?.toggleOpen(); } // the Captain's Book is the whole menu now
  else if (k === 'v' && chatPanel && !vHeld) { // Onda Social: hold-to-talk (first press asks for the mic)
    vHeld = true;
    if (chatPanel.voiceReady()) chatPanel.pttKeyDown(); else void chatPanel.requestVoice();
  }
});
window.addEventListener('keyup', (e) => {
  if (e.key.toLowerCase() === 'v') { vHeld = false; chatPanel?.pttKeyUp(); }
});
renderer.onSplash = (x, z, big) => { const s = spatialOf(x, z); audio.splash(s.d, s.pan, big); };

/* ----------------------------- Creative Mode "Save Online" (city/capital overrides) -----------------------------
 * A saved city/capital layout (from the editor's Save Online) drives BOTH the port render AND the local walker
 * prediction, in place of the shipped layout, when a captain steps into the matching outpost. The SERVER is now
 * authoritative on this slice too (RoomManager.setPortLayoutOverride → PortRoom.applyLayout), so this is no longer
 * "render-only" — the client MUST predict against the SAME slice the server collides against, or the walker
 * rubber-bands into invisible walls.
 *
 * Onda Parede-invisível-2 (bug "a parede invisível voltou"): we USED to fetch the slice once at boot and freeze
 * it. But the owner keeps Save-ing new capital revisions; the server hot-swaps every live port room to the newest
 * slice the instant a save lands, so a page that booted before the save predicted against a STALE layout →
 * felt-but-unseen walls where a building had moved. Fix: re-fetch on EVERY port entry (`refreshPortOverride`),
 * so each time a captain steps ashore the client predicts against the server's current slice. Fetch failure is
 * silent (keep the last-known / shipped port). */
let editorPortOverride: { city?: PortLayout; capital?: PortLayout } = {};
/** Fetch the live Save-Online overrides and (re)apply them to the client — the render exteriors, landmark, port
 *  flags, seat tuning, and the `editorPortOverride` that `resolvePortLayout` reads. Called once at boot and again
 *  on every port entry so an in-play client always predicts against the server's CURRENT capital/city slice.
 *  Resolves silently (keeping the last-known overrides) if the server is unreachable or has no overrides saved. */
async function refreshPortOverride(): Promise<void> {
  try {
    const httpBase = resolveServerUrl().replace(/^ws/, 'http'); // ws→http / wss→https (same-origin in prod)
    const res = await fetch(`${httpBase}/editor/layouts`, { cache: 'no-store' });
    if (!res.ok) return;
    const j = (await res.json()) as { city?: PortLayout | null; capital?: PortLayout | null; landmark?: Record<string, LandmarkPiece[]> | null; ports?: Record<string, { hideOutpostStructure?: boolean }> | null; seats?: Record<string, Partial<SeatConfig>> | null };
    editorPortOverride = { city: j.city ?? undefined, capital: j.capital ?? undefined };
    // Onda Portos Livres — the EXTERIOR props of a Save-Online layout (the `capital` city OR any `city` village)
    // also render from the sea, so a rampart/lighthouse/tree — or a generated harbour building (wharf/citadel) —
    // placed OUTSIDE the walls is visible sailing up, not just from inside. Render-only; a no-op when a layout
    // has nothing outside its plaza. (Village-1's shipped Skull Harbor wharf projects even with no override.)
    renderer.setPortExteriors(j.city ?? null, j.capital ?? null);
    // Landmark Editor micro-wave: a saved per-outpost sea-exterior override, applied render-only.
    if (j.landmark && typeof j.landmark === 'object') renderer.setLandmarkOverride(j.landmark as Record<number, readonly LandmarkPiece[]>);
    // Item 3 (hideOutpostStructure): a saved per-outpost `ports` flag map — the renderer skips the stock
    // trading-post GLB where the owner hid it, so his own landmark stands alone. Render-only, no wire.
    if (j.ports && typeof j.ports === 'object') {
      const flags: Record<number, { hideOutpostStructure?: boolean }> = {};
      for (const [id, v] of Object.entries(j.ports)) if (v && typeof v === 'object') flags[Number(id)] = { hideOutpostStructure: v.hideOutpostStructure === true };
      renderer.setPortFlags(flags);
    }
    // Onda Sit-Configurador: a saved per-kind SEAT override — retunes where/how a captain sits on any chair/bench/
    // bed (house) or cobble bench/stool (port). Render-only (sitting is a local visual); the pure pickers read it.
    if (j.seats && typeof j.seats === 'object') applySeatOverride(j.seats);
  } catch { /* no editor overrides saved / server unreachable — keep the shipped ports */ }
}
void refreshPortOverride(); // boot: adopt any persisted overrides before the first port entry
/** The layout to render + predict for an outpost: a Save-Online override when present, else the shipped one. */
function resolvePortLayout(outpostId: number, cap: boolean): PortLayout {
  const ov = cap ? editorPortOverride.capital : editorPortOverride.city;
  return ov ?? layoutForOutpost(outpostId);
}
/** Cheap content signature of the gameplay-relevant parts of a layout — mirrors the server's `layoutSig`
 *  (portRoom.ts). Used to tell whether a fresh /editor/layouts fetch actually CHANGED the slice we entered on,
 *  so we only rebuild the port scene + prediction when it matters (no churn on the common no-change re-fetch). */
function portLayoutSig(l: PortLayout): string {
  return JSON.stringify([l.size, l.spawn, l.exit, l.tavern, l.stalls, l.casino ?? [], l.obstacles]);
}

/* ----------------------------- Onda Social (v40): chat + voice ----------------------------- */
let chatPanel: ChatPanel | null = null;
let voiceEngine: VoiceEngine | null = null;
let myName = 'Captain'; // the name the player set sail with (fallback for self-mention detection)

/* ----------------------------- wave 6: the walkable port ----------------------------- */
let portScene: PortScene | null = null;
let portHud: PortHud | null = null;
const portPred = new PortPrediction();
let lastPortModeV = 0;
let portInputAcc = 0;
let portSeq = 0;
let lastPortSnap = -1;
// wave 8: the captain's house reuses the port HUD + walker path, but its own scene + rectangular prediction
let houseScene: HouseScene | null = null;
const housePred = new HousePrediction();
let houseInputAcc = 0;
let houseSeq = 0;
let lastHouseSnap = -1;
let lastHouseV = -1;
let houseStepAcc = 0; // Interior Vivo: distance accumulator for wooden footstep cadence
let decorateCtl: DecorateHandle | null = null;
let clientLastCombatMs = 0; // wall-clock ms of the last hit I took/dealt — gates the "Enter Port" button
let lastPortPreloadNear = false;
let lastCapitalPreloadNear = false; // Onda 6.5: edge-trigger the capital-kit preload on bay approach

// "Enter Port" button — shown at sea when moored inside an outpost ring and eligible to step ashore.
const enterPortBtn = document.createElement('button');
enterPortBtn.id = 'enterportbtn';
enterPortBtn.textContent = '⚓ Enter Port';
enterPortBtn.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:36;display:none;padding:14px 22px;border-radius:14px;border:1px solid rgba(232,192,116,.5);background:linear-gradient(180deg,rgba(58,42,18,.95),rgba(36,26,10,.96));color:#f2d98c;font-weight:800;font-size:17px;cursor:pointer;box-shadow:0 8px 26px rgba(0,0,0,.55);';
enterPortBtn.addEventListener('click', () => { net.sendPort(PortOp.ENTER); audio.ui(); enterPortBtn.style.display = 'none'; });
document.body.appendChild(enterPortBtn);
// "Enter House" button — shown at sea when berthed at YOUR home island (Onda 8). Sends C_HOUSE ENTER (0 = own dock).
const enterHouseBtn = document.createElement('button');
enterHouseBtn.id = 'enterhousebtn';
enterHouseBtn.textContent = '🏠 Enter House';
enterHouseBtn.style.cssText = 'position:fixed;top:calc(50% + 56px);left:50%;transform:translate(-50%,-50%);z-index:36;display:none;padding:12px 20px;border-radius:14px;border:1px solid rgba(232,192,116,.5);background:linear-gradient(180deg,rgba(46,58,30,.95),rgba(28,38,18,.96));color:#dff0c8;font-weight:800;font-size:16px;cursor:pointer;box-shadow:0 8px 26px rgba(0,0,0,.55);';
let enterHouseTarget: number = HOUSE_OWN_DOCK; // own dock, or the islandId of the clanmate base being visited
enterHouseBtn.addEventListener('click', () => { net.sendHouse(HouseOp.ENTER, enterHouseTarget); audio.ui(); enterHouseBtn.style.display = 'none'; });
document.body.appendChild(enterHouseBtn);
// a lightweight body toast for house/entry messages (the sea HUD's toast lives on the hidden sea overlay ashore)
const houseToastEl = document.createElement('div');
houseToastEl.style.cssText = 'position:fixed;top:calc(env(safe-area-inset-top,8px) + 60px);left:50%;transform:translateX(-50%);z-index:62;display:none;padding:9px 16px;border-radius:11px;border:1px solid rgba(232,192,116,.5);background:rgba(20,32,24,.92);color:#f2e2b8;font-size:13.5px;font-weight:700;box-shadow:0 6px 18px rgba(0,0,0,.5);';
document.body.appendChild(houseToastEl);
let houseToastTimer = 0;
function houseToast(msg: string): void { houseToastEl.textContent = msg; houseToastEl.style.display = 'block'; clearTimeout(houseToastTimer); houseToastTimer = window.setTimeout(() => { houseToastEl.style.display = 'none'; }, 2600); }
// Onda 6.5: the Capital entry fade — snap to dark, then reveal the harbour over ~1s so the scene swap +
// kit build are invisible (the kit is preloaded on the bay approach, so nothing pops).
const fadeEl = document.createElement('div');
fadeEl.style.cssText = 'position:fixed;inset:0;z-index:60;background:#080d12;opacity:0;pointer-events:none;';
document.body.appendChild(fadeEl);
function playEntryFade(ms: number): void {
  fadeEl.style.transition = 'none'; fadeEl.style.opacity = '1';
  void fadeEl.offsetWidth; // force reflow so the next opacity change animates
  fadeEl.style.transition = `opacity ${ms}ms ease`;
  requestAnimationFrame(() => { fadeEl.style.opacity = '0'; });
}

// ── ZERO-FRICTION ONBOARDING: whisk a first-time captain STRAIGHT to the open sea ────────────────────────────
// The fun is on the water; a newcomer used to be BORN facing the back of their cabin and had to find the exit.
// A brand-new captain (Helm Coach still owed, hasn't reached the sea yet this session) is now auto-"Set Sail"'d
// out of the house the instant they land in it — client-only, using the SAME existing PortOp.EXIT the Set Sail
// button sends (zero wire). Bonus for the cold-load diet: the ~8 MB of cabin interior GLBs never load for them,
// since we skip the house preload entirely and go to sea. The Casa do Capitão stays fully reachable LATER via
// HOME (any HOME visit after the first sea-reach builds the cabin normally). Veterans (coach done) are untouched.
let reachedSeaThisSession = false;   // flips true the first time we step onto open water — gates the auto-sail
let newbieAutoSailSent = false;      // the auto-EXIT was already fired this house-spawn (never double-send)
let suppressHouseLoop = false;       // while true, runHouse() is skipped (masking the newcomer's auto-sail handoff,
                                     // so its houseVersion-driven setHouseState never streams the 8 MB interior)
// A branded full-screen mask over the ~1s house→sea handoff, so the newcomer never sees the bare cabin flash.
const hoistEl = document.createElement('div');
hoistEl.id = 'hoist-sails';
hoistEl.style.cssText = 'position:fixed;inset:0;z-index:66;display:none;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:radial-gradient(120% 90% at 50% 20%,#123a4e 0%,#08283544 0%,#041720 100%),#041720;color:#eaf6ff;font-family:Georgia,"Times New Roman",serif;';
hoistEl.innerHTML = '<div style="font-size:46px;filter:drop-shadow(0 3px 8px rgba(0,0,0,.6));">⚓</div>'
  + '<div style="font-size:clamp(17px,4.6vw,22px);font-weight:800;letter-spacing:.02em;background:linear-gradient(180deg,#fff6d8,#f2c94c 60%,#c8901e);-webkit-background-clip:text;background-clip:text;color:transparent;filter:drop-shadow(0 2px 6px #000);">Hoisting the sails…</div>'
  + '<div style="width:26px;height:26px;border-radius:50%;border:3px solid rgba(242,201,76,.28);border-top-color:#f2c94c;animation:ccSpin .9s linear infinite;"></div>';
document.body.appendChild(hoistEl);
function showHoist(): void { hoistEl.style.display = 'flex'; hoistEl.style.opacity = '1'; }
function hideHoist(): void {
  if (hoistEl.style.display === 'none') return;
  hoistEl.style.transition = 'opacity .45s ease'; hoistEl.style.opacity = '0';
  window.setTimeout(() => { hoistEl.style.display = 'none'; hoistEl.style.transition = 'none'; }, 480);
}
// "N captains in port" chip — shown near an outpost so the social hub's population is visible from the sea.
const portPopChip = document.createElement('div');
portPopChip.id = 'portpopchip';
portPopChip.style.cssText = 'position:fixed;top:calc(env(safe-area-inset-top,8px) + 76px);left:50%;transform:translateX(-50%);z-index:35;display:none;padding:5px 13px;border-radius:11px;border:1px solid rgba(232,192,116,.44);background:rgba(20,32,24,.86);color:#f2e2b8;font-size:12.5px;font-weight:700;pointer-events:none;box-shadow:0 4px 14px rgba(0,0,0,.5);';
document.body.appendChild(portPopChip);

// damage vignette — a red flash when an enemy shot lands on YOUR hull
const dmgFlash = document.createElement('div');
dmgFlash.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:40;opacity:0;box-shadow:inset 0 0 130px 36px rgba(205,28,18,.62);';
document.body.appendChild(dmgFlash);
function flashDamage(): void {
  dmgFlash.style.transition = 'none'; dmgFlash.style.opacity = '1';
  requestAnimationFrame(() => { dmgFlash.style.transition = 'opacity .42s ease-out'; dmgFlash.style.opacity = '0'; });
}

// Reconnect banner — an unexpected WS drop mid-game auto-reconnects (same device token → same
// profile/base), so this shows live status instead of the old dead "Connection lost".
const rcSpin = document.createElement('style');
rcSpin.textContent = '@keyframes ccSpin{to{transform:rotate(360deg)}}';
document.head.appendChild(rcSpin);
const reconnEl = document.createElement('div');
reconnEl.style.cssText = 'position:fixed;top:calc(env(safe-area-inset-top,8px) + 12px);left:50%;transform:translateX(-50%);z-index:70;display:none;align-items:center;gap:9px;padding:11px 18px;border-radius:13px;border:1px solid rgba(0,200,5,.35);background:rgba(6,16,20,.92);color:#eaf6ff;font-size:14px;font-weight:700;box-shadow:0 8px 26px rgba(0,0,0,.55);';
document.body.appendChild(reconnEl);
function showReconnecting(attempt: number): void {
  reconnEl.innerHTML = `<span style="width:13px;height:13px;border:2px solid rgba(0,200,5,.35);border-top-color:#00e006;border-radius:50%;display:inline-block;animation:ccSpin .7s linear infinite;"></span>⚓ Reconnecting…${attempt > 1 ? ` (${attempt})` : ''}`;
  reconnEl.style.display = 'flex';
}
function hideReconnecting(): void { reconnEl.style.display = 'none'; }

// Weekly event banner (5.4d) — a small parchment-gold chip showing today's active world event.
const eventEl = document.createElement('div');
eventEl.id = 'eventchip';
eventEl.style.cssText = 'position:fixed;top:calc(env(safe-area-inset-top,8px) + 44px);left:50%;transform:translateX(-50%);z-index:35;display:none;padding:5px 13px;border-radius:11px;border:1px solid rgba(232,192,116,.5);background:linear-gradient(180deg,rgba(58,42,18,.94),rgba(36,26,10,.95));color:#f2d98c;font-size:12.5px;font-weight:700;letter-spacing:.3px;box-shadow:0 4px 16px rgba(0,0,0,.5);text-shadow:0 1px 2px #000;pointer-events:auto;cursor:pointer;';
document.body.appendChild(eventEl);
// tap the event chip to read what today's world event does (mobile has no hover tooltip).
let eventDesc = '';
eventEl.addEventListener('pointerdown', (e) => { e.preventDefault(); if (eventDesc) hud?.toast(`⚑ ${eventDesc}`); });
let lastCalV = -1;
function updateEventBanner(): void {
  if (net.calendarVersion === lastCalV) return;
  lastCalV = net.calendarVersion;
  const c = net.calendar;
  if (!c || c.eventId === WorldEventId.NONE) { eventEl.style.display = 'none'; return; }
  const day = CALENDAR[c.dayOfWeek];
  if (!day) { eventEl.style.display = 'none'; return; }
  eventEl.textContent = `⚑ ${day.label}`;
  eventEl.title = day.desc;
  eventDesc = day.desc;
  eventEl.style.display = 'block';
}
// World-edge "wall of bruma" (final UX pass #11): the sim soft-clamps the ship at 0/WORLD_SIZE with zero
// signal, so sailing into the border silently pins you. Client-only cue (no sim/server change): a mist wall
// thickens on the horizon ahead as you near any edge, plus a one-time session toast the first time you truly
// hit the wall. The element sits between the 3D canvas and #overlay in DOM order (both z-auto) so it paints
// over the sea but UNDER the HUD/toasts — no z-index needed, keeps the topbar chips crisp.
const edgeMistEl = document.createElement('div');
edgeMistEl.style.cssText = 'position:fixed;inset:0;pointer-events:none;opacity:0;background:radial-gradient(135% 82% at 50% -12%, rgba(156,174,192,.92) 0%, rgba(124,144,166,.52) 30%, rgba(98,118,142,.2) 52%, transparent 66%);';
document.body.insertBefore(edgeMistEl, overlay);
const EDGE_MIST_M = WORLD_EDGE.MARGIN; // v37: fade the bruma in exactly across the come-about band so "the fog turns you"
let edgeToastShown = false;  // "The open sea ends here" — fired once per session, on the first true wall-hit
const EDGE_BANNER_COOLDOWN_MS = 15000; // v37: longer cooldown so the epic flat-earth banner can't re-pop while grinding the border
let edgeBannerAt = -1e9;     // performance.now() of the last "coming about" banner
// Onda Fix 5 (frente 2): when you sail back OUT of a port/house the ship teleports to a fresh sea spawn and
// `net` nulls selfAuth. Suppress the flat-earth banner for a beat while that reconcile settles so the
// come-about can never falsely flash on the way out of a city (the owner's report).
let seaResumedAt = -1e9;     // performance.now() when the sea loop last resumed after a port/house handoff
const PORT_RESUME_SUPPRESS_MS = 1500;

// Frente 1 (v37): a captain who sails near their OWN base doesn't know the "🏠 Enter House" button only
// appears once berthed. Show a one-shot, non-intrusive hint on approach — the client already knows its base
// position (net.bases). Hysteresis (inner fires / outer re-arms) so it never spams; starts DISARMED so it
// fires on a RETURN to port, not at spawn.
const BASE_HINT_INNER = 120;  // metres from your base where the hint fires
const BASE_HINT_OUTER = 200;  // sail back out past this to re-arm for the next homecoming
// (tap-to-sail arrival radius now lives in AUTOSAIL.ARRIVE_R — see autosail.ts, the smart quartermaster)
let baseHintArmed = false;
/** The player's OWN base position (from S_BASES), or null before it arrives — feeds the chart's gold home
 *  marker and the HOME "sail there" button. */
function homeBaseXY(): { x: number; y: number } | null {
  const b = net.bases.find((bb) => bb.islandId === net.yourIslandId);
  return b ? { x: b.x, y: b.y } : null;
}
function updateEdgeMist(cx: number, cz: number, worldSize: number, atSea: boolean): void {
  const edgeDist = atSea ? Math.min(cx, cz, worldSize - cx, worldSize - cz) : Infinity;
  const k = edgeDist >= EDGE_MIST_M ? 0 : 1 - edgeDist / EDGE_MIST_M; // 0 far … 1 at the wall
  edgeMistEl.style.opacity = (k * k * 0.9).toFixed(3);                // ease-in: imperceptible until close
  if (atSea && edgeDist <= 6 && !edgeToastShown) { edgeToastShown = true; hud?.toast('🌫 The open sea ends here'); }
}
function showReconnectFailed(): void {
  reconnEl.innerHTML = '⚠ Connection lost — <a href="." style="color:#00e006;font-weight:800;">reload</a>';
  reconnEl.style.display = 'flex';
}

/**
 * A TERMINAL server refusal that arrives when the captain is already at sea (the duplicate-session bump).
 *
 * It cannot go through `showStatus`: that writes into `#status`, which lives inside the menu, and `hideMenu()`
 * REMOVES the menu from the DOM the moment the sea starts — so the message a bumped tab most needs was a no-op,
 * and all it ever saw was the reconnect spinner. `reconnEl` is a permanent HUD element, which is why it is the
 * channel here. Reload (not SET SAIL) is the honest action: this socket is dead and the menu is gone.
 */
function showTerminalReject(text: string): void {
  const safe = text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
  reconnEl.innerHTML = `⚓ ${safe} — <a href="." style="color:#00e006;font-weight:800;">reload</a>`;
  reconnEl.style.display = 'flex';
}

// Haptics — the 3rd feedback channel on mobile. Coalesced so a furball of hits
// can't buzz the phone into a blur; gated to touch devices (no-op on desktop/iOS).
const canVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;
let lastHaptic = 0;
function haptic(pattern: number | number[]): void {
  if (!canVibrate || !renderer.isMobile) return;
  const now = performance.now();
  if (now - lastHaptic < 55) return;
  lastHaptic = now;
  try { navigator.vibrate(pattern); } catch { /* noop */ }
}

/** Distance + stereo pan of a world event relative to the local ship (+pan = starboard/right). */
function spatialOf(ex: number, ey: number): { d: number; pan: number } {
  const ok = prediction.hasBaseline;
  const sx = ok ? prediction.body.x : (net.selfAuth?.x ?? ex);
  const sy = ok ? prediction.body.y : (net.selfAuth?.y ?? ey);
  const sh = ok ? prediction.body.heading : (net.selfAuth?.heading ?? 0);
  const dx = ex - sx, dy = ey - sy, d = Math.hypot(dx, dy);
  const pan = d > 1 ? (dx * Math.cos(sh + Math.PI / 2) + dy * Math.sin(sh + Math.PI / 2)) / d : 0;
  return { d, pan };
}

/** Near-miss detector (game juice): a small geyser + stereo whistle when an INCOMING cannonball passes just
 *  outside the local hull without hitting. Deduped per ball id and gated by a short global cooldown so a
 *  raking volley can't machine-gun the cue. Pure read of the already-fetched interpolated balls. */
function detectNearMiss(balls: InterpBall[], sx: number, sy: number, cls: number, t: number, now: number): void {
  const hullR = SHIP_CLASS[cls as keyof typeof SHIP_CLASS]?.radius ?? SHIP_CLASS[1].radius;
  const outer = hullR + NEAR_MISS_CLEAR, outer2 = outer * outer;
  const innerR = hullR * 0.85, inner2 = innerR * innerR; // inside ≈ the hull → a hit/graze; the HIT fx owns that
  for (const b of balls) {
    const dx = b.x - sx, dy = b.y - sy, d2 = dx * dx + dy * dy;
    if (d2 > outer2 || d2 < inner2) continue;
    // only INCOMING balls (moving toward the hull): excludes our own outgoing broadside + receding shots
    if (Math.cos(b.heading) * -dx + Math.sin(b.heading) * -dy <= 0) continue;
    if (nearMissSeen.has(b.id)) continue;
    nearMissSeen.add(b.id);
    if (now < nearMissCooldownUntil) continue;      // still whistling from a shot a moment ago
    nearMissCooldownUntil = now + NEAR_MISS_COOLDOWN_MS;
    renderer.spawnGeyser(b.x, b.y, t);
    audio.whistle(Math.sqrt(d2), Math.sin(renderer.bearingTo(b.x, b.y))); // pan L/R by camera-relative bearing
    haptic(10);                                     // a faint tick — a shot just screamed past
  }
  // prune ids whose ball is gone so a recycled u16 can whistle again later (nearMissSeen stays tiny)
  if (nearMissSeen.size) for (const id of nearMissSeen) {
    let alive = false; for (const b of balls) if (b.id === id) { alive = true; break; }
    if (!alive) nearMissSeen.delete(id);
  }
}

/** Format an async-raid outcome (or a "you were raided" alert) as a HUD toast. */
function raidToast(rr: RaidResultMsg): string {
  switch (rr.outcome) {
    case RaidOutcome.LOOTED: return `🏴‍☠️ Plundered ${rr.stolen} 🪙 from ${rr.targetName}!`;
    case RaidOutcome.REPELLED: return `⚔️ ${rr.targetName} repelled your attack!`;
    case RaidOutcome.ALERTED: return `🚨 ${rr.targetName} raided ${rr.stolen} 🪙 from your base!`;
    default: // NONE — targetName carries a short reason hint
      return rr.targetName === 'cooldown' ? '⏳ Wait before raiding again'
        : rr.targetName === 'dailycap' ? '🚫 Daily plunder cap reached'
        : '🌊 No offline base to raid right now';
  }
}
let hud: Hud | null = null;
let combatFx: CombatFx | null = null; // HUD 2.0 combat-awareness overlay (created in onReady)

// ── Contextual push opt-in (audit P0: the setting was buried in the Book, reaching almost nobody) ──
// Asked exactly ONCE, at the FIRST MOMENT OF VICTORY — the earliest of a first kill, a hoisted chest, or an
// outpost sale (Pacote Funil item 5: the old 1st-chest-only trigger sat too deep and converted ~1 opt-in/day),
// plus a time fallback after PUSH_OPTIN.SEA_FALLBACK_MS of active sea time so an engaged captain who hits none
// of those is still asked. DECOUPLED from onboarding (the quest-FTUE is gone): the only gate besides
// once-per-device is "no teaching layer owns the screen right now" — a beat landing mid-coach/mid-manual simply
// waits for the next trigger (the 4-min fallback guarantees one). The privacy link is inline; "Later" never
// re-nags (the `sor:pushAsked` device flag); Settings keeps the re-toggle.
let pushAsked = false;
function askPushOnce(): void {
  if (pushAsked) return;
  pushAsked = true;
  try { if (localStorage.getItem('sor:pushAsked')) return; localStorage.setItem('sor:pushAsked', '1'); } catch { /* private mode */ }
  if (!('Notification' in window) || Notification.permission !== 'default') return; // unsupported / already decided
  const card = document.createElement('div');
  card.style.cssText = 'position:fixed;left:50%;top:22%;transform:translateX(-50%);z-index:75;width:min(88vw,340px);padding:14px 16px;border-radius:14px;background:linear-gradient(165deg,rgba(16,30,46,.96),rgba(6,14,26,.97));border:1px solid rgba(232,192,116,.4);box-shadow:0 12px 36px rgba(0,0,0,.6);color:#eef6ff;font-size:13.5px;line-height:1.45;';
  card.innerHTML = '<b style="color:#f2d79b">⚓ Stay in the fight?</b><br>Get a ping when your base is raided or your streak is about to break. Only what matters — <a href="/site/privacy.html" target="_blank" rel="noopener" style="color:#9fd8ff">privacy</a>.'
    + '<div style="display:flex;gap:8px;margin-top:11px">'
    + '<button id="pushyes" style="flex:1;min-height:42px;border-radius:10px;border:1px solid #8a6a1e;background:linear-gradient(180deg,#e2be5a,#b98a2c);color:#2a1c06;font-weight:800;cursor:pointer">🔔 Enable</button>'
    + '<button id="pushno" style="flex:1;min-height:42px;border-radius:10px;border:1px solid rgba(120,180,220,.3);background:rgba(255,255,255,.06);color:#dfeaf5;font-weight:700;cursor:pointer">Later</button></div>';
  document.body.appendChild(card);
  card.querySelector('#pushyes')!.addEventListener('click', () => { card.remove(); audio.ui(); void enablePush().then((r) => { if (r.note) hud?.toast(r.note); }); });
  card.querySelector('#pushno')!.addEventListener('click', () => { card.remove(); audio.ui(); });
}
let pushAskScheduled = false;
/** Schedule the one-time opt-in ask after a positive beat — only while NO teaching layer owns the screen (the
 *  Helm Coach guiding / the Primer manual sheet open), and only once per session. Reused by every positive-beat
 *  trigger (first kill, first hoisted chest, first sale) plus the sea-time fallback, so an engaged captain of
 *  any tenure sees the ask exactly once without ever being interrupted mid-lesson: a beat that lands during a
 *  lesson is simply not scheduled, and the next trigger (at latest the 4-min fallback) picks it up. */
function maybeAskPushLater(delayMs: number): void {
  const teaching = (coach?.guiding() ?? false) || (primer?.isOpen() ?? false);
  if (!shouldSchedulePushAsk({ asked: pushAsked, scheduled: pushAskScheduled, teaching })) return;
  pushAskScheduled = true;
  setTimeout(askPushOnce, delayMs);
}

// ── HUD Forge (mobile HUD layout editor) ────────────────────────────────────
// Lazy chunk: costs the shipped bundle nothing. Opened from Settings → Controls; while open it blocks
// the sea HUD's input (its overlay swallows touches; setEditMode parks the keyboard path).
let hudEditOpen = false;
async function openHudForge(): Promise<void> {
  if (hudEditOpen || !hud || net.portMode) return;
  const m = await import('./hudedit.js');
  if (hudEditOpen) return; // double-tap guard across the await
  hudEditOpen = true;
  m.openHudEditor({
    setInputBlocked: (on) => hud?.setEditMode(on),
    toast: (t) => hud?.toast(t),
    onSaved: () => { audio.ui(); haptic(14); },
    onClosed: () => { hudEditOpen = false; },
    sfx: () => audio.ui(),
  });
}

// ── Onboarding v4 (the quest-FTUE is GONE — owner 2026-07-16, prod-proven collapse) ─────────────────────────
// coach.ts = the Helm Coach: contextual coach-marks glued to the REAL HUD controls (wheel, sails, aim gauge,
// 🪣, wind pennant, chart), each advancing when the captain uses the control; skippable; never blocks the game.
// primer.ts = the Captain's Primer: the illustrated parchment manual, opened on demand from the Book.
let coach: Coach | null = null;
let primer: Primer | null = null;
// Contextual survival hints (client-only): emergency "bail!" + "weigh anchor!" nudges. See uxhints.ts.
let uxHints: UxHints | null = null;
// ── Captain's Opportunities (non-linear "what can I do?" board) ──────────────
// A compass of possibilities, NOT a questline (owner's anti-funnel call): the whole leque of sandbox
// activities, each with reward + WHERE, always shown; a soft "for you now" glow on a bounded subset. Gated
// to appear only once the Helm Coach is done so the two never fight for a first-timer. See opportunities.ts.
let opportunities: Opportunities | null = null;

let basePanel: BasePanel | null = null;
let clanMenu: ClanMenu | null = null;
let marketMenu: MarketMenu | null = null;
let exchangeMenu: ExchangeMenu | null = null;
let premiumMenu: PremiumMenu | null = null;
let dragaMenu: DragaMenu | null = null;
let dragaScene: DragaScene | null = null;
let killcam: ReplayPlayer | null = null; // Cinematic Kill-Cam / Highlight system (client-only, zero wire)
let storesMenu: StoresMenu | null = null;
let profNotifier: ProfNotifier | null = null;
let lastProfNotifyV = -1; // profession XP toast/banner: fire on each fresh S_PROFESSIONS
let freightMenu: FreightMenu | null = null;
let auctionMenu: AuctionMenu | null = null;
let casinoMenu: CasinoMenu | null = null;
let nearStation: PortStation | null = null; // wave 7: the casino/auction/clan station in reach (ashore)
let captainsBook: CaptainsBook | null = null;
let dawnReport: DawnReport | null = null;

let seq = 0;
let inputAcc = 0;
let lastSnap = -1;
let wasAlive = true;
let last = performance.now();
let fps = 60;
let started = false;
// Reconnect state cleanup (the mobile glitch fix): a live link dropped and we're mid-reconnect. Set on the drop,
// cleared once the fresh authoritative self re-seeds the local ship, so the sea loop FREEZES prediction during
// the gap (no dead-reckoning drift, no false flat-earth banner) and then hard re-baselines to the real rejoin
// spot — the reconnect analogue of the port-handoff baseline drop. Without it the ship kept sailing from the
// stale pre-drop position and the camera jumped on rejoin.
let pendingRebaseline = false;

/* ----------------------------- Fullscreen (owner order + power-user GOLDEN RULE) -----------------------------
 * Android/desktop: requestFullscreen must ride a user gesture — SET SAIL is the natural one. It is OPT-OUT
 * (Settings → Controls → "Fullscreen on set sail", default on) and, crucially, GOLDEN RULE: once the player
 * leaves fullscreen manually (swipe / Esc), the game NEVER re-forces it for the rest of the session — so a
 * captain can scroll/chill between fishing without being yanked back. Only an explicit re-arm brings it back:
 * the Settings toggle (which re-enters on that very click), or a fresh page load. iPhone Safari has NO
 * fullscreen API — there the installed PWA (Add to Home Screen) IS the fullscreen path (one-time hint below).
 * All silent no-ops where unsupported. */
let fsSuppressed = false;   // GOLDEN RULE latch: user left fullscreen manually → don't auto re-enter this session
let fsProgrammatic = false; // guards the change handler while WE enter/exit (so our own exit ≠ a manual exit)
function goFullscreen(): void {
  try {
    if (!getSetting('fullscreen') || fsSuppressed) return; // opted out, or they walked out this session
    if (document.fullscreenElement || !document.documentElement.requestFullscreen) return;
    fsProgrammatic = true;
    void document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => { fsProgrammatic = false; });
  } catch { /* no API (iOS) */ }
}
function exitFullscreen(): void {
  try { fsProgrammatic = true; if (document.fullscreenElement && document.exitFullscreen) void document.exitFullscreen().catch(() => { fsProgrammatic = false; }); else fsProgrammatic = false; }
  catch { fsProgrammatic = false; }
}
document.addEventListener('fullscreenchange', () => {
  // A change we didn't initiate that LEFT fullscreen is a manual exit → latch the golden rule.
  if (!document.fullscreenElement && !fsProgrammatic) fsSuppressed = true;
  fsProgrammatic = false;
  try { window.dispatchEvent(new Event('resize')); } catch { /* keep the renderer/HUD laid out (no torn layout on exit) */ }
});
const IS_IOS_BROWSER = /iPhone|iPad|iPod/.test(navigator.userAgent)
  && !(navigator as Navigator & { standalone?: boolean }).standalone
  && !window.matchMedia?.('(display-mode: standalone)').matches;
let worldApplied = false;
let nameCached = false; // one-shot: cache the server's authoritative captain name so the menu re-shows it next visit
let lastChestStateV = -1; // Onda Carona: re-push chest visibility to the renderer only when net.takenChests changes
let lastAnchor = false; // edge-detect the anchor drop to play the chain/splash SFX once
let autoSailTrimRef = 0; // sail trim snapshotted when a course engages — any change = the captain took the sheets (cancels auto-sail)
let lastBasesVersion = -1;
// solid base islands the local ship collides with — kept in lockstep with the server.
const ISLAND_R = BASE.GRID * BASE.CELL_SIZE * 0.62; // == server ISLAND_R (matches client render)
let solidObstacles: Obstacle[] = [];
let lastShipClass = -1;
let lastTrophyMask = -1;      // wave 3.3: toast when a new figurehead trophy is earned
let lastRenown = -1;           // Renown (profile trophies) — float "+X/−X 🏆" when it changes (kill/death/raid)
let lastEyeOn = false;         // wave 3.8: eye-of-storm open/close edge
// Weather PREVIEW (`?storm=0..1`, `?eye=1`) — render-only owner/dev lever: it pins what the SKY does, never
// what the sim does (wind, turn resistance and every economy multiplier keep reading net.stormIntensity).
const weatherPreview = weatherOverride();
// 🌌 AURORA PREVIEW (`?aurora=1|0.5|breakup|0`) — render-only owner/dev lever, applied once at boot. The
// real display is a rare scheduled night event; this pins it so it can be photographed on demand. The
// renderer still gates on night and storm, so this can never conjure one in daylight.
const auroraPreview = auroraOverride();
if (auroraPreview.amt != null) renderer.setAurora(auroraPreview.amt, auroraPreview.breakup);
// 🐋 White-Whale PREVIEW (`?whale=1` / `?whale=die`) — render-only: a synthetic marker (same shape, same ~5 Hz
// cadence as the server's) circling the captain, so the breach/sound/death choreography can be audited without
// waiting on the scheduled hunt. net.whale is ignored for the RENDER while it is on; nothing else changes.
const whalePreviewMode = whalePreview();
let whalePreviewT0 = -1;      // sea-clock seconds when the preview whale was summoned
let whalePreviewOrigin = { x: 0, z: 0 }; // frozen circle centre (constant marker velocity, like the server's)
let whalePreviewDead = false; // `?whale=die`: the death beat has been fired once
let previewEye: { x: number; y: number } | null = null; // `?eye=1` places one Eye off the bow, once
let aimRamp = 0;      // 0..1 eased aim intensity (drives the bearing guide + camera zoom)
let seaActiveMs = 0;           // accumulated ACTIVE time at sea (alive, not ashore) — the push-optin time fallback (item 5)
// First-60s-at-sea breadcrumb guards (front C) — cheap per-session latches so the beacon helpers (themselves
// once-guarded) aren't hit every frame. Not reset on respawn: these are session firsts.
let sea60Sent = false, firstShotSent = false, firstHelmSent = false, firstSailSent = false, firstChartSent = false;
let seaSailBaseline = -1;      // the sail trim on the first sea input frame; a change past it = the captain trimmed
let lastChestCount = 0;        // rising/falling edge of chests aboard -> treasure aboard/sold toasts
let lastCargoUnits = 0;        // falling edge of trade cargo while alive -> a delivery (retires the first-cargo hint)
let chestCapArmed = false;     // Fix 4: one-shot "hold full — sell first" cue when parking over another chest with a full hold
let cursedClaimedByMe = false; // set when I hoist the cursed hoard -> suppress the "Rich waters" toast
let plunderRunActive = false;  // I'm mid-raid on an enemy storehouse (PLUNDER events with my id)
let plunderRunStart = 0;       // my carried cargo when the current plunder run began
let plunderRunUntil = 0;       // wall-clock ms after which, with no new PLUNDER, the run is "over" (I left)
let lastDailyEarned = 0;       // soft-cap tier-crossing detector (one toast per tier per day)
let lastContractsV = -1;       // contract board version (re-render + completion toast)
let lastClaimed = 0;           // contracts completed so far today (toast on increase)
let chestsFlat: { x: number; y: number }[] | null = null; // world chests, flattened once for the minimap
let treasureObstacles: Obstacle[] = []; // v43: static treasure-island collision cores, built once from S_WORLD
let tradeLanesFlat: { ax: number; ay: number; bx: number; by: number }[] | null = null; // outpost triangle lanes
// ⚑ Armada chart markers (v54): the chart's view-model of net.marks, rebuilt only when the server pushes a
// new list (marksVersion) or the clan context changes (clanVersion) — never per frame.
let clanMarksVm: { id: number; icon: number; x: number; y: number; label: string; author: string; mine: boolean }[] = [];
let clanMarksVmKey = '';
let lastAimSide = 0;  // remembered broadside while the guide ramps back out
let lastRudder = 0;   // latest steering input — the camera lags (never freezes) while turning so the target barely drifts
// Near-miss feedback: dedupe by ball id (so one ball whistles once, not every frame it lingers in the band)
// plus a short global cooldown so a broadside volley doesn't machine-gun the whistle.
const nearMissSeen = new Set<number>();  // ball ids already whistled (pruned each frame to live balls)
let nearMissCooldownUntil = 0;           // performance.now() ms; suppresses back-to-back whistles under fire
const NEAR_MISS_CLEAR = 8;               // metres of clearance from the hull collision radius = "just missed"
const NEAR_MISS_COOLDOWN_MS = 300;       // anti-spam window under a raking volley
const LEAD_ARC_HALF = 1.0;               // rad (~57°): a target within this of the broadside normal is engageable
let lastActivity = 0; // wave 2b sea-activity edge detector (0 none, 1 fishing-wait, 2 bite, 3 diving)
let fishBiteAtMs = 0; // fishing-polish: when the bite reached THIS client — anchors the HUD tension needle
let diveMaxProgress = 0; // peak dive-bar progress this dive (to tell a completed dive from an abandoned one)
// 🤿 Onda diving-polish: itemized salvage reveal — snapshot the hold at dive START, then when the post-dive
// S_GOODS lands (or the deadline passes → plain toast fallback), diff it and show the parchment loot card.
// Presentation only: mechanics/odds/timers untouched; the loot itself already rode the normal goods sync.
let diveHoldBefore: Map<number, number> | null = null;
let diveGoodsV = 0;          // net.goodsVersion at dive start (a bump after surfacing = the salvage landed)
let diveLootDeadline = 0;    // >0 → watching for the salvage diff; performance.now() past it → fallback toast
const diveFx = new DiveFx(); // DOM underwater vignette (combatfx "screen state" pattern; zero WebGL)
let lastDynamicWrecksV = -1;
let lastBottlesV = -1;         // wave 3.1: drifting-bottle set version
let lastStormEssence = -1; // storm-essence count in the hold (toast on increase)
let lastHoldDetailV = -1;  // Fix 4 frente 2: hold-chip breakdown text, rebuilt only on a fresh S_GOODS
let holdDetailText = '';
let lastRevengeToast = 0;  // wave 5.1: throttle the "target nearby" hunt toast (marker re-pings every 3s)

function serverSeconds(): number {
  return net.serverNowMs() / 1000;
}

// Viral loop: after the session's FIRST kill, offer a discreet, tappable "share the tale" pill (once). Tapping
// opens the native share sheet (clipboard fallback) with an invite link that drops a friend into your waters.
// Sea-only + self-dismissing, so it never clutters a PvP screen for long ("less info is better for PvP").
let sharePillShown = false;
function showSharePill(): void {
  if (sharePillShown || net.portMode) return;
  sharePillShown = true;
  const pill = document.createElement('button');
  pill.id = 'sharepill';
  pill.textContent = '⚔️ Share the tale';
  pill.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(env(safe-area-inset-bottom,10px) + 98px);z-index:44;pointer-events:auto;padding:9px 15px;border-radius:14px;border:1px solid rgba(232,192,116,.5);background:linear-gradient(180deg,rgba(58,42,18,.95),rgba(36,26,10,.96));color:#f2d98c;font-size:13px;font-weight:700;letter-spacing:.3px;box-shadow:0 6px 18px rgba(0,0,0,.5);text-shadow:0 1px 2px #000;cursor:pointer;opacity:.97;';
  const dismiss = (): void => { pill.remove(); };
  pill.addEventListener('pointerdown', (e) => { e.preventDefault(); audio.ui(); void shareInvite(net.myDisplayName(), (m) => hud?.toast(m)); dismiss(); });
  overlay.appendChild(pill);
  setTimeout(dismiss, 9000); // discreet: fades itself if ignored
}

function startGame(name: string): void {
  myName = name || 'Captain';
  telemetryFunnel('play'); // P1 funnel: SET SAIL tapped — anchors t0 for the boot→spawn breadcrumbs (socket/welcome/world/frame)
  hideBootSplash(); // covers the ?auto=1 headless path that skips the menu entirely
  goFullscreen(); // rides the SET SAIL tap (auto=1 boots have no gesture — the catch swallows it)
  if (IS_IOS_BROWSER) {
    try {
      if (!localStorage.getItem('sor:iosFsHint')) {
        localStorage.setItem('sor:iosFsHint', '1');
        setTimeout(() => hud?.toast('📲 True fullscreen on iPhone: Share → Add to Home Screen'), 12000);
      }
    } catch { /* private mode */ }
  }
  showStatus('⚓ Catching the wind…');
  // A graceful CCU-cap refusal ("the sea is full") shows a friendly retry prompt instead of the raw reject;
  // the menu's SET SAIL button stays live and is the retry (this flag also stops onClose clobbering the note).
  let rejectedFull = false;
  let connectDone = false; // stops a late onClose (from a superseded socket during a retry) from clobbering a status
  net.onReady = () => {
    if (started) return;
    started = true;
    connectDone = true; // the handshake landed — later socket churn must not repaint a connect status
    // COLD-LOAD DIET: the deferred WORLD GLBs (~world props/whale/kraken/isles) are NOT prefetched here anymore.
    // A fresh captain is BORN in their HOUSE (and newcomers are now whisked straight to sea) — firing this on the
    // raw spawn flooded ~5-6 MB of sea props while the player was still ashore in the cabin. It's kicked instead
    // the moment the local ship is first seated at OPEN SEA (see the spawn block in loop()), so the flood never
    // competes with the cabin, the connect, or the first snapshots. Idempotent (renderer guards re-entry).
    hud = new Hud(overlay);
    // Cinematic Kill-Cam: starts recording the rolling transform/event/environment buffer the moment the game is
    // live. Client-only, zero wire; the highlight track + wordmark stream lazily on the first trigger (cold-load safe).
    killcam = new ReplayPlayer(renderer, audio, overlay, haptic);
    hud.onMinimapPing = (x, y) => { // tap chart → clan ping (server ignores non-Armada pings silently)
      if (!net.clan?.inClan) { hud?.toast('⚑ Pings are an Armada perk — join one in the Book.'); return; }
      net.sendClan(ClanOp.PING, '', '', '', 0, x, y);
    };
    // ⚑ Armada chart markers (v54): the big chart's long-press picker → C_MARK PLACE; a pin tag's
    // "scrape it off" → C_MARK REMOVE. Authority is entirely server-side (membership/cap/roles) — the
    // pin only appears/disappears when the S_MARKS broadcast lands.
    hud.onMarkPlace = (x, y, icon, label) => { net.sendMark(MarkOp.PLACE, 0, icon, x, y, label); audio.chartPlot(); haptic([10, 45, 18]); };
    hud.onMarkRemove = (id) => { net.sendMark(MarkOp.REMOVE, id); audio.ui(); };
    hud.onConsume = (id) => { net.sendConsume(id); audio.brewPotion(); }; // ⚗ sip a consumable buff
    hud.onEscort = () => { net.sendEscort(EscortOp.ACCEPT); hud?.toast('⚓ Escorting the convoy — see it home!'); audio.ui(); }; // wave 3.7
    // ── EAGER: what a NEWCOMER needs the instant they spawn ──────────────────────────────────────────────────
    // A fresh captain is BORN inside their HOUSE, so houseScene (which statically pulls in portScene) must be
    // ready on the very first snapshot — those scenes are NOT deferrable. The Book, casino and chat/voice ARE,
    // and stream in the background right after the loop starts (see bootGameUi). profNotifier + dawnReport are
    // tiny and drive off per-frame state, so they stay eager too.
    profNotifier = new ProfNotifier(overlay, audio);
    // frente 1: a 🎒 Ship's Stores shortcut. Hidden by the float-dock stylesheet (the sea keeps only 📖 + 💬),
    // kept for the HUD Forge + deep-link. Opens the (lazily-loaded) Book Stores chapter, awaiting it if needed.
    const invBtn = document.createElement('button');
    invBtn.id = 'invbtn'; invBtn.title = "Ship's Stores — inventory & crafting";
    invBtn.textContent = '🎒';
    invBtn.style.cssText = 'position:fixed;z-index:60;width:44px;height:44px;border-radius:12px;border:1px solid rgba(232,192,116,.34);background:linear-gradient(165deg,rgba(16,30,46,.86),rgba(6,14,26,.9));color:#eef6ff;font-size:19px;cursor:pointer;box-shadow:0 6px 18px rgba(0,8,16,.5),inset 0 1px 0 rgba(255,255,255,.07);'; // position: float-dock stylesheet
    invBtn.addEventListener('pointerdown', (e) => { e.preventDefault(); audio.pageTurn(); void ensureGameUi().then(() => captainsBook?.openChapter('stores')); });
    overlay.appendChild(invBtn);
    // map-ux: the chart-plot ritual — stamp thump + quill scratch + compass ping, with a stamp-landing haptic
    hud.onWaypointSet = () => { audio.chartPlot(); haptic([12, 55, 22]); coach?.notifyWaypoint(); };
    // map-ux ⇄ auto-sail: the chart's voyage line threads the quartermaster's LIVE route (it rounds
    // islands) and the X stamps on its water-projected mark. Falls back to a straight line while idle.
    hud.getRoute = () => (autoSail.active && autoSail.route.length ? autoSail.route : null);
    // Onboarding v4 — the Helm Coach: contextual coach-marks on the live HUD (cards on document.body, NOT
    // #overlay, so they survive port/house transitions). Only ever armed for a brand-new captain; a veteran's
    // constructor read short-circuits everything. The Primer manual mounts lazily on its first menu open.
    coach = new Coach(document.body, {
      sfx: { ui: () => audio.ui(), step: () => audio.coins(), done: () => audio.coins() },
      tele: { step: (k) => telemetryTutStep(k), skip: (k) => telemetryTutSkip(k), done: () => telemetryTutDone() }, // no PII
    });
    primer = new Primer(document.body, {
      sfx: { ui: () => audio.pageTurn() },
      tele: { seen: () => telemetryPrimerSeen(), reopened: () => telemetryPrimerReopened(), dismissed: (k, d) => telemetryPrimerDismissed(k, d) }, // no PII
    });
    // Contextual survival hints (client-only): the emergency "bail!" nudge when a captain floods without repairing,
    // and the "weigh anchor!" nudge when a moored captain tries to sail and can't. Card on document.body so it
    // sits above the HUD; anchor/cargo hold while a teaching layer is up — the bail alarm NEVER does. See uxhints.ts.
    uxHints = new UxHints({ host: document.body, sfx: () => audio.ui(), haptic: () => haptic(22) });
    // The Opportunities board (document.body, like the coach cards — survives port/house transitions). Mounted
    // now; setActive is gated in the sea loop on the coach no longer guiding, so a first-timer sees the lessons
    // first and the "what can I do?" leque the moment they end.
    opportunities = new Opportunities(document.body, { ui: () => audio.ui() });
    // HUD 2.0: directional damage arcs + low-integrity heartbeat + combat-focus fades (render-layer only)
    combatFx = new CombatFx((x, y) => renderer.bearingTo(x, y));
    combatFx.onHeartbeat = () => audio.heartbeat();
    dawnReport = new DawnReport(overlay, audio);
    // wave 6: the walkable port scene + its HUD (hidden until we step ashore)
    portScene = new PortScene(renderer);
    if (lightModeActive) portScene.setLowMemory(true); // wallet webview / low-RAM phone: bias the port to its lean LOD
    portScene.setDealerAudio(audio); // Onda Dealers — the croupiers' pre-generated voice clips route through the sfx bus
    houseScene = new HouseScene(renderer); // wave 8: the captain's house interior (same avatar/HUD machinery)
    portHud = new PortHud(overlay);
    portHud.hide();
    portHud.onEmote = (id) => { net.sendPort(PortOp.EMOTE, id); audio.ui(); };
    // v43 Synced Sit: mirror the LOCAL sit/stand over the wire so every captain on the plaza / in the house sees
    // the avatar seated on the exact bench. Fires for the explicit Sit button AND the scene's auto-stand (walk-off).
    portScene.onSitWire = (on) => net.sendPort(PortOp.SIT, on ? 1 : 0);
    houseScene.onSitWire = (on) => net.sendPort(PortOp.SIT, on ? 1 : 0);
    // wave 8 house-only callbacks (never fire in a normal port — those buttons stay hidden there)
    portHud.onDecorate = () => openDecorateMode();
    portHud.onToggleOpen = (next) => { net.sendHouse(HouseOp.TOGGLE_OPEN, next ? 1 : 0); audio.ui(); };
    portHud.onKick = (id) => { net.sendHouse(HouseOp.KICK, id); audio.ui(); };
    // Interior Vivo: Sit/Stand on the nearest chair/bench/bed. v43: the pose is SYNCED (the scene fires
    // onSitWire → the server re-broadcasts it). Pressing Sit with no seat within reach toasts instead of
    // floating a chair pose in mid-air (owner's design ask).
    portHud.onSit = () => {
      // House: sit on a chair/bench/bed. Port (Onda Cidade Viva): sit on a decorated plaza bench. The scene
      // snaps the local avatar AND emits the wire sit; pressing away from any seat toasts (no wire, no pose).
      if (net.roomKind === RoomKind.HOUSE) {
        const b = housePred.body;
        if (houseScene?.toggleSit(b.x, b.y) === 'noseat') houseToast('Find yerself a seat first, captain');
      } else {
        const b = portPred.body;
        if (portScene?.toggleCitySit(b.x, b.y) === 'noseat') houseToast('Find yerself a seat first, captain');
      }
      audio.ui();
    };
    // ashore: the diegetic port stations open the same chapters directly, standalone (no Contents behind them). A
    // port can't be reached in the deferral window (you spawn at sea / in the house and must sail to it), so the
    // lazy Book/casino cluster is always in by now — but await it defensively so a very slow pipe still opens.
    portHud.onTrade = () => { void ensureGameUi().then(() => { casinoMenu?.close(); captainsBook?.openChapter('market', { standalone: true }); }); };
    portHud.onPlay = () => { // wave 7: the contextual station in reach opens its UI (casino / auction / clan hall)
      const st = nearStation; if (!st) return; // capture before the await (the station in reach can change)
      void ensureGameUi().then(() => {
        if (st.game === CasinoGame.AUCTION) { casinoMenu?.close(); auctionMenu?.setAvailable(true); captainsBook?.openChapter('auction', { standalone: true }); }
        else { captainsBook?.close(); casinoMenu?.openStation(st.id, st.game); audio.pageTurn(); }
      });
    };
    portHud.onLeave = () => { net.sendPort(PortOp.EXIT); audio.ui(); };
    hideMenu();
    requestAnimationFrame(loop); // ── the sea is now playable: render + net + HUD are all live ──
    void ensureGameUi();         // stream the Book / casino / chat / voice cluster in the background (post-spawn)
  };
  // "Entering the sea" — real handshake stages drive the status, not a mute "connecting". `connecting` = the socket
  // is opening; `entering` = it's open, hello sent, world sync inbound. On a bad network the watchdog + auto-retry
  // in net.ts keep this honest instead of hanging forever on a dead screen.
  net.onConnectProgress = (stage) => {
    if (connectDone) return;
    showStatus(stage === 'connecting' ? '⚓ Catching the wind…' : '🌊 Entering the sea…');
  };
  // A handshake attempt timed out / dropped and we're backing off for another go — diegetic, with the attempt count.
  net.onConnectRetry = (attempt, max) => {
    if (connectDone) return;
    showStatus(`🌬 The winds be against us — trying again… (${attempt + 1}/${max})`);
  };
  // Every attempt spent — a clear terminal message; the menu's SET SAIL button is the manual retry.
  net.onConnectFailed = () => {
    if (connectDone) return;
    showStatus('⚠ Couldn’t reach the sea. Check yer connection and tap SET SAIL to try again.');
  };
  // Stale cached client vs a bumped wire (v44): show "new version" and hard-reload to the fresh (no-cache) bundle.
  net.onVersionReject = () => {
    connectDone = true;
    showStatus('✨ A new version has arrived — reloading…');
    setTimeout(() => { try { location.reload(); } catch { /* noop */ } }, 1200);
  };
  net.onReject = (r) => {
    connectDone = true; // a server refusal is terminal for the connect flow — keep its message on screen
    // WHERE the reason goes is the load-bearing part, so the decision is a tested pure function (rejectui.ts):
    // `showStatus` writes into the MENU's status line, and the menu is removed from the DOM once the sea starts —
    // so an in-game refusal (the duplicate-session bump is always in-game) has to use the permanent HUD banner or
    // the captain sees nothing but a spinner. Session refusals are matched against the SHARED `REJECT` constants
    // instead of sniffed with a regex, so rewording the server text can't silently stop matching.
    const pres = rejectPresentation(r, net.welcomed);
    rejectedFull = pres.seaFull;
    if (pres.channel === 'hud') showTerminalReject(pres.text); else showStatus(pres.text);
  };
  // Initial-connect failure is owned by onConnectRetry/onConnectFailed above (net.ts auto-retries); a mid-game drop
  // is owned by the reconnect banner. So onClose no longer paints a dead "Connection failed" over an in-flight retry.
  net.onClose = () => { /* intentionally quiet — see onConnectFailed (initial) / onReconnecting (in-game) */ };
  net.onReconnecting = (attempt) => {
    showReconnecting(attempt);
    // The live link dropped. Arm a clean re-baseline from the FRESH authoritative self once we're back (the sea
    // loop freezes prediction until then), so the local ship snaps to its real rejoin spot instead of
    // dead-reckoning on from the stale pre-drop position. Clear a latched edge-turn + any autopilot mark so
    // neither a stale flat-earth banner nor a stale course can survive the drop.
    pendingRebaseline = true;
    prediction.hitEdge = false;
    hud?.clearWaypoint();
  };
  net.onReconnected = () => { hideReconnecting(); hud?.toast('⚓ Reconnected'); };
  net.onReconnectFailed = () => showReconnectFailed();
  // Deep-link "sail with me": forward any `?crew=<captain>` (from the invite link) onto the WS URL so the
  // server routes us into that captain's room. Absent → unchanged, so `?auto=1`/`?server=` are untouched.
  net.connect(withCrew(resolveServerUrl(), crewParam()), name);
}

/* ----------------------------- Lazy post-spawn UI cluster ----------------------------- */
// The Captain's Book (+ its chapters), the casino tables and chat/voice are ~85 KB gz of code that the sea itself
// never touches — every one is reached only by a discrete user action (tap 📖 / step into a port station / tap 💬)
// that cannot happen in the first playable moment. They are dynamic-imported HERE (their own chunks, kept out of
// the boot bundle) and constructed + wired exactly as the old synchronous onReady block did. The Book's 📖 and
// chat's 💬 float buttons are minted by those classes, so they simply appear a beat after spawn — no broken taps.
let gameUiPromise: Promise<void> | null = null;
/** Kick off (once) the background load+wiring of the deferred UI cluster; returns the shared promise. Idempotent. */
function ensureGameUi(): Promise<void> { return (gameUiPromise ??= bootGameUi()); }

async function bootGameUi(): Promise<void> {
  const [
    { BasePanel }, { ClanMenu }, { MarketMenu }, { ExchangeMenu }, { PremiumMenu }, { StoresMenu },
    { FreightMenu }, { AuctionMenu }, { CasinoMenu }, { CaptainsBook }, { VoiceEngine }, { ChatPanel },
    { DragaMenu }, { DragaScene },
  ] = await Promise.all([
    import('./basepanel.js'), import('./clanmenu.js'), import('./marketmenu.js'), import('./exchangemenu.js'), import('./premiummenu.js'), import('./storesmenu.js'),
    import('./freightmenu.js'), import('./auctionmenu.js'), import('./casinomenu.js'), import('./captainsbook.js'), import('./voice.js'), import('./chat.js'),
    import('./dragamenu.js'), import('./dragascene.js'),
  ]);
  // "The Book is the Menu": every former floating HUD toggle is a chapter of the Captain's Book. The menu classes
  // own their content/state/refresh; the Book drives their lifecycle through the shared BookChapter contract.
  basePanel = new BasePanel(overlay, net, () => audio.coins());
  basePanel.onSetSail = () => { hud?.raiseAnchor(); captainsBook?.close(); };
  // Home Port → "🏴‍☠️ Sail with me": share an invite link that drops a friend into your waters (?crew=you).
  basePanel.onInvite = () => { audio.ui(); void shareInvite(net.myDisplayName(), (m) => hud?.toast(m)); };
  clanMenu = new ClanMenu(net);
  marketMenu = new MarketMenu(net, audio);
  exchangeMenu = new ExchangeMenu(net, audio);
  premiumMenu = new PremiumMenu(net);
  storesMenu = new StoresMenu(net, audio);
  // craft-juice toast sink: ASHORE (walkable port/house) the sea HUD + its #toast live in the hidden sea
  // overlay, so a "⚒ Crafted …" cue routed there would never be seen — craft ashore now works, and its
  // success toast must show. Route to the body-level houseToast while ashore; the sea HUD toast at sea.
  storesMenu.toast = (m) => { if (net.portMode) houseToast(m); else hud?.toast(m); };
  freightMenu = new FreightMenu(net, audio);
  auctionMenu = new AuctionMenu(net, audio);
  casinoMenu = new CasinoMenu(overlay, net, audio);
  dragaMenu = new DragaMenu(net, audio);
  dragaScene = new DragaScene(renderer, audio, net, haptic);
  captainsBook = new CaptainsBook(overlay, net, audio, renderer);
  captainsBook.registerChapters([basePanel.chapter, clanMenu.chapter, marketMenu.chapter, exchangeMenu.chapter, dragaMenu.chapter, premiumMenu.chapter, storesMenu.chapter, freightMenu.chapter, auctionMenu.chapter]);
  // A Draga: a decided S_GACHA roll arrives → close the Book and play the reveal ritual (server already
  // debited/burned/granted; the client only dramatises). The pity meter is kept fresh on every reply.
  net.onGacha = (m) => {
    dragaScene?.setPity(m.pity);
    if (m.rolled && m.ok) { captainsBook?.close(); dragaScene?.play({ rarity: m.rarity, kind: m.itemKind, ref: m.itemRef, dup: m.dup, shardsGained: m.shardsGained }, () => { captainsBook?.openChapter('draga'); }); }
  };
  captainsBook.onHudEdit = () => { void openHudForge(); }; // HUD Forge: Settings → Controls → Edit
  // How to Play → the two onboarding surfaces: the illustrated Primer manual (opens over everything) and an
  // ephemeral Helm Coach replay (the coach-marks resume at sea; a quit mid-replay never re-arms a veteran).
  captainsBook.onOpenManual = () => { captainsBook?.close(); primer?.open(); };
  captainsBook.onReplayCoach = () => {
    captainsBook?.close();
    coach?.restart();
    if (net.portMode) houseToast('⚓ The helm lessons resume at sea');
  };
  // Fullscreen toggle: turning it ON re-arms the golden rule + enters on this very click; OFF exits.
  captainsBook.onFullscreen = (on: boolean) => { if (on) { fsSuppressed = false; goFullscreen(); } else { exitFullscreen(); } };
  basePanel.onAutoOpen = () => captainsBook?.openChapter('homeport'); // docking rising edge → the Home Port chapter
  casinoMenu.closeOthers = () => captainsBook?.close(); // opening a casino table closes the Book
  casinoMenu.onReact = (stationId, kind) => portScene?.dealerReact(stationId, kind); // Onda Dealers — table events nudge the croupier
  // Onda Social (v40): the chat panel + the WebRTC voice engine. selfName/nameOf resolve the authoritative cached
  // name (sea: ship id; ashore: avatar id), falling back to the name the player set sail with.
  const selfName = (): string => (net.portMode ? net.avatarNames.get(net.yourAvatarId) : net.names.get(net.yourId)) || myName;
  const nameOf = (id: number): string => (net.portMode ? net.avatarNames.get(id) : net.names.get(id)) || '';
  voiceEngine = new VoiceEngine(net, audio, nameOf, (n) => chatPanel?.isMutedName(n) ?? false,
    (n, on) => chatPanel?.setSpeaking(n, on), (on) => chatPanel?.setLocalSpeaking(on),
    (n, st) => chatPanel?.setPeerState(n, st),      // per-peer voice link indicator (connecting/connected/failed)
    (t) => hud?.toast(t));                          // one-time "voice couldn't connect" notice
  chatPanel = new ChatPanel(net, audio, selfName, voiceEngine, !renderer.isMobile);
  chatPanel.onSend = (ch, text) => net.sendChat(ch, text);
  net.onChat = (m) => chatPanel?.push(m);          // net invokes these with `?.` — safe if a msg lands pre-wire
  net.onRtc = (m) => voiceEngine?.handleRtc(m);
  // ⚓ Captain's Orders (v46): an owner broadcast BANNER — a body-level overlay that shows at sea AND ashore, then
  // auto-dismisses. The CHAT half of a broadcast (mode chat|both) arrives via the S_CHAT path above as a system line.
  net.onAnnounce = (m) => hud?.announceBanner(m.text, m.level, m.durationMs);
  // Re-apply the one-shot join state that landed WHILE the cluster was still streaming: the world (outposts) is
  // sent once and gated behind `worldApplied` in the loop, so freightMenu — built here, after that fired — must be
  // handed the outposts directly or it would never learn them.
  if (net.world) freightMenu.setWorld(net.world.outposts);
  applyHudLayout(); // HUD Forge: the floating 💬/🎒 buttons are born here — re-apply the saved layout
}

let hiddenOverlayEls: HTMLElement[] = [];
function handlePortTransition(): void {
  if (net.portMode) {
    const isHouse = net.roomKind === RoomKind.HOUSE;
    // MEMORY RELIEF (the wallet-webview blue-screen fix): free the heavy sea GLBs (islands/outposts/landmarks +
    // player bases + shoals + remote hulls) BEFORE the walkable port streams its own kit into a second scene on the
    // shared GL context. Without this the two peak together and a WKWebView (Phantom/MetaMask) OOMs → blue screen.
    // Everything rebuilds deterministically from renderer-owned state on the way back out (restoreSeaAssets).
    renderer.releaseSeaAssets();
    coach?.onLeaveSea(); // Helm Coach: the sea controls left the screen — park the card (state is kept)
    prediction.hitEdge = false; // don't let an edge turn latched at sea flash the banner on the next sea frame
    hud?.clearWaypoint(); // drop any tap-to-sail course when stepping ashore
    combatFx?.reset(); // no damage arc/vignette may bleed into the port scene
    captainsBook?.close(); // closing the Book unmounts whatever chapter was open
    // hide every sea overlay element (the 📖 toggle lives directly on `overlay`) except the port HUD.
    hiddenOverlayEls = [];
    for (const el of Array.from(overlay.children) as HTMLElement[]) {
      if (el.id === 'porthud' || el.style.display === 'none') continue;
      hiddenOverlayEls.push(el); el.style.display = 'none';
    }
    eventEl.style.display = 'none';
    enterPortBtn.style.display = 'none';
    enterHouseBtn.style.display = 'none';
    portPopChip.style.display = 'none';
    portHud?.show();
    if (isHouse) {
      // ---- Onda 8: the captain's HOUSE (a rectangular interior; same walker path + avatar HUD) ----
      // ZERO-FRICTION ONBOARDING: a brand-new captain (Helm Coach still owed, never reached the sea this
      // session) is Set Sail'd straight back out — no cabin, no 8 MB of interior GLBs, no "find the door". We
      // mask the ~1s handoff and fire the SAME PortOp.EXIT the Set Sail button uses. Any later HOME visit
      // (reachedSea already true) or a veteran builds the cabin normally below.
      const newbieFirstSpawn = (coach?.armed ?? false) && !reachedSeaThisSession;
      // Common CHEAP setup (procedural cabin only) — runs for everyone so runHouse always has a valid scene to
      // render behind the mask. The HEAVY calls (GLB avatar/interior preload, furniture state) are skipped for a
      // newcomer, since they're leaving for the sea this same second.
      houseInputAcc = 0; houseSeq = 0; lastHouseSnap = -1; lastHouseV = -1;
      housePred.hasBaseline = false;
      housePred.setObstacles(houseScene?.solidObstacles() ?? []);
      houseScene?.reset();
      houseScene?.setOrbitActive(true); portScene?.setOrbitActive(false); // drag-to-orbit for the live scene only
      // craft-ashore: the cabin IS the top-tier home workshop — craft feeds the base STOCKPILE (home=true), so
      // the craft-juice baseline is the stockpile, not the hold. The sea loop that normally sets this never runs
      // ashore (loop early-returns in port mode), so pin it here explicitly.
      if (storesMenu) { storesMenu.docked = true; storesMenu.port = 0; }
      if (newbieFirstSpawn) {
        showHoist();               // mask the ~1s house→sea handoff so the bare cabin never flashes
        suppressHouseLoop = true;  // freeze runHouse so it can't stream the interior GLBs while we leave
        if (!newbieAutoSailSent) {
          newbieAutoSailSent = true;
          net.sendPort(PortOp.EXIT); // existing wire — routes house → sea, near the captain's own base
          // Safety net (coordinator's fallback): if the sea somehow hasn't arrived, un-freeze the cabin (it now
          // builds in full), drop the mask, re-fire EXIT once, and give them a real, tappable Set Sail.
          window.setTimeout(() => {
            if (reachedSeaThisSession || !net.portMode || net.roomKind !== RoomKind.HOUSE) return;
            suppressHouseLoop = false;
            net.sendPort(PortOp.EXIT);
            hideHoist();
            portHud?.setHouseMode(true, net.house?.youAreOwner ?? false);
            houseToast('⚓ Tap Set Sail to reach the open sea');
          }, 4500);
        }
      } else {
        // Returning to (or a veteran visiting) the cabin via HOME — build it in full.
        void houseScene?.preload(); // the captain avatars (reused from the port) + interior GLB heroes
        if (net.house) houseScene?.setHouseState(net.house);
        portHud?.setHouseMode(true, net.house?.youAreOwner ?? false);
        portHud?.setOpenToClan(net.house?.openToClan ?? false);
        portHud?.setTradeAvailable(false); portHud?.setPlay(null); portHud?.setKickable(-1);
        audio.setPortAmbient(true, 0, false);
      }
    } else {
      // ---- stepped ashore into a normal walkable port (dense Capital vs village plaza) ----
      const cap = isCapitalOutpost(net.portOutpost);
      const lay = resolvePortLayout(net.portOutpost, cap);
      portScene?.setLayout(lay, cap);
      portScene?.setMerchant(net.portOutpost); // Onda NPC Mercante — one deterministic market NPC per city
      portScene?.setDealers(net.portOutpost);   // Onda Dealers — a croupier behind each blackjack + poker table
      portPred.setLayout(lay.obstacles, lay.size);
      // Onda Parede-invisível-2: `lay` came from the LAST /editor/layouts fetch, which may predate the owner's
      // newest capital save (the server already hot-swapped every live room to it — PortRoom.applyLayout). Re-fetch
      // now; if the current slice differs from the one we entered on, rebuild the scene + prediction so the client
      // predicts against the SAME obstacles the server collides against (kills the felt-but-unseen wall). The
      // common no-change re-fetch rebuilds nothing (signature match).
      const entryOutpost = net.portOutpost;
      const entrySig = portLayoutSig(lay);
      void refreshPortOverride().then(() => {
        if (!net.portMode || net.roomKind === RoomKind.HOUSE || net.portOutpost !== entryOutpost) return; // moved on
        const cap2 = isCapitalOutpost(net.portOutpost);
        const fresh = resolvePortLayout(net.portOutpost, cap2);
        if (portLayoutSig(fresh) === entrySig) return; // slice unchanged → nothing to rebuild
        portScene?.setLayout(fresh, cap2);
        portPred.setLayout(fresh.obstacles, fresh.size);
        if (cap2 || (fresh.props?.length ?? 0) > 0) void portScene?.preloadCapital();
        portScene?.preload().then(() => { if (net.portMode) portScene?.reset(); }).catch(() => {});
        portScene?.reset();
      });
      if (cap) { playEntryFade(CAPITAL.FADE_MS); void portScene?.preloadCapital(); } // the harbour reveal
      else if ((lay.props?.length ?? 0) > 0) void portScene?.preloadCapital(); // Skull Harbor: a kit village needs the same GLB kit baked
      portScene?.preload().then(() => { if (net.portMode) portScene?.reset(); }).catch(() => {}); // rebuild as GLBs land
      portScene?.reset();
      portPred.hasBaseline = false;
      portSeq = 0; lastPortSnap = -1; portInputAcc = 0;
      portHud?.setHouseMode(false, false); // ensure the house buttons are hidden + Leave label reset
      portScene?.setOrbitActive(true); houseScene?.setOrbitActive(false); // drag-to-orbit for the live scene only
      audio.setPortAmbient(true, 0, cap); // the Capital layers a city-crowd murmur over the port ambiance
      // craft-ashore: the plaza is a PUBLIC station — craft feeds the carried HOLD (home=false). The sea loop
      // that normally sets this never runs ashore, so pin the craft-juice baseline (hold) here explicitly.
      if (storesMenu) { storesMenu.docked = false; storesMenu.port = cap ? 2 : 1; }
    }
  } else {
    // sailed back out to the sea
    // MEMORY RELIEF (symmetric): rebuild the sea GLBs freed on the way ashore (islands/outposts/bases/shoals from
    // renderer-owned last-payloads; remote hulls re-materialise from the next snapshot). No-op if never released.
    renderer.restoreSeaAssets();
    // ZERO-FRICTION ONBOARDING: we've reached open water — from here on a HOME visit builds the cabin normally,
    // and the newcomer auto-sail never fires again. Drop the "hoisting the sails" mask now that the sea is here.
    reachedSeaThisSession = true;
    suppressHouseLoop = false;
    hideHoist();
    // Frente 2 fix: drop the STALE sea baseline so the first frames don't predict on the pre-port body (net
    // has already nulled selfAuth). hasBaseline=false makes applyInput a no-op until initFrom re-seats the
    // ship from the fresh authoritative spawn — exactly the respawn path — so `enforceWorldEdge` never runs
    // on a teleported/stale body and the flat-earth banner can't false-fire on the way out of a city.
    prediction.hasBaseline = false;
    prediction.hitEdge = false;
    seaResumedAt = performance.now();
    decorateCtl?.close(); decorateCtl = null;
    portScene?.setOrbitActive(false); houseScene?.setOrbitActive(false); // the sea owns the camera drag again
    portHud?.hide();
    portScene?.reset(); houseScene?.reset();
    portScene?.clearMerchant(); // Onda NPC Mercante — drop the city merchant on the way back to sea
    portScene?.clearDealers();  // Onda Dealers — drop the casino croupiers on the way back to sea
    portHud?.setHouseMode(false, false);
    captainsBook?.close(); // drop any ashore-summoned chapter (Market / Auction)
    casinoMenu?.close(); nearStation = null; portHud?.setPlay(null);
    for (const el of hiddenOverlayEls) el.style.display = '';
    hiddenOverlayEls = [];
    lastCalV = -1; // re-evaluate today's event banner
    hud?.setVisible(true);
    audio.setPortAmbient(false, 0);
    coach?.onSeaEntry(); // Helm Coach: the sea controls are on screen — start (brand-new captain) or re-show
    telemetryFirstSpawn(); // P1 funnel: first time the captain reaches open water this session (once)
  }
}

/** Lazy-load the Decorate chunk (owner only) and open the furniture editor over the house scene. */
function openDecorateMode(): void {
  if (decorateCtl || !houseScene) return;
  void import('./decorate.js').then((m) => {
    if (!net.portMode || net.roomKind !== RoomKind.HOUSE || !houseScene) return; // left before it loaded
    decorateCtl = m.openDecorate({ houseScene, net, audio, onClose: () => { decorateCtl = null; } });
  }).catch((err) => { console.error('[decorate] failed to load', err); });
}

/** The ashore game loop for the captain's HOUSE — mirrors runPort but uses the rectangular HousePrediction
 *  + HouseScene, reflects S_HOUSE_STATE into the HUD, and routes S_HOUSE_EDIT results to decorate. */
function runHouse(now: number, dt: number): void {
  const house = net.house;
  if (net.houseVersion !== lastHouseV) {
    lastHouseV = net.houseVersion;
    if (house) houseScene?.setHouseState(house);
    housePred.setObstacles(houseScene?.solidObstacles() ?? []); // solid furniture become walker obstacles
    portHud?.setHouseMode(true, house?.youAreOwner ?? false);
    portHud?.setOpenToClan(house?.openToClan ?? false);
    decorateCtl?.refresh();
  }
  if (!housePred.hasBaseline && net.portSelf) housePred.initFrom(net.portSelf);
  if (net.portSnapSeq !== lastHouseSnap) {
    lastHouseSnap = net.portSnapSeq;
    if (net.portSelf && housePred.hasBaseline) housePred.reconcile(net.portSelf, net.portAckSeq);
  }
  houseInputAcc += dt;
  let guard = 0;
  while (houseInputAcc >= INPUT_DT && guard++ < 5) {
    const mv = portHud?.sampleMove() ?? { moveX: 0, moveY: 0 };
    // Camera-relative walk (WoW/L2): forward (mv.moveY) advances where the CAMERA looks and strafe (mv.moveX)
    // goes to its right — not map-north. Rotate the intent into the world plane on the CLIENT before it's sent
    // + predicted, so the wire and the shared walker sim are untouched (prediction stays == server).
    // v37 fix: the strafe was MIRRORED (push-right walked left). With the camera trailing behind camYaw and
    // three's lookAt/up, the on-screen RIGHT vector in the sim plane is (-f.y, f.x), not (f.y, -f.x) — proven
    // across every azimuth. Forward (mv.moveY·f) was already correct, so only the strafe sign flips.
    const f = houseScene?.groundForward() ?? { x: 0, y: 1 };
    const wx = mv.moveY * f.x - mv.moveX * f.y, wy = mv.moveY * f.y + mv.moveX * f.x;
    houseSeq += 1;
    net.sendWalk({ seq: houseSeq, clientTimeMs: now, moveX: wx, moveY: wy, buttons: WalkBtn.INTERACT & 0 });
    housePred.applyInput(houseSeq, { moveX: wx, moveY: wy });
    houseInputAcc -= INPUT_DT;
  }
  const t = serverSeconds();
  const b = housePred.body;
  const moving = housePred.hasBaseline && (b.vx * b.vx + b.vy * b.vy) > 0.09;
  // 60fps smoothing: the walker sim steps at INPUT_HZ (30) but we render every rAF (60/120+ Hz). Dead-reckon
  // the local avatar forward by the leftover input accumulator so its motion is smooth on EVERY frame instead
  // of frozen on ~half of them (the "podre" desktop stutter). Continuous across steps; reconcile still owns truth.
  const local = housePred.hasBaseline
    ? { x: b.x + b.vx * houseInputAcc, y: b.y + b.vy * houseInputAcc, heading: b.heading, moving, cls: net.shipClass, skin: net.mySkin }
    : null;
  const remotes = net.getInterpAvatars();
  for (const e of net.portEmoteQueue) houseScene?.playEmote(e.id, e.emote);
  net.portEmoteQueue.length = 0;
  houseScene?.sync(local, net.yourAvatarId, remotes, t, dt);
  // v43 Synced Sit: apply seated/stood poses for OTHER visitors (self owns its sit locally). After sync so the
  // avatar object is ensured; the held state persists across frames until a stand event flips it back.
  for (const e of net.portSitQueue) if (e.id !== net.yourAvatarId) houseScene?.applyRemoteSit(e);
  net.portSitQueue.length = 0;
  // Interior Vivo: contextual Sit button + positional wooden footsteps paced to the avatar's real speed.
  const seated = houseScene?.isSitting() ?? false;
  // In the house the Sit affordance is always offered on foot (there is furniture to find); pressing it away
  // from a seat toasts. The port has no seats, so setHouseMode(false) keeps this button hidden there.
  portHud?.setSittable(!!local || seated, seated);
  if (local && moving && !seated) {
    houseStepAcc += Math.hypot(b.vx, b.vy) * dt;
    if (houseStepAcc >= 1.05) { houseStepAcc = 0; audio.footstep(0.9 + Math.random() * 0.16); } // wood creak, pitch varied
  } else { houseStepAcc = 1.05; } // primed so the first step fires the instant they set off again
  portHud?.setWealth(house?.doubloons ?? net.portSelf?.doubloons ?? 0);
  decorateCtl?.refresh();
  // owner's contextual Boot: the nearest visitor within reach
  if (local && house?.youAreOwner && remotes.length) {
    let bestId = -1, bestD = 5.5 * 5.5;
    for (const r of remotes) { const d2 = (r.x - local.x) ** 2 + (r.y - local.y) ** 2; if (d2 < bestD) { bestD = d2; bestId = r.id; } }
    portHud?.setKickable(bestId);
  } else portHud?.setKickable(-1);
  if (local) {
    audio.setPortAmbient(true, houseScene?.nearHearth(local.x, local.y) ?? 0, false); // fireside crossfade
    const atExit = houseScene?.atExit(local.x, local.y) ?? false;
    // dig-ux: standing at the decorative Chart Table points to the REAL treasure-map craft (a port's Ship's
    // Stores) — the leaving hint still wins when you're on the door.
    const nearChart = !atExit && (houseScene?.nearChartTable(local.x, local.y) ?? false);
    portHud?.setHint(atExit ? 'Walk through the door to set sail ⚓'
      : nearChart ? '🗺 Décor only — craft a Treasure Map at any port’s Ship’s Stores' : null);
  }
  // S_HOUSE_EDIT results → decorate (ack/reject reconcile) or a plain toast
  for (const res of net.houseEditQueue) { if (decorateCtl) decorateCtl.onResult(res); else if (!res.ok) houseToast(res.reason || 'Not allowed'); }
  net.houseEditQueue.length = 0;
}

/** The ashore game loop: fixed-rate walk input + prediction, avatar sync, port HUD + audio. */
function runPort(now: number, dt: number): void {
  if (!portPred.hasBaseline && net.portSelf) portPred.initFrom(net.portSelf);
  if (net.portSnapSeq !== lastPortSnap) {
    lastPortSnap = net.portSnapSeq;
    if (net.portSelf && portPred.hasBaseline) portPred.reconcile(net.portSelf, net.portAckSeq);
  }
  portInputAcc += dt;
  let guard = 0;
  while (portInputAcc >= INPUT_DT && guard++ < 5) {
    const mv = portHud?.sampleMove() ?? { moveX: 0, moveY: 0 };
    // Camera-relative walk (same as the house loop): forward goes where the camera looks, strafe to its right.
    // Rotated client-side before send + predict → wire + shared walker sim untouched (prediction == server).
    // v37 fix: screen-right in the sim plane is (-f.y, f.x); the old (f.y, -f.x) strafe was mirrored.
    const f = portScene?.groundForward() ?? { x: 0, y: 1 };
    const wx = mv.moveY * f.x - mv.moveX * f.y, wy = mv.moveY * f.y + mv.moveX * f.x;
    portSeq += 1;
    net.sendWalk({ seq: portSeq, clientTimeMs: now, moveX: wx, moveY: wy, buttons: WalkBtn.INTERACT & 0 });
    portPred.applyInput(portSeq, { moveX: wx, moveY: wy });
    portInputAcc -= INPUT_DT;
  }
  const t = serverSeconds();
  const b = portPred.body;
  const moving = portPred.hasBaseline && (b.vx * b.vx + b.vy * b.vy) > 0.09;
  // 60fps smoothing (same as the house loop): dead-reckon the local avatar by the leftover input accumulator
  // so the 30 Hz walker sim renders as smooth motion every frame, not the every-other-frame desktop stutter.
  const local = portPred.hasBaseline
    ? { x: b.x + b.vx * portInputAcc, y: b.y + b.vy * portInputAcc, heading: b.heading, moving, cls: net.shipClass, skin: net.mySkin }
    : null;
  const remotes = net.getInterpAvatars();
  for (const e of net.portEmoteQueue) portScene?.playEmote(e.id, e.emote);
  net.portEmoteQueue.length = 0;
  portScene?.sync(local, net.yourAvatarId, remotes, t, dt);
  // v43 Synced Sit: apply seated/stood poses for OTHER captains on the plaza (self owns its sit locally).
  for (const e of net.portSitQueue) if (e.id !== net.yourAvatarId) portScene?.applyRemoteSit(e);
  net.portSitQueue.length = 0;
  // HUD: wealth + contextual trade/play buttons + tavern-audio crossfade + a dock/stall/table hint
  const near = local ? (portScene?.nearestStall(local.x, local.y) ?? -1) : -1;
  nearStation = local ? (portScene?.nearestStation(local.x, local.y) ?? null) : null;
  // Onda Cidade Viva — offer the contextual Sit button only when a decorated plaza bench is within reach (or the
  // captain is already seated on one). The port had no seats before, so this button stayed hidden there.
  const seatedCity = portScene?.isCitySitting() ?? false;
  const canSit = !!local && !!portScene?.citySittableAt(local.x, local.y);
  portHud?.setSittable(canSit || seatedCity, seatedCity);
  portHud?.setTradeAvailable(near >= 0);
  portHud?.setPlay(nearStation ? stationLabel(nearStation.game) : null);
  portHud?.setWealth(net.portSelf?.doubloons ?? 0);
  // walking out of reach of the open casino table closes it (poker stands you up + cashes out server-side)
  if (casinoMenu?.isOpen() && (!nearStation || nearStation.id !== casinoMenu.openedStation())) casinoMenu.close();
  if (local) {
    audio.setPortAmbient(true, portScene?.tavernProximity(local.x, local.y) ?? 0, isCapitalOutpost(net.portOutpost));
    const atExit = portScene?.atExit(local.x, local.y) ?? false;
    portHud?.setHint(nearStation ? stationHint(nearStation.game) : near >= 0 ? (portScene?.merchantHintFor(near) ?? '🛒 Trade at the stall') : atExit ? 'Walk onto the dock to set sail ⚓' : null);
  }
  // "Live Wins" feed — big casino payouts across the plaza pop as toasts
  if (net.casinoFeed.length) { for (const f of net.casinoFeed) hud?.toast(`🎰 ${f.name} won ${Math.round(f.profit).toLocaleString()} at ${f.game}!`); net.casinoFeed.length = 0; }
  captainsBook?.tick(); // refreshes an ashore-summoned Book chapter (Market / Auction) + auto-closes on gating
  // craft-juice feedback is polled once per ashore frame by the caller (loop's port-mode branch) — covering
  // both this plaza loop and the house loop, so a craft armed anywhere ashore resolves. (was here, moved up.)
  casinoMenu?.maybeRefresh();
}

/** Wave 7: the contextual "Play" button label + hint for each casino/auction/clan station. */
function stationLabel(game: number): string {
  return game === CasinoGame.SLOTS ? '🎰 Slots' : game === CasinoGame.BLACKJACK ? '🂡 Blackjack'
    : game === CasinoGame.POKER ? '🃏 Poker' : game === CasinoGame.AUCTION ? '🔨 Auction' : '🏆 Champions';
}
function stationHint(game: number): string {
  return game === CasinoGame.SLOTS ? '🎰 Try your luck at the slots' : game === CasinoGame.BLACKJACK ? '🂡 Sit down for blackjack'
    : game === CasinoGame.POKER ? "🃏 Join the Hold'em table" : game === CasinoGame.AUCTION ? '🔨 Bid at the auction house' : '🏆 View the Hall of Champions';
}

/** Onda Social (v40): feed the voice engine the local pose + a resolver for each peer entity's live position
 *  so it can place the spatial panners and run VAD — works in all three loops (sea / port / house). */
function updateVoice(): void {
  const v = voiceEngine; if (!v || !v.ready()) return;
  if (net.portMode) {
    const pred = net.roomKind === RoomKind.HOUSE ? housePred : portPred;
    if (!pred.hasBaseline) return;
    const b = pred.body;
    const map = new Map(net.getInterpAvatars().map((a) => [a.id, { x: a.x, y: a.y }] as const));
    v.update(b.x, b.y, b.heading, (id) => (id === net.yourAvatarId ? { x: b.x, y: b.y } : map.get(id) ?? null));
  } else {
    if (!prediction.hasBaseline) { if (net.selfAuth) v.update(net.selfAuth.x, net.selfAuth.y, net.selfAuth.heading, () => null); return; }
    const b = prediction.body;
    const map = new Map(net.getInterpShips().map((s) => [s.id, { x: s.x, y: s.y }] as const));
    v.update(b.x, b.y, b.heading, (id) => (id === net.yourId ? { x: b.x, y: b.y } : map.get(id) ?? null));
  }
}

let loopFrames = 0;
function loop(now: number): void {
  // WKWebView OOM: the GL context is gone and the parchment reload card is up — stop the frame loop entirely so we
  // never poke a dead context (silent console errors) while the captain reads the card and taps Reload.
  if (renderer.contextLost) return;
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  fps += ((1 / Math.max(1e-3, dt)) - fps) * 0.1;
  // P1 funnel: the first playable scene has been drawn (any mode — sea, house, or port). We're on the 2nd loop
  // tick here, so exactly one full frame (net + render) completed — the honest "first paint" breadcrumb between
  // the handshake and reaching open water. Once-guarded downstream, so this fires exactly once per session.
  if (++loopFrames === 2) telemetryFunnel('first_frame');

  updateVoice(); // Onda Social: keep the spatial voice mesh placed + VAD-gated every frame (both loops)

  // A Draga ritual takes over the frame while active: it renders its own cinematic scene to the shared canvas
  // (the game render + input are paused behind its full-screen overlay). Net keeps flowing via ws events.
  if (dragaScene?.isActive()) { dragaScene.frame(serverSeconds(), dt); requestAnimationFrame(loop); return; }

  // Cinematic Kill-Cam takes over the frame while a highlight replay is active: prediction + input are paused
  // behind its cinematic (invariant #1 — everything it draws is interpolated from the frozen buffer). Net keeps
  // flowing via ws events; the sea loop resumes untouched when it closes.
  if (killcam?.isActive()) { killcam.frame(dt); requestAnimationFrame(loop); return; }

  // Self-heal the saved captain name (bug fix — "the name won't save"): once the server's authoritative name is
  // known, cache it so the menu re-shows it next visit — including for captains who predate client-side name
  // persistence. RUNS IN EVERY MODE: a v42 newcomer is BORN in their HOUSE and a docked captain is ashore, so
  // reading only the sea `net.names` below the port-mode early-return meant this never fired for them and their
  // real name was never cached → the menu re-showed a random Captain#### every visit. Ashore the authoritative
  // self name lives in `avatarNames[yourAvatarId]`, at sea in `names[yourId]`.
  if (!nameCached) {
    const authName = net.portMode ? net.avatarNames.get(net.yourAvatarId) : net.names.get(net.yourId);
    if (authName) { nameCached = true; try { localStorage.setItem('sor:name', authName); } catch { /* private mode */ } }
  }

  // wave 6: port-mode transitions + the ashore loop (skips the entire sea flow while ashore)
  if (net.portModeVersion !== lastPortModeV) { lastPortModeV = net.portModeVersion; handlePortTransition(); }
  if (net.portMode) {
    // ZERO-FRICTION ONBOARDING: while a newcomer is being auto-sailed out, runHouse is frozen (its houseVersion
    // path would otherwise stream the interior GLBs) — the "hoisting the sails" mask covers the still frame.
    if (net.roomKind === RoomKind.HOUSE) { if (!suppressHouseLoop) runHouse(now, dt); } else runPort(now, dt);
    // craft-juice fix: resolve any armed craft/ammo-buy in BOTH ashore contexts — the plaza (runPort) AND the
    // captain's house (runHouse). runHouse never polled, so a craft armed inside the house stuck on "Crafting…"
    // forever (no success, no failure). Polling here (outside the runHouse guard) also covers the frozen-newbie
    // frames. Idempotent + closed-over pending set, so it's safe to call every ashore frame.
    storesMenu?.pollCraftFeedback();
    requestAnimationFrame(loop); return;
  }

  if (!worldApplied && net.world) {
    renderer.setWorld(net.world); renderer.setShoals(net.world.shoals); freightMenu?.setWorld(net.world.outposts);
    // v43: the treasure islands become solid CORES for the local ship's prediction — the identical `collideCircles`
    // input the server merges in (room.ts islandSolids), so the predicted hull never sails through an islet either.
    treasureObstacles = net.world.islands.map((i) => ({ x: i.x, y: i.y, r: i.radius * TREASURE_ISLAND_COLLIDE_FRAC }));
    solidObstacles = treasureObstacles.concat(net.bases.map((b) => ({ x: b.x, y: b.y, r: ISLAND_R })));
    prediction.setObstacles(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11);
    autoSail.setChart(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11); // same chart the hull collides with
    worldApplied = true;
  }
  // Onda Carona: mirror the authoritative taken-chest set into the renderer whenever it changes (hoist by
  // another captain, respawn, or the resync burst on join/reconnect) so a looted chest vanishes for everyone.
  if (worldApplied && net.chestStateVersion !== lastChestStateV) { lastChestStateV = net.chestStateVersion; renderer.setChestStates(net.takenChests); }
  if (net.basesVersion !== lastBasesVersion) {
    lastBasesVersion = net.basesVersion;
    renderer.syncBases(net.bases, net.yourIslandId);
    solidObstacles = treasureObstacles.concat(net.bases.map((b) => ({ x: b.x, y: b.y, r: ISLAND_R }))); // v43: treasure cores + base islands
    prediction.setObstacles(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11);
    autoSail.setChart(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11);
  }
  if (net.shipClass !== lastShipClass) { // shipyard swap changes the hull radius
    lastShipClass = net.shipClass;
    prediction.setObstacles(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11);
    autoSail.setChart(solidObstacles, (SHIP_CLASS as Record<number, { radius: number }>)[net.shipClass]?.radius ?? 11);
  }

  const self = net.self;
  const alive = !self || self.respawnIn <= 0;
  // chain-shot rigging + grapple drag cripple mobility — the server applies the same
  // factors, so feeding them into prediction keeps the local ship in lockstep with authority.
  const rig = self?.riggingDamage ?? 0;
  const grp = self?.grappleFactor ?? 0;
  const mob = (1 - rig) * (1 - grp);
  // wave 2b: grog (sail buff) + sailing perk + the Leviathan's Roe surge ride SelfState.speedBuffMul — fold
  // into prediction so the local ship stays in lockstep with the server (same factor applied there).
  const buffMul = self && self.speedBuffMul > 0 ? self.speedBuffMul : 1;
  const effSpeedMul = net.speedMul * mob * buffMul;
  // storm steers a touch heavier + the Reef Snapper fish buff tightens the helm — apply the SAME factors the
  // server uses (turnBuffMul rides SelfState like speedBuffMul) so prediction stays exact while turning.
  const turnBuff = self && (self.turnBuffMul ?? 0) > 0 ? self.turnBuffMul! : 1;
  const effTurnMul = net.turnMul * mob * (1 - net.stormIntensity * STORM.TURN_RESIST) * turnBuff;
  // Cargo Runs: the LOCAL ship's weight load (0..1) = trade goods aboard / this hull's cargo cap. Fed into
  // prediction so the laden top-speed scrub (stepShipBody) matches the server tick-for-tick (no rubber-band).
  // Sinks P3: prefer the server's RESOLVED cap (net.cargoCap, incl. the Stevedore's Hook charm) so a charmed
  // hull's weight penalty predicts identically; fall back to cargoOf() on a legacy/short profile read.
  const shipCargoCap = Math.max(1, net.cargoCap || cargoOf(net.shipClass, net.loadout.cargoLvl));
  const cargoLoad = Math.min(1, (self?.cargoUnits ?? 0) / shipCargoCap);

  // death/respawn baseline management. `pendingRebaseline` (set on a reconnect) forces a re-seed even when a
  // stale baseline survived the drop, so the local ship hard-snaps to the FRESH rejoin spot (base / restored
  // position / resumed husk) instead of continuing from the pre-drop position — the mobile reconnect glitch.
  if (wasAlive && !alive) { prediction.hasBaseline = false; hud?.clearWaypoint(); }
  if (alive && net.selfAuth && (!prediction.hasBaseline || pendingRebaseline)) {
    pendingRebaseline = false;
    prediction.initFrom(net.selfAuth, cargoLoad);
    // Flat-earth banner guard (extends Onda Fix 5 frente 2 to EVERY re-seat). handlePortTransition's sea-exit
    // branch already stamps seaResumedAt on a deliberate port/house handoff, but a plain sea re-baseline —
    // a reconnect rejoin (pendingRebaseline) or a respawn/first-spawn — NEVER runs that branch, so the 1.5 s
    // banner-suppression window was missing there. A rejoin/respawn teleports the ship to a fresh authoritative
    // spot; stamping the window here too means the "coming about" banner can't flash on the first frames after
    // ANY handoff back to open water (the owner's report), only on a genuine mid-sea edge approach. Spawns are
    // always ≥ WORLD_EDGE.MARGIN off the border, so this never swallows a real come-about (≥5 s of sailing away).
    seaResumedAt = performance.now();
    // P1 funnel: the local ship is now seated + controllable at open sea. This is the honest "reached open water"
    // moment for EVERY captain — including a returning one who spawns straight at sea via S_WELCOME and so never
    // trips handlePortTransition's sea-exit branch (where first_spawn also fires for house-born newcomers). Both
    // are once-guarded, so this only counts the first reach; without it, sea-spawned captains were invisible to
    // the funnel and the connect→first_spawn drop read worse than reality.
    telemetryFirstSpawn();
    // COLD-LOAD DIET: the local ship is seated at OPEN SEA — the earliest moment the deferred world GLBs are
    // actually needed (and provably not in the cabin/port). Kick the flood now; it's idempotent, so the later
    // respawns that also reach this block are harmless no-ops, and it never runs while ashore.
    void renderer.beginWorldAssets();
    // spawn/respawn: seat the camera behind the fresh ship (sea ahead) so the first frame is framed to
    // sail, not buried in the base geometry. Runs on both the first spawn and every later respawn.
    renderer.resetCameraBehind(net.selfAuth.x, net.selfAuth.y, net.selfAuth.heading, serverSeconds());
    // Kill-Cam: a fresh spawn TELEPORTS the hull across the sea — wipe the rolling tape so a future clip can
    // never splice the old life's position into the new one (a death's highlight was already frozen on the sink).
    killcam?.clearTape();
  }
  wasAlive = alive;

  // Push opt-in TIME FALLBACK (item 5): count only ACTIVE sea time (alive, in the sea loop — port/house frames
  // return early above, so they never accrue). Once a captain has been engaged this long without hitting a
  // victory beat, offer the one-time ask anyway. maybeAskPushLater is idempotent + gated (onboarded, once/device).
  if (started && alive) {
    seaActiveMs += dt * 1000;
    if (!sea60Sent && seaActiveMs >= 60_000) { sea60Sent = true; telemetrySea60(); } // cleared the ~14 s bounce zone
    if (seaActiveMs >= PUSH_OPTIN.SEA_FALLBACK_MS) maybeAskPushLater(0);
  }

  // reconcile on each new authoritative snapshot
  if (net.snapSeq !== lastSnap) {
    lastSnap = net.snapSeq;
    if (alive && net.selfAuth && prediction.hasBaseline) prediction.reconcile(net.selfAuth, net.ackSeq, net.wind, effSpeedMul, effTurnMul, self?.knockX ?? 0, self?.knockY ?? 0, cargoLoad);
  }

  // fixed-rate input + prediction. FROZEN while the socket is down / awaiting the post-reconnect re-baseline:
  // dead-reckoning on from the stale pre-drop position would drift the camera and could flash a false
  // flat-earth banner at the world edge. Holding the last predicted pose until the fresh baseline snaps in
  // (pendingRebaseline, above) is what makes a mobile reconnect seamless instead of glitchy.
  inputAcc += dt;
  const frozen = !net.connected || pendingRebaseline;
  if (frozen) inputAcc = 0;
  let guard = 0;
  while (!frozen && inputAcc >= INPUT_DT && guard++ < 5) {
    const inp = hud!.sampleInput();
    // First-60s breadcrumbs (front C): read the RAW thumb input BEFORE auto-sail may overwrite it below, so these
    // reflect the CAPTAIN's own hands, not the quartermaster. Each is a once-per-session latch.
    if (alive) {
      if (!firstHelmSent && (hud!.manualSteerActive() || Math.abs(inp.rudder) > 0.04)) { firstHelmSent = true; telemetrySeaControl('helm'); }
      if (!firstSailSent) {
        if (seaSailBaseline < 0) seaSailBaseline = inp.sailTarget;
        else if (Math.abs(inp.sailTarget - seaSailBaseline) > 0.05) { firstSailSent = true; telemetrySeaControl('sail'); }
      }
      // TAP-TO-SAIL IS ALSO TAKING CONTROL. The Helm Coach's 'chart' step teaches a fresh captain to tap the map
      // and let the quartermaster steer (autoSail) — so the helm/sail below are SYNTHESIZED, the raw thumb never
      // moves, and the two beacons above never fire even though the captain absolutely commanded the ship. Those
      // are exactly the captains carried to a mark and living past 60 s (sea_60s) while sea_control read 0. A
      // plotted course (hud.waypoint) is set ONLY by the captain (stampCourse → chart tap / Sail-Home), never by
      // autoSail (which only reads/clears it), so this is a clean, false-positive-free "first real input".
      if (!firstChartSent && hud!.waypoint) { firstChartSent = true; telemetrySeaControl('chart'); }
    }
    // HUD 2.0 (audit fix): the single most frequent action gets a haptic tick on its rising edge —
    // firing a broadside is edge-triggered in the button bits, so this fires exactly once per volley.
    if (inp.buttons & (Btn.FIRE_LEFT | Btn.FIRE_RIGHT)) { haptic(18); if (!firstShotSent) { firstShotSent = true; telemetrySeaShot(); } }
    // SMART AUTO-SAIL (upgrade of frente 6d): the chart course is now a full quartermaster (autosail.ts) —
    // it ROUTES AROUND islands (A* over the SAME solid-obstacle set the prediction collides with), holds the
    // helm AND the sheets, and STOPS at the mark (sails furled + anchor down) instead of sailing past it.
    // Still pure client-side input synthesis on the SAME C_INPUT path a thumb uses — zero wire, the server
    // stays fully authoritative. hud.waypoint remains the single source of truth; ANY manual input (helm,
    // sail trim, anchor, retap on the mark) hands the ship straight back to the captain.
    if (hud!.waypoint && alive && prediction.hasBaseline) {
      const b = prediction.body;
      const cmd = autoSail.update(hud!.waypoint, b.x, b.y, b.heading, b.speed, INPUT_DT);
      if (cmd) {
        if (cmd.engaged) { hud!.raiseAnchor(); autoSailTrimRef = hud!.sail; } // weigh anchor + snapshot the trim
        if (hud!.manualSteerActive() || hud!.anchor || hud!.sail !== autoSailTrimRef) {
          hud!.clearWaypoint(); // the captain took the wheel/sheets/hook — release silently (as before)
          autoSail.clear();
        } else if (cmd.arrived) {
          hud!.dropAnchor(); // drop the hook for real (latches the button's ANCHORED state) — rides the same Btn.ANCHOR bit; chain SFX via the lastAnchor edge below
          inp.anchor = true; inp.buttons |= Btn.ANCHOR; inp.sailTarget = 0; inp.rudder = 0;
          hud!.clearWaypoint('⚓ Arrived — anchor down, sails furled');
        } else if (cmd.noRoute) {
          hud!.clearWaypoint('🧭 No clear route to that mark, captain');
        } else if (cmd.aground) {
          hud!.clearWaypoint('⚓ Run aground — take the wheel, captain');
        } else {
          inp.rudder = cmd.rudder;
          inp.sailTarget = cmd.sailTarget;
        }
      }
    } else if (!hud!.waypoint) autoSail.clear();
    lastRudder = inp.rudder;
    seq += 1;
    net.sendInput({ seq, clientTimeMs: now, rudder: inp.rudder, sailTarget: inp.sailTarget, buttons: inp.buttons, elevation: inp.elevation });
    prediction.applyInput(seq, { rudder: inp.rudder, sailTarget: inp.sailTarget, anchor: inp.anchor }, net.wind, effSpeedMul, effTurnMul);
    inputAcc -= INPUT_DT;
  }

  const t = serverSeconds();

  // camera + render center
  let cx = net.worldSize / 2, cz = net.worldSize / 2, chead = 0;
  const localView = (alive && prediction.hasBaseline)
    ? { x: prediction.body.x, y: prediction.body.y, heading: prediction.body.heading, sail: prediction.body.sail, cls: net.shipClass }
    : null;
  if (localView) { cx = localView.x; cz = localView.y; chead = localView.heading; }
  else if (net.selfAuth) { cx = net.selfAuth.x; cz = net.selfAuth.y; chead = net.selfAuth.heading; }

  // ease the renderer toward the authoritative storm intensity BEFORE syncShips, so the ship bob
  // (which samples the storm-scaled ocean) and the rendered sea agree this frame.
  renderer.setStorm(weatherPreview.storm ?? net.stormIntensity, dt);
  renderer.setWind(net.wind.dir, net.wind.strength); // frente 3b: streaks stream with the authoritative wind
  const remotes = net.getInterpShips();
  // push equipped cosmetics: your own from the authoritative market state (updates instantly on equip),
  // everyone else's from the SPAWN-cached ids. The renderer tints hull/sails per ship.
  const selfCos = net.market ? { hull: net.market.equippedHull, sail: net.market.equippedSail, flag: net.market.equippedFlag } : net.cosmetics.get(net.yourId);
  if (selfCos) renderer.setShipCosmetics(net.yourId, selfCos.hull, selfCos.sail, selfCos.flag, net.equippedTrophy); // own figurehead from S_PROFILE (instant on earn/switch)
  for (const [cid, c] of net.cosmetics) if (cid !== net.yourId) renderer.setShipCosmetics(cid, c.hull, c.sail, c.flag, c.trophy);
  const npcIds = new Set<number>(); for (const r of remotes) if (r.flags & ShipFlag.NPC) npcIds.add(r.id); // Onda Marauders: hostile NPC hulls get a red name
  for (const [cid, nm] of net.names) if (cid !== net.yourId) { const isNpc = npcIds.has(cid); renderer.setShipName(cid, nm, isNpc ? '' : (net.tags.get(cid) ?? ''), isNpc, net.badges.get(cid) ?? 0); } // floating [TAG] name over other ships (red for marauders; gold + DEV tag for owner/dev grants — Onda Frota v47)
  renderer.syncShips(localView, net.yourId, remotes, t, rig, net.selfAuth ? net.selfAuth.hp : 100, grp > 0.01);
  const balls = net.getInterpBalls();
  renderer.syncBalls(balls, t);
  // Kill-Cam: record this frame's interpolated state (hero + remotes + balls) + the environment into the rolling
  // buffer (throttled to CAPTURE_HZ internally). This is the SAME interpolated data we just fed the renderer, so
  // the "recording" is a free by-product of rendering — it never touches netcode or the sim (invariant #1).
  killcam?.recordFrame(t,
    localView ? { id: net.yourId, x: localView.x, y: localView.y, heading: localView.heading, speed: prediction.body.speed, sail: localView.sail, hp: net.selfAuth ? net.selfAuth.hp : 100, flags: 0, cls: localView.cls } : null,
    remotes, balls,
    { night: renderer.night, storm: renderer.storm, windDir: net.wind.dir, windStrength: net.wind.strength });
  // near-miss feedback (game juice): an INCOMING interpolated ball that screams within ~8 m of the local hull
  // without landing → a small geyser at the pass point + a stereo whistle. Reuses the balls we already fetched
  // (no new structure); the "approaching" gate naturally excludes our own outgoing broadside.
  if (localView) detectNearMiss(balls, localView.x, localView.y, localView.cls, t, now);

  for (const e of net.drainFx()) {
    if (e.t === EventType.HIT) {
      const mine = e.by === net.yourId;
      const hitMe = e.victim === net.yourId;
      // impact direction = the ball's travel (shooter -> victim); fall back to impact-point -> victim centre
      const vp = hitMe ? localView : remotes.find((r) => r.id === e.victim);
      const sp = mine ? localView : remotes.find((r) => r.id === e.by);
      let dx = 0, dz = 0;
      if (vp && sp) { dx = vp.x - sp.x; dz = vp.y - sp.y; }
      else if (vp) { dx = vp.x - e.x; dz = vp.y - e.y; }
      renderer.spawnHit(e.x, e.y, t, mine, dx, dz);
      killcam?.recordEvent(EventType.HIT, t, e.x, e.y, e.by, e.victim, e.rake ? 1 : 0); // Kill-Cam: the director reads hits to find the fatal blow + hero ball
      if (vp) renderer.shipRecoil(e.victim, dx, dz, mine ? 0.7 : 0.5, t); // cosmetic knockback on the struck hull
      const s = spatialOf(e.x, e.y); audio.hit(s.d, s.pan);
      if (mine) {
        audio.hitConfirm(); renderer.addShake(0.7); renderer.hitStop(0.045); renderer.cameraPunch(3.4); // you landed a hit -> punch
        haptic(e.rake ? [18, 20, 24] : 14);
        const p = renderer.worldToScreen(e.x, e.y, t); // a hit-marker on the target reads "MY shot connected" in a furball
        if (p.onScreen) hud?.hitMarker(p.x, p.y, e.rake);
      }
      if (hitMe) {
        renderer.addShake(1.1); renderer.cameraPunch(2.2); flashDamage(); haptic(30);                             // you took a hit -> bigger jolt
        // HUD 2.0: the directional arc points at the SHOOTER (their live interpolated position); a shot
        // from outside the remote set (despawned same-tick) simply shows no arc — the flash still fires.
        const shooter = remotes.find((r) => r.id === e.by);
        if (shooter) combatFx?.hitFrom(shooter.x, shooter.y);
      }
      if (mine || hitMe) { audio.combatPing(); clientLastCombatMs = performance.now(); combatFx?.noteCombat(performance.now() + 8000); } // battle theme + port combat-lock + combat-focus fade
      // RAKING fire (a ball swept down the keel, biting deep) reads distinctly: a heavier second burst,
      // extra jolt + a callout — so the player learns to cross the enemy's T.
      if (e.rake && (mine || hitMe)) {
        renderer.spawnHit(e.x, e.y, t, mine, dx, dz);
        renderer.addShake(mine ? 0.8 : 1.3); renderer.cameraPunch(mine ? 2.0 : 1.7); audio.hitConfirm();
        if (mine) hud?.toast('🎯 RAKED!');
        else if (hitMe) hud?.toast('💥 Raked!');
      }
    }
    else if (e.t === EventType.SINK) {
      renderer.spawnWreck(e.id, e.x, e.y, t);
      const s = spatialOf(e.x, e.y);
      const iKilled = e.killerId === net.yourId && e.id !== net.yourId; // I sank them
      const iDied = e.id === net.yourId;
      // Kill-Cam: log the sink, and ARM the "★ Save Highlight" button when the local captain is involved (a kill
      // OR their own death). The window freezes ~TAIL later so the sink itself lands on the tape; a tap replays it.
      killcam?.recordEvent(EventType.SINK, t, e.x, e.y, e.id, e.killerId, e.ballId); // v56: c = the FATAL BALL ID — the director rides that exact ball's trajectory (no more guessing)
      if (iKilled || iDied) killcam?.armHighlight(t, net.yourId);
      if (iKilled) {
        // celebrate YOUR kill: gold banner + bounty + victory sting + a heavier punch + haptic
        hud?.killBanner(net.nameOf(e.id), ECONOMY.GOLD_PER_KILL);
        audio.kill();
        renderer.addShake(0.5); renderer.cameraPunch(2.6); renderer.hitStop(0.06);
        haptic([45, 30, 70]);
        // the one-time push ask: a kill is the session's peak positive beat (a beat landing mid-lesson is
        // skipped — the next trigger / the 4-min sea fallback picks it up).
        maybeAskPushLater(PUSH_OPTIN.FIRST_WIN_DELAY_MS);
        telemetryFirstKill(); // P1 funnel: the captain's first kill this session (once)
        showSharePill(); // viral loop: a discreet, once-per-session "share the tale" invite pill after a kill
      } else if (iDied) {
        hud?.toast('☠ You were sunk!'); haptic(150);
      } else {
        hud?.toast('☠ Ship sunk!');
      }
      // killfeed line for any sink that had an attacker (killerId 0 = drowned/flooded with no killer)
      if (e.killerId && e.killerId !== e.id) hud?.killFeed(net.nameOf(e.killerId), net.nameOf(e.id), iKilled);
      // audio follows the 3 phases: a low groan now (damage) -> wood crack + heavy splash at the break -> sinking creaks
      audio.sink(s.d, s.pan);
      window.setTimeout(() => { audio.ram(0.8, s.d, s.pan); audio.splash(s.d, s.pan, true); }, SINK_FX.DAMAGE_S * 1000);
      window.setTimeout(() => audio.sink(s.d, s.pan), (SINK_FX.DAMAGE_S + 0.4) * 1000);
    }
    else if (e.t === EventType.FIRE) {
      const ship = e.id === net.yourId && localView ? localView : remotes.find((r) => r.id === e.id);
      if (ship) { renderer.spawnFire(ship.x, ship.y, t, e.ammo); const s = spatialOf(ship.x, ship.y); audio.fire(e.ammo, s.d, s.pan); }
      killcam?.recordEvent(EventType.FIRE, t, ship ? ship.x : 0, ship ? ship.y : 0, e.id, e.side, e.ammo); // Kill-Cam: broadsides drive the BROADSIDE beat
      if (e.id === net.yourId && localView) { // recoil your own hull away from the muzzle + a small kick (weight)
        // ammo economy: the server drew ROUND while a special type was selected = that stock ran dry and it
        // fell back — surface the (previously silent) fallback, once per depletion.
        if (hud && e.ammo === AmmoType.ROUND && hud.ammoIndex() !== AmmoType.ROUND) hud.notifyAmmoFallback(hud.ammoIndex());
        coach?.notifyFire(); // Helm Coach: a real, server-confirmed broadside completes the aim lesson
        const h = localView.heading;
        const rdx = e.side === 1 ? -Math.sin(h) : Math.sin(h);
        const rdz = e.side === 1 ? Math.cos(h) : -Math.cos(h);
        renderer.shipRecoil(net.yourId, rdx, rdz, 0.22, t);
        renderer.addShake(0.32); renderer.cameraPunch(1.5);
        audio.combatPing(); // firing also keeps the battle music up
      }
    }
    else if (e.t === EventType.RAM) {
      renderer.spawnRam(e.x, e.y, t, e.mag);
      killcam?.recordEvent(EventType.RAM, t, e.x, e.y, e.mag); // Kill-Cam: a ram near the sink → the RAM climax beat
      const s = spatialOf(e.x, e.y), m = e.mag / 255;
      audio.ram(m, s.d, s.pan);
      // collision feel: a ram you're IN jolts the camera (shake + dolly punch + a brief hit-stop),
      // scaled by impact magnitude AND proximity, so a distant scrape barely registers.
      const near = Math.max(0, 1 - s.d / 60);
      if (near > 0.02) {
        renderer.addShake(0.6 + m * 2.2 * near);
        renderer.cameraPunch(0.8 + m * 3.0 * near);
        if (m * near > 0.45) renderer.hitStop(0.05);
        haptic(Math.round(25 + m * near * 90)); // a ram you're in thumps the phone, scaled by impact
      }
    }
    else if (e.t === EventType.GRAPPLE_START) {
      renderer.spawnTether(e.attackerId, e.victimId);
      const s = spatialOf(e.x, e.y); audio.grapple(s.d, s.pan);
      if (e.attackerId === net.yourId) hud?.setGrappleRole('plundering');
      else if (e.victimId === net.yourId) hud?.setGrappleRole('boarded');
    }
    else if (e.t === EventType.GRAPPLE_END) {
      renderer.removeTether(e.attackerId, e.victimId);
      const escaped = e.reason === GrappleEnd.ESCAPED;
      if (e.attackerId === net.yourId) hud?.toast(escaped ? `⛓ They escaped! (+${e.amountStolen})` : `⛓ Plundered +${e.amountStolen}`);
      else if (e.victimId === net.yourId) hud?.toast(escaped ? '⛓ Broke free! Escaped' : `⛓ Boarded! -${e.amountStolen}`);
      // only clear OUR role — a third-party grapple ending in AOI must not hide our own mash button
      if (e.attackerId === net.yourId || e.victimId === net.yourId) hud?.setGrappleRole(null);
    }
    else if (e.t === EventType.PLUNDER) {
      renderer.plunderStream(e.id, e.x, e.y, t); // gold coins from the storehouse to the thief + embers
      if (e.id === net.yourId) { // I'm the raider — track this run for the "Plundered N" banner on exit
        if (!plunderRunActive) { plunderRunActive = true; plunderRunStart = net.self?.gold ?? 0; }
        plunderRunUntil = performance.now() + 1500;
      }
    }
  }

  // async (offline) raid outcomes -> toast + audio feedback
  for (const rr of net.drainRaidResults()) {
    hud?.toast(raidToast(rr));
    if (rr.outcome === RaidOutcome.LOOTED) audio.coins();
    else if (rr.outcome === RaidOutcome.REPELLED || rr.outcome === RaidOutcome.ALERTED) audio.negative();
    else audio.ui();
  }

  // soft aiming assist (client-only; never auto-leads): hold a fire button -> bearing guide + camera zoom
  const aimSideNow = hud!.aimSide();
  renderer.setAimOrbit(aimSideNow); // mobile: swing the camera to view the firing broadside while aiming
  if (aimSideNow >= 0) lastAimSide = aimSideNow;
  aimRamp += ((aimSideNow >= 0 ? 1 : 0) - aimRamp) * Math.min(1, dt / AIM.RAMP);
  if (localView && aimRamp > 0.01) {
    const am = AMMO[hud!.ammoIndex() as AmmoType] ?? AMMO[AmmoType.ROUND];
    const elev = hud!.elevationValue();
    const range = (CANNON.ELEV_RANGE_MIN + (CANNON.ELEV_RANGE_MAX - CANNON.ELEV_RANGE_MIN) * elev) * am.rangeMul;
    renderer.showAimGuide(lastAimSide, localView.x, localView.y, localView.heading, elev, range, t, aimRamp);
    // lead ghost: while actively holding a broadside, mark the intercept point of the most engageable enemy on
    // that side (nearest within CANNON.RANGE + the broadside arc). Target velocity is derived from the snapshot
    // (a remote hull travels along its heading at `speed`), the ball speed from CANNON.SPEED × ammo — the player
    // still leads onto it manually. Pure-math solver in aimlead.ts; 1 reused mesh in the renderer.
    if (aimSideNow >= 0 && remotes.length) {
      const perp = localView.heading + (aimSideNow === 1 ? Math.PI / 2 : -Math.PI / 2);
      const idx = selectLeadTarget(localView.x, localView.y, Math.cos(perp), Math.sin(perp), remotes, CANNON.RANGE, LEAD_ARC_HALF);
      if (idx >= 0) {
        const tgt = remotes[idx]!;
        const ballSpeed = CANNON.SPEED * am.ballSpeedMul;
        const sol = solveIntercept(localView.x, localView.y, tgt.x, tgt.y, Math.cos(tgt.heading) * tgt.speed, Math.sin(tgt.heading) * tgt.speed, ballSpeed);
        if (sol) {
          const d = Math.hypot(tgt.x - localView.x, tgt.y - localView.y);
          const alpha = aimRamp * (1 - Math.min(1, d / CANNON.RANGE) * 0.7) * 0.8; // discreet; fades with distance
          renderer.showLeadGhost(sol.x, sol.y, t, alpha);
        } else renderer.hideLeadGhost();
      } else renderer.hideLeadGhost();
    } else renderer.hideLeadGhost();
  } else {
    renderer.hideAimGuide();
    renderer.hideLeadGhost();
  }

  renderer.updateCamera(cx, cz, chead, t, dt, Math.abs(lastRudder) > 0.15);
  updateEdgeMist(cx, cz, net.worldSize, !!localView); // world-border bruma wall + first-hit toast (#11)
  // wave 36: the shared sim just turned the LOCAL ship around at the map border → flash the flat-earth gag
  if (prediction.hitEdge) {
    prediction.hitEdge = false;
    if (localView && now - edgeBannerAt > EDGE_BANNER_COOLDOWN_MS && now - seaResumedAt > PORT_RESUME_SUPPRESS_MS) {
      edgeBannerAt = now; hud?.edgeBanner();
      renderer.addShake(1.4); renderer.hitStop(0.12); // v37: a light camera heave as the ship comes about
      audio.thunder(0.6); audio.ui();                  // heavier "the world groans" boom + the banner whoosh
    }
  }
  renderer.render(t, dt, cx, cz);
  audio.setNight(renderer.night); // crossfade the music bed to the nocturne when the cycle goes dark
  audio.setStorm(renderer.storm); // wind/rain bed + thunder scale with the (smoothed) storm intensity
  // Onda Velas: the canvas cracking when it comes aback. The renderer already computed how hard YOUR sail is
  // luffing (same number that collapses it on screen), so the sound and the picture can never disagree.
  audio.setSailStress(renderer.sailLuff, renderer.storm);
  // A strike's thunder has finished travelling. It already arrived LATE by its distance (storm.ts); now
  // it also arrives QUIETER, so a far strike rolls and a near one cracks — and the renderer has already
  // kicked the camera for the close ones. One number, three consistent cues.
  if (renderer.consumeThunder()) audio.thunder(renderer.storm * (1 - Math.min(0.62, renderer.thunderDist / 1200)));

  // HUD
  if (hud) {
    const hp = net.selfAuth ? net.selfAuth.hp / 100 : 1;
    hud.setHp(alive ? hp : 0);
    // HUD 2.0: flooding vignette/heartbeat track the live integrity; arcs re-aim as the camera orbits.
    combatFx?.setIntegrity(alive ? hp : 1);
    combatFx?.tick(performance.now());
    hud.update(self, prediction.body.speed, fps, alive, self?.reloadL ?? 0, self?.reloadR ?? 0, net.trophies);
    // Cargo Runs (V1): the ⚖ weight chip (goods aboard + live speed scrub) + the 🏭 HAUL READY cue (warehouse full).
    // When laden, build the per-port delivery PREVIEW (payout × distance mult from YOUR base) so the captain can
    // pick the port worth the risk — all client-side, from the shared payout math (identical to what the server pays).
    let cargoPreview = '';
    const cargoUnitsNow = self?.cargoUnits ?? 0;
    if (cargoUnitsNow > 0) {
      const hb = homeBaseXY();
      const ops2 = net.world?.outposts ?? [];
      if (hb && ops2.length) {
        cargoPreview = ops2.map((o) => {
          const dist = Math.hypot(o.x - hb.x, o.y - hb.y);
          return `${o.name}: +${cargoDeliveryValue(cargoUnitsNow, dist, shipCargoCap)} (×${cargoDistanceMult(dist).toFixed(1)})`;
        }).join('  ·  ');
      }
    }
    hud.setCargoRun(cargoUnitsNow, shipCargoCap, self?.warehouseUnits ?? 0, self?.warehouseCap ?? 0, Math.round((1 - cargoSpeedMul(cargoLoad)) * 100), cargoPreview);
    // First-cargo teaching hint: a laden hold EMPTYING while alive & not respawning is a delivery — retire the
    // "where do I deliver?" hint for good (a sink empties it too, but that sets respawnIn>0, so it's excluded).
    if (cargoUnitsNow < lastCargoUnits && alive && (net.self?.respawnIn ?? 0) <= 0) uxHints?.markCargoDelivered();
    lastCargoUnits = cargoUnitsNow;
    // Helm Coach: feed the live control reads so each lesson advances the moment its control is USED (wheel,
    // sails, 🪣 hold; aim/chart advance via their notify hooks). All state we already hold — zero new reads.
    coach?.tick({ alive, rudder: lastRudder, sailTarget: hud.sail, speed: prediction.body.speed, bailing: hud.bail, dtMs: dt * 1000 });
    const teachingOpen = (coach?.guiding() ?? false) || (primer?.isOpen() ?? false);
    // Contextual survival hints (client-only): emergency bail + weigh-anchor nudges. All reads are state we
    // already hold; anchor/cargo hold while a teaching layer is up (the bail alarm never does — life-or-death
    // fires even mid-lesson). waterLevel = 1 − integrity (0 when dead so no ghost hint).
    uxHints?.tick({
      alive,
      waterLevel: alive ? Math.max(0, 1 - hp) : 0,
      repairing: hud.bail,
      anchored: hud.anchor,
      sailTarget: hud.sail,
      rudder: lastRudder,
      speed: prediction.body.speed,
      cargoUnits: cargoUnitsNow,
      teachingOpen,
    });
    // If the life-or-death 🪣 alarm fires mid-coach, it OWNS the screen (owner rule): the coach card yields and
    // returns when the alarm clears. The Primer manual / the Book covering the HUD parks the card the same way.
    coach?.setSuppressed((uxHints?.debug().kind === 'repair') || (primer?.isOpen() ?? false) || (captainsBook?.isOpen() ?? false));
    // Captain's Opportunities: show the non-linear "what can I do?" leque once the coach is no longer guiding
    // (a veteran sees it immediately; a first-timer only after the lessons end — they never overlap). No wire:
    // every signal already rides the snapshot self-header / S_PROFILE / S_CLAN.
    if (opportunities) {
      opportunities.setActive(!teachingOpen);
      const oppState: OppState = {
        carrying: (net.self?.chestCount ?? 0) > 0,
        holdCount: net.goods.hold.reduce((s, e) => s + e.count, 0),
        gold: net.self?.gold ?? 0,
        doubloons: net.self?.doubloons ?? 0,
        kills: net.self?.kills ?? 0,
        shipClass: net.shipClass,
        brigantineUnlocked: (net.unlockedMask & (1 << ShipClassId.BRIGANTINE)) !== 0,
        inClan: !!net.clan?.inClan,
      };
      opportunities.update(oppState);
    }
    // "berthed at your home harbor" — anchored, near-stopped, not grappled, within your base water
    // radius. Mirrors the server's dockedHome predicate so the gated upgrades a client shows enabled
    // are exactly the ones the server will accept.
    const ownBase = net.bases.find((b) => b.islandId === net.yourIslandId);
    const dockedNow = !!(hud.anchor && localView && alive && ownBase && grp <= 0 &&
      prediction.body.speed < BASE.DOCK_MAX_SPEED &&
      (ownBase.x - localView.x) ** 2 + (ownBase.y - localView.y) ** 2 < BASE.WATER_RADIUS * BASE.WATER_RADIUS);
    hud.setDocked(dockedNow);
    // Frente 1: as you sail home, remind you to anchor to enter your house (the button only shows once berthed).
    if (ownBase && localView && alive) {
      const bd2 = (ownBase.x - localView.x) ** 2 + (ownBase.y - localView.y) ** 2;
      if (bd2 > BASE_HINT_OUTER * BASE_HINT_OUTER) baseHintArmed = true; // sailed off → re-arm for next homecoming
      else if (baseHintArmed && !dockedNow && bd2 < BASE_HINT_INNER * BASE_HINT_INNER) {
        baseHintArmed = false; hud.toast('⚓ Drop anchor at your dock to step into your house');
      }
    }
    // Onda 8: berthed at your OWN island → "Enter House"; anchored at ANOTHER captain's dock → "Visit House"
    // (the server enforces the Armada + "Open to Armada" gate — a rejected ENTER comes back as S_HOUSE_EDIT
    // and is toasted with the reason). The same button serves both, carrying the target base's islandId.
    let visitBase: import('@piratas/protocol').WireBase | null = null;
    if (!dockedNow && hud.anchor && localView && alive && grp <= 0 && prediction.body.speed < BASE.DOCK_MAX_SPEED) {
      for (const b of net.bases) {
        if (b.islandId === net.yourIslandId) continue;
        if ((b.x - localView.x) ** 2 + (b.y - localView.y) ** 2 < BASE.WATER_RADIUS * BASE.WATER_RADIUS) { visitBase = b; break; }
      }
    }
    enterHouseBtn.style.display = (dockedNow || visitBase) ? 'block' : 'none';
    enterHouseBtn.textContent = dockedNow ? '🏠 Enter House' : '🏠 Visit House';
    enterHouseTarget = dockedNow ? HOUSE_OWN_DOCK : (visitBase?.islandId ?? HOUSE_OWN_DOCK);
    for (const res of net.houseEditQueue) { if (!res.ok) hud.toast(res.reason || 'Cannot enter that house'); }
    net.houseEditQueue.length = 0;
    if (storesMenu) {
      storesMenu.docked = dockedNow; // home crafting: only a Lv-8+ workshop (server enforces)
      // farms-demand-first: public craft stations — inside any outpost ring (2 = the Capital's master forges).
      let portState: 0 | 1 | 2 = 0;
      if (localView) for (const o of net.world?.outposts ?? []) {
        if (Math.hypot(o.x - localView.x, o.y - localView.y) <= o.radius) { portState = o.capital ? 2 : 1; break; }
      }
      storesMenu.port = portState;
    }
    renderer.setLocalBerth(dockedNow, ownBase?.x ?? 0, ownBase?.y ?? 0); // moor broadside when berthed
    if (hud.anchor && !lastAnchor) audio.anchor(); // chain rattle + splash on the rising edge of dropping anchor
    lastAnchor = hud.anchor;
    if (!alive && self) hud.setRespawn(self.respawnIn);
    basePanel?.update(dockedNow, alive); // docking rising edge auto-opens the Home Port chapter + banks loot
    // menu content refresh is driven by captainsBook.tick() (only the open chapter re-renders)
    // professions: on each fresh S_PROFESSIONS, diff for XP toasts / level-up banners + light the 🧰 badge
    if (net.professionsVersion !== lastProfNotifyV) { lastProfNotifyV = net.professionsVersion; profNotifier?.onUpdate(net.professions); }
    storesMenu?.syncProfBadge();
    storesMenu?.pollCraftFeedback(); // craft-juice: correlate S_GOODS deltas → craft success/fail cues (runs each frame)
    // wave 2b: the HOLD chip — total gathered materials carried (piratable until banked at home).
    // Fix 4 frente 2: the chip is now tappable — cache its breakdown text per S_GOODS version.
    if (net.goodsVersion !== lastHoldDetailV) { lastHoldDetailV = net.goodsVersion; holdDetailText = holdSummaryText(net.goods.hold); }
    hud.setHold(net.goods.hold.reduce((s, e) => s + e.count, 0), holdDetailText);
    // Fix 4 frente 2: the 💰 CHEST chip — persistent "chest aboard" flag (the pickup toast fades)
    hud.setChest(net.self?.chestCount ?? 0);
    // wave 2b: storm essence collected (risk gather) — toast on the hold increase
    {
      const seNow = net.goods.hold.find((e) => e.id === MaterialId.STORM_ESSENCE)?.count ?? 0;
      if (lastStormEssence >= 0 && seNow > lastStormEssence) { hud.toast('⚡ Storm essence collected!'); haptic([20, 30]); }
      lastStormEssence = seNow;
    }
    // wave 2b: consumable buffs — the ⚗ quick-sip menu (from base stockpile) + active buff badges
    hud.setConsumables(net.goods.base.filter((e) => CONSUMABLE_IDS.includes(e.id) && e.count >= 1).map((e) => {
      const b = buffOf(e.id), m = materialById(e.id);
      // Prefer the material's raster icon when it has one (the fish species) so the sip menu shows the art;
      // otherwise fall back to the buff's emoji, then the material's, then a flask.
      const icon = (m && isIconPath(m.icon)) ? m.icon : (b?.icon ?? m?.icon ?? '⚗');
      return { id: e.id, count: Math.floor(e.count), icon, label: `${m?.name ?? ''} — ${b?.label ?? ''}` };
    }));
    hud.setBuffs(net.self?.drinkBuff ?? 0, net.self?.oilBuff ?? 0, net.self?.buffS ?? 0, net.self?.fishBuff ?? 0, net.self?.fishBuffS ?? 0, net.self?.fishCdS ?? 0);
    // wave 2b: the contextual CAST/REEL/DIVE button + gulls bed + fishing/diving feedback
    if (net.dynamicWrecksVersion !== lastDynamicWrecksV) { lastDynamicWrecksV = net.dynamicWrecksVersion; renderer.syncDynamicWrecks(net.dynamicWrecks); }
    if (net.bottlesVersion !== lastBottlesV) { lastBottlesV = net.bottlesVersion; renderer.syncBottles(net.bottles); }
    // wave 3.5: the White Whale follows its latest marker (`?whale=` swaps in the synthetic preview marker)
    if (whalePreviewMode === 'off') renderer.setWhale(net.whale);
    else {
      // the circle's centre is frozen on the first frame: the marker must travel at a CONSTANT world velocity
      // (exactly like the server's) or the client's dead-reckoning would be validated against a lie.
      if (whalePreviewT0 < 0 && localView) { whalePreviewT0 = t; whalePreviewOrigin = { x: cx, z: cz }; }
      const m = whalePreviewT0 < 0 ? null
        : previewMarkerAt(t - whalePreviewT0, whalePreviewOrigin.x, whalePreviewOrigin.z, whalePreviewMode === 'die');
      renderer.setWhale(m);
      if (!m && whalePreviewT0 >= 0 && !whalePreviewDead) { whalePreviewDead = true; renderer.whaleFelled(); hud.toast('🐋 The White Whale has been felled!'); }
    }
    renderer.setKraken(KRAKEN.ENABLED ? krakenStateAt(Date.now()) : null); // Kraken spectacle — gated until the sea monster model is final
    // wave 3.8 (+ `?eye=1` preview: one Eye anchored off the bow the first frame we know where we are)
    if (weatherPreview.eye && !previewEye && net.selfAuth) previewEye = { x: net.selfAuth.x + 300, y: net.selfAuth.y + 140 };
    { const eye = net.eye ?? previewEye; const eyeOn = !!eye; if (eyeOn !== lastEyeOn) { lastEyeOn = eyeOn; renderer.setEye(eye); if (eyeOn) { hud.toast('🌀 The eye of the storm has opened — calm, rich waters within.'); } } }
    renderer.setDigMarker(net.digX); // wave 3.9: the treasure-map X
    for (const d of net.digQueue) {
      hud.toast(`⛏ Buried hoard unearthed! +${d.gold} 🪙`); audio.coins(); haptic([30, 40, 60]);
      if (d.capped) hud.toast('⚠ Cargo full — some gold washed away'); // Onda Carona: no more silent capping
    }
    net.digQueue.length = 0;
    for (const w of net.whaleQueue) {
      // the marker stops the same frame the kill lands, so setWhale(null) has already started it LEAVING —
      // whaleFelled() upgrades that into the death beat (belly-up, a wash of foam, then down) before anything drew.
      if (w.state === WhaleState.DEAD) { renderer.whaleFelled(); hud.toast('🐋 The White Whale has been felled!'); audio.whaleCall(); }
      else if (w.state === WhaleState.GONE) { hud.toast('🐋 The White Whale sounds and is gone…'); }
      else { hud.toast('🐋 The White Whale breaches — the hunt is on!'); audio.whaleCall(); localNotify('🐋 The White Whale!', 'The hunt is on — set sail for ambergris.'); if (net.whale) hud.pingCursed(net.whale.x, net.whale.y); }
    }
    net.whaleQueue.length = 0;
    // wave 3.6: a captain crossed the WANTED threshold — alert the whole sea
    for (const b of net.bountyQueue) { const nm = net.names.get(b.id) ?? 'A captain'; hud.wantedBanner(nm, b.bounty); audio.bountyWanted(); }
    net.bountyQueue.length = 0;
    // wave 5.4c: a kill streak — announced map-wide (mine celebrates, a rival's warns)
    for (const k of net.killStreakQueue) {
      if (k.id === net.yourId) { hud.toast(`🔥 ${k.count}-kill streak! A bounty's on your head.`); audio.kill(); haptic([40, 30, 60]); }
      else { const nm = net.names.get(k.id) ?? 'A captain'; hud.toast(`⚔ ${nm} is on a ${k.count}-kill streak — take their head!`); audio.bountyWanted(); }
    }
    net.killStreakQueue.length = 0;
    hud.setWanted(net.self?.bounty ?? 0, BOUNTY.WANTED_THRESHOLD);
    hud.setRepairPlanks(net.self?.repairPlanks ?? 0); // wave 3.10: carried repair planks
    // wave 3.7: offer an escort contract when a merchant convoy is within range
    { let convoyNear = false;
      if (localView) for (const s of remotes) { if (net.tags.get(s.id) !== 'CONVOY') continue; if (Math.hypot(s.x - localView.x, s.y - localView.y) <= CONVOY.ESCORT_RANGE) { convoyNear = true; break; } }
      hud.setEscortAvailable(convoyNear); }
    // wave 3.11/3.12: freight at any outpost; the auction house only at the Capital — feed the Book's chapter
    // gating (Freight/Auction show disabled-with-reason on the Contents page until eligible).
    { let atOutpost = 0, atCapital = false;
      if (localView) for (const o of net.world?.outposts ?? []) { if (Math.hypot(o.x - localView.x, o.y - localView.y) <= o.radius) { atOutpost = o.id; atCapital = !!o.capital; break; } }
      freightMenu?.setAvailable(atOutpost);
      auctionMenu?.setAvailable(atCapital); }
    // wave 6: preload the port GLBs on approach, offer the "Enter Port" button in an outpost ring, and
    // show "N captains in port" so the walkable hub's population is visible from the sea.
    {
      let atOutpostId = 0, nearDist = Infinity, nearCapital = Infinity;
      if (localView) for (const o of net.world?.outposts ?? []) {
        const d = Math.hypot(o.x - localView.x, o.y - localView.y);
        if (d <= o.radius && !atOutpostId) atOutpostId = o.id;
        if (d < nearDist) nearDist = d;
        if (o.capital && d < nearCapital) nearCapital = d;
      }
      const nearForPreload = nearDist < OUTPOST_RADIUS + 280;
      if (nearForPreload && !lastPortPreloadNear) void portScene?.preload();
      lastPortPreloadNear = nearForPreload;
      // Onda 6.5: entering the capital BAY preloads the modular kit so the ~1s entry fade hides the load
      const nearCapitalBay = nearCapital < CAPITAL.PRELOAD_R;
      if (nearCapitalBay && !lastCapitalPreloadNear) void portScene?.preloadCapital();
      lastCapitalPreloadNear = nearCapitalBay;
      const hp01 = net.selfAuth ? net.selfAuth.hp / 100 : 1;
      const combatLocked = (performance.now() - clientLastCombatMs) <= PORT.COMBAT_LOCK_MS;
      const eligible = alive && atOutpostId > 0 && hp01 > 1 - PORT.MAX_WATER_LEVEL && grp <= 0 && !combatLocked;
      // the CAPITAL has no button — sailing across its harbour-mouth faixa is the intent (server-detected)
      enterPortBtn.style.display = (eligible && !isCapitalOutpost(atOutpostId)) ? 'block' : 'none';
      if (atOutpostId > 0) {
        const n = net.portPop[atOutpostId - 1] ?? 0;
        // Front 2 fix ("you can't enter Port Royal"): the Capital shows NO Enter-Port button — entry is the
        // harbour-mouth faixa — so a captain sitting in the ring had no idea how, and a silent eligibility gate
        // (recent combat / flooding / grapple) looked like the port was simply broken. Guide them at the ring:
        // tell them to sail through the mouth, or WHY they're blocked. Villages keep the "N captains in port" chip.
        if (isCapitalOutpost(atOutpostId)) {
          portPopChip.textContent =
            !alive ? `⚓ ${n} captain${n === 1 ? '' : 's'} in Port Royal`
            : hp01 <= 1 - PORT.MAX_WATER_LEVEL ? '⚓ Bail out your hull to enter Port Royal'
            : grp > 0 ? '⚓ Break the grapple to enter Port Royal'
            : combatLocked ? '⚓ Leave combat to enter Port Royal'
            : '⚓ Sail through the harbour mouth to enter Port Royal';
        } else {
          portPopChip.textContent = `⚓ ${n} captain${n === 1 ? '' : 's'} in port`;
        }
        portPopChip.style.display = 'block';
      } else portPopChip.style.display = 'none';
    }
    captainsBook?.tick(); // wave 5: the meta tome (War Journal, Season, Codex, Almanac, Settings)
    dawnReport?.maybeShow(net); // wave 5.4a: the login "what happened while you were away" scroll
    updateEventBanner(); // wave 5.4d: today's world-event chip
    // wave 5.1: your revenge target is at sea — a hunt marker on the chart (+ a throttled toast)
    for (const m of net.revengeMarkQueue) { hud.pingRaid(m.x, m.y); }
    if (net.revengeMarkQueue.length) {
      if (performance.now() - lastRevengeToast > 20000) { hud.toast('🔥 Your revenge target sails nearby — hunt them down!'); lastRevengeToast = performance.now(); }
      net.revengeMarkQueue.length = 0;
    }
    // wave 3.1: a fished-up Message in a Bottle — reveal its note (parchment) + a coin cue when it pays
    for (const m of net.bottleMsgQueue) { hud.bottleNote(m.kind, m.text); audio.pageTurn(); if (m.kind === BottleKind.COIN) audio.coins(); }
    net.bottleMsgQueue.length = 0;
    {
      const act = net.self?.activity ?? 0;
      let shoalD = Infinity, wreckD = Infinity, shoalX = 0, shoalY = 0;
      if (localView) {
        for (const s of net.world?.shoals ?? []) { const d = Math.hypot(s.x - localView.x, s.y - localView.y); if (d < shoalD) { shoalD = d; shoalX = s.x; shoalY = s.y; } }
        for (const w of net.world?.wrecks ?? []) { const d = Math.hypot(w.x - localView.x, w.y - localView.y); if (d < wreckD) wreckD = d; }
        for (const w of net.dynamicWrecks.values()) { const d = Math.hypot(w.x - localView.x, w.y - localView.y); if (d < wreckD) wreckD = d; }
      }
      const inShoal = shoalD < FISHING.RING_R;
      const slowFish = prediction.body.speed < FISHING.CAST_MAX_SPEED;
      const nearWreck = wreckD < WRECK.DIVE_R && prediction.body.speed < WRECK.DIVE_MAX_SPEED;
      // wave 3.9: at the treasure-map X → DIG
      const digD = net.digX && localView ? Math.hypot(net.digX.x - localView.x, net.digX.y - localView.y) : Infinity;
      const atDig = digD < CARTOGRAPHER.DIG_R && prediction.body.speed < CARTOGRAPHER.DIG_MAX_SPEED;
      if (act === 4) hud.setContextAction('Digging…', '⛏', true);
      else if (act === 2) hud.setContextAction('REEL!', '🎣', true);
      else if (act === 1) hud.setContextAction('Waiting…', '🎣', false);
      else if (act === 3) hud.setContextAction('Surface', '🤿', false);
      else if (atDig && alive) hud.setContextAction('Dig Here!', '⛏', true);
      else if (inShoal && slowFish && alive) hud.setContextAction('Cast Line', '🎣', false);
      else if (nearWreck && alive) hud.setContextAction('Dive Wreck', '🤿', false);
      else hud.setContextAction(null);
      audio.setGulls(shoalD < 130 ? Math.max(0, 1 - shoalD / 130) : 0);
      // fishing-polish: the cast/wait/fight is now VISIBLE — line + cork in the world (renderer) and the
      // parchment LINE-TENSION plate on the HUD (gold PERFECT band = the reel telegraph). Client theatre
      // only: timings/odds stay the server's; the plate's clock starts on the act 1→2 edge we see here.
      if (localView) renderer.setFishing(act === 1 || act === 2 ? act : 0, localView.x, localView.y, localView.heading, shoalX, shoalY);
      else renderer.setFishing(0, 0, 0, 0, 0, 0);
      if (act === 2 && lastActivity !== 2) fishBiteAtMs = performance.now();
      hud.setFishing(act, act === 2 ? (performance.now() - fishBiteAtMs) / 1000 : 0, FISHING.REEL_WINDOW_S, FISHING.PERFECT_S);
      if (act === 1 && lastActivity === 0) audio.fishingCast();          // cast a line
      // ---- 🤿 Onda diving-polish: the dive is a SEQUENCE now — splash in, the sea closes over the hull
      // (abyssal ocean tint + underwater vignette + diver bubble column, all render/DOM-only), then on a
      // successful surfacing the salvage is ITEMIZED on a parchment card (hold diff → raster icons). ----
      if (act === 3 && lastActivity !== 3) {                             // splash in — going under
        audio.diveSplash();
        diveHoldBefore = holdCounts(net.goods.hold);                     // the "before" side of the salvage diff
        diveGoodsV = net.goodsVersion;
      }
      if (act === 3) {
        diveMaxProgress = Math.max(diveMaxProgress, net.self?.activityProgress ?? 0);
        if (localView) renderer.setDiveFx(true, localView.x, localView.y); // bubbles boil around the hull
        diveFx.set(true);                                                  // teal depths breathe in from the edges
      } else {
        renderer.setDiveFx(false);
        diveFx.set(false);
      }
      if (lastActivity === 3 && act !== 3) {                             // surfaced — back into daylight
        audio.surfaceGasp();
        if (diveMaxProgress > 0.9) { diveLootDeadline = performance.now() + 2500; haptic([30, 40]); } // watch for the haul
        else { diveHoldBefore = null; }                                  // abandoned dive — nothing to itemize
        diveMaxProgress = 0;
      }
      // the post-dive S_GOODS landed → itemize the haul on the parchment card; deadline → plain toast fallback
      if (diveLootDeadline > 0) {
        if (net.goodsVersion !== diveGoodsV && diveHoldBefore) {
          const gains = diffHoldGain(diveHoldBefore, net.goods.hold);
          const items = gains.map((g) => { const m = materialById(g.id); return { icon: m?.icon ?? null, name: m?.name ?? 'Salvage', qty: g.qty }; });
          if (items.length) { hud.lootReveal('Wreck Salvage', 'Hauled up from the deep', items); audio.coins(); }
          else hud.toast('🤿 Wreck salvage aboard!'); // hold was full / nothing fit — keep the honest headline
          diveLootDeadline = 0; diveHoldBefore = null;
        } else if (performance.now() > diveLootDeadline) {
          hud.toast('🤿 Wreck salvage aboard!');
          diveLootDeadline = 0; diveHoldBefore = null;
        }
      }
      lastActivity = act;
    }
    for (let i = 0; i < net.fishBiteQueue.length; i++) { audio.fishingBite(); haptic([20, 25]); }
    net.fishBiteQueue.length = 0;
    for (const c of net.fishCatchQueue) {
      renderer.fishingResult(c.tier !== FishTier.ESCAPED, c.tier >= FishTier.RARE); // splash + the haul arcing aboard (or a dark flick darting away)
      if (c.tier === FishTier.ESCAPED) { hud.toast('🎣 It slipped the hook…'); audio.negative(); }
      else if (fishSpeciesRarity(c.item)) {
        // A buffed fish SPECIES → the art reveal card (tier-tinted; the legendary Roe gets the gold moment).
        audio.fishingCatch();
        hud.fishReveal(c.item);
        haptic(c.tier >= FishTier.RARE ? [30, 40, 60] : 12);
      } else {
        // A pearl / figurehead / fragment / generic FISH — keep the plain headline toast.
        audio.fishingCatch();
        const nm = c.item === 255 ? 'a legendary figurehead' : (materialById(c.item)?.name ?? 'a fish');
        const pre = c.tier === FishTier.JACKPOT ? '⭐ JACKPOT — ' : c.tier === FishTier.RARE ? '✨ Rare — ' : '🐟 Caught ';
        hud.toast(pre + nm + '!');
        haptic(c.tier >= FishTier.RARE ? [30, 40, 60] : 12);
      }
    }
    net.fishCatchQueue.length = 0;
    // Top Captains / Sea Journal have no sea-HUD panel (owner: HUD limpo) — both render in the Book's Ship's Log.
    if (net.trophyMask !== lastTrophyMask) { if (lastTrophyMask >= 0 && net.trophyMask > lastTrophyMask) { hud.toast('🏆 A new figurehead adorns your bow!'); audio.coins(); } lastTrophyMask = net.trophyMask; }
    // Renown floater: when the 🏆 score moves (sink +10 / death −6 / raid), pop "+X/−X 🏆 Renown" so the cause
    // is legible. The delta is the real (already-floored) change; skip the first assignment (sentinel −1).
    if (net.trophies !== lastRenown) { if (lastRenown >= 0) hud.renownFloat(net.trophies - lastRenown); lastRenown = net.trophies; }
    if (net.raidWarnQueue.length) { const w = net.raidWarnQueue[net.raidWarnQueue.length - 1]!; hud.toast('⚔ Base under attack!'); hud.pingRaid(w.x, w.y); audio.alarm(); localNotify('🏴‍☠️ Base under attack!', 'Sail home to defend your vault.'); net.raidWarnQueue.length = 0; }
    // Cursed Treasure — map-wide convergence event: sighting toast + purple minimap flash; claim FX
    for (const c of net.cursedSpawnQueue) { hud.toast('💀 Cursed Treasure sighted!'); hud.pingCursed(c.x, c.y); audio.negative(); }
    net.cursedSpawnQueue.length = 0;
    for (const c of net.cursedClaimQueue) {
      const near = !!net.selfAuth && (net.selfAuth.x - c.x) ** 2 + (net.selfAuth.y - c.y) ** 2 < CHEST.CURSED_PICKUP_R * CHEST.CURSED_PICKUP_R;
      if (near) cursedClaimedByMe = true; // cursed uses its own flavor — not the risk-waters toast
      hud.toast(near ? '💀 The cursed hoard is YOURS!' : '💀 The cursed treasure was claimed');
      audio.coins();
    }
    net.cursedClaimQueue.length = 0;
    renderer.setCursed(net.cursedChest);
    // plunder result banner: the moment a raid run ends (no PLUNDER for 1.5s = I left the base), if I
    // hauled loot, flash "🏴‍☠️ Plundered N 🪙" (client-side, from my carried-cargo delta over the run).
    if (plunderRunActive && performance.now() > plunderRunUntil) {
      plunderRunActive = false;
      const gained = Math.round((net.self?.gold ?? 0) - plunderRunStart);
      if (gained > 0) { hud.plunderBanner(gained); audio.coins(); }
    }
    // daily contracts: no sea-HUD board anymore (they live in the Book's Ship's Log) — keep the completion toast
    if (net.contractsVersion !== lastContractsV) {
      lastContractsV = net.contractsVersion;
      const claimed = net.contracts.filter((c) => c.claimed).length;
      if (claimed > lastClaimed && lastClaimed >= 0) {
        const just = net.contracts.filter((c) => c.claimed).slice(-1)[0];
        if (just) hud.toast(`📜 Contract complete! +${just.reward} gold`);
      }
      lastClaimed = claimed;
    }
    // treasure: the hoist bar while winching (shared with the wreck-dive bar), then the aboard/sold toasts
    const chestCountNow = net.self?.chestCount ?? 0;
    const chestCap = chestCapFor(net.shipClass as ShipClassId); // chests this hull holds: 2 on the Fluyt/East Indiaman, 1 elsewhere
    const holdFull = chestCountNow >= chestCap;
    const hoistNow = net.self?.hoist ?? 0;
    if ((net.self?.activity ?? 0) === 4) hud.setActivityBar(net.self?.activityProgress ?? 0, '⛏ Digging for the hoard…', "Hold still — you're exposed");
    else if ((net.self?.activity ?? 0) === 3) hud.setActivityBar(net.self?.activityProgress ?? 0, '🤿 Diving the wreck…', "Hold still — you're exposed");
    else hud.setActivityBar(holdFull ? 0 : hoistNow, !holdFull && hoistNow > 0.001 ? '⚓ Hoisting treasure…' : null, "Hold still — you're a sitting duck");
    if (hoistNow > 0.02 && hoistNow < 0.99) haptic(8); // subtle tick while the crane winches
    if (chestCountNow > lastChestCount) {
      // a fresh chest just came aboard (0→1, or a laden hauler's 1→2)
      haptic([30, 40, 60]); audio.coins();
      // risk waters: if this chest came from richer (central / stormy) water, say so — computed from the
      // SAME shared function the server used to scale the payout. Cursed pickups get their own flavor.
      let msg = '💰 Treasure aboard — run it to an outpost ring!';
      if (!cursedClaimedByMe && net.selfAuth) {
        const mul = chestRiskMultiplier(net.selfAuth.x, net.selfAuth.y, net.stormIntensity);
        if (mul > 1.01) msg = `🌊 Rich waters ×${mul.toFixed(1).replace(/\.0$/, '')} — run it to an outpost!`;
      }
      hud.toast(msg);
      cursedClaimedByMe = false;
      chestCapArmed = false; // sitting on the chest just grabbed — don't nag until they move to another one
      maybeAskPushLater(PUSH_OPTIN.FIRST_WIN_DELAY_MS); // first hoisted chest = an early, easy positive beat to offer notifications
    }
    else if (chestCountNow < lastChestCount && (net.self?.respawnIn ?? 0) <= 0) {
      hud.toast('⚓ Treasure sold!'); audio.coins();
      maybeAskPushLater(PUSH_OPTIN.FIRST_WIN_DELAY_MS); // an outpost SALE is a victory beat too — offer the opt-in here (item 5)
    }
    lastChestCount = chestCountNow;
    // Chest-cap feedback: a hull carries `chestCap` chests at once (1, or 2 on a hauler) — server-authoritative.
    // Parking over ANOTHER chest with the hold already FULL does nothing, silently (the owner's original report).
    // We know the count from the snapshot + every chest position from world data, so explain it with no wire cue:
    // arm when clear of chests, fire once when a captain slows over another chest with a full hold.
    if (holdFull && chestsFlat) {
      let nearChest = false;
      const capR2 = (CHEST.PICKUP_R + 6) ** 2;
      for (const c of chestsFlat) { const dx = c.x - cx, dz = c.y - cz; if (dx * dx + dz * dz < capR2) { nearChest = true; break; } }
      if (!nearChest) chestCapArmed = true; // moved clear of all chests — re-arm the reminder
      else if (chestCapArmed && prediction.body.speed <= CHEST.HOIST_MAX_SPEED + 1) {
        chestCapArmed = false;
        hud.toast(chestCap > 1 ? '💰 Hold full — sell your chests at an outpost ring to grab another' : '💰 Chest slot full — sell your chest at an outpost ring to grab another');
      }
    } else chestCapArmed = false;
    // soft-cap: one toast the moment you cross each daily earn tier (day-roll resets dailyEarned -> re-arms)
    const de = net.self?.dailyEarned ?? 0;
    if (de < lastDailyEarned) lastDailyEarned = 0; // new day (or fresh spawn) — re-arm the tier toasts
    if (de >= ECONOMY.DAILY_EARN_CAP && lastDailyEarned < ECONOMY.DAILY_EARN_CAP) hud.toast('⚓ Daily hold full — reduced earnings until tomorrow');
    else if (de >= ECONOMY.SOFT_CAP.TIER1_UNTIL && lastDailyEarned < ECONOMY.SOFT_CAP.TIER1_UNTIL) hud.toast('🌙 Deep waters — earnings now a trickle');
    lastDailyEarned = de;
    // (v54: the passive-farm cap toasts left with the dead farmTier field — the farm makes cargo, not gold.)
    // frentes 2 + 3a: the ship's-log gauge — speed (knots) + sail trim + the wind's bearing relative to the bow.
    hud.drawSpeedo(prediction.body.speed, prediction.body.sail, net.wind.dir, chead, hud.anchor, topSpeedMs(net.shipClass, net.loadout.speedLvl));
    const myTag = net.clan?.inClan ? net.clan.tag : '';
    let allyIds: Set<number> | undefined;
    if (myTag) { allyIds = new Set(); for (const [id, t] of net.tags) if (t === myTag) allyIds.add(id); }
    for (const p of net.clanPingQueue) hud.pingClan(p.x, p.y);
    net.clanPingQueue.length = 0;
    // ⚑ Armada chart markers: toast the one-shot server note (cap hit / permission refusal), and refresh
    // the chart's marker view-model only when the list or the clan context actually changed.
    if (net.marksNote) { hud.toast('⚑ ' + net.marksNote); net.marksNote = ''; }
    const marksKey = `${net.marksVersion}:${net.clanVersion}`;
    if (marksKey !== clanMarksVmKey) {
      clanMarksVmKey = marksKey;
      const myPid = net.clan?.inClan ? net.clan.myId : '';
      clanMarksVm = net.marks.map((m) => ({ id: m.id, icon: m.icon, x: m.x, y: m.y, label: m.label, author: m.authorName, mine: !!myPid && m.authorId === myPid }));
    }
    const rally = net.clan?.inClan && (net.clan.rallyX > 0 || net.clan.rallyY > 0) ? { x: net.clan.rallyX, y: net.clan.rallyY } : null;
    if (!chestsFlat && net.world) chestsFlat = net.world.islands.flatMap((i) => i.chests.map((c) => ({ x: c.x, y: c.y })));
    // trade-wind lanes = the outpost triangle (consecutive posts, wrapping) — same order the sim circulates
    if (!tradeLanesFlat && net.world && net.world.outposts.length >= 2) {
      const ops = net.world.outposts;
      tradeLanesFlat = ops.map((o, i) => { const b = ops[(i + 1) % ops.length]!; return { ax: o.x, ay: o.y, bx: b.x, by: b.y }; });
    }
    // CARRYING a chest OR laden with cargo → the nearest outpost to run it to (the "onde vendo?" answer).
    // Squared-distance pick over the outpost list; drawn as a bold pulsing "SELL" ring + course line on the
    // chart. Client-only, no wire. (Cargo delivery pays more at a FARTHER port, but the nearest is the safe cash-in.)
    let cashInAt: { x: number; y: number } | null = null;
    if (((net.self?.chestCount ?? 0) > 0 || (net.self?.cargoUnits ?? 0) > 0) && net.world && net.world.outposts.length) {
      let best = Infinity;
      for (const o of net.world.outposts) { const dx = o.x - cx, dy = o.y - cz, d2 = dx * dx + dy * dy; if (d2 < best) { best = d2; cashInAt = { x: o.x, y: o.y }; } }
    }
    hud.drawMinimap({
      selfX: cx, selfY: cz, selfHeading: chead, windDir: net.wind.dir, ships: remotes, allyIds,
      chests: chestsFlat ?? undefined, shoals: net.world?.shoals, cursed: net.cursedChest,
      territory: net.territory, myTag, rally,
      // ⚑ Armada chart markers (v54): pins + this captain's rights (officer/leader may scrub any pin;
      // OFFICER rank = 1 on the ClanRank ladder). inClan gates the long-press gesture with a hint.
      clanMarks: clanMarksVm,
      canModerateMarks: !!(net.clan?.inClan && (net.clan.isLeader || net.clan.role >= 1)),
      inClan: !!net.clan?.inClan,
      islands: net.world?.islands ?? [], outposts: net.world?.outposts ?? [],
      tradeLanes: tradeLanesFlat ?? undefined,
      // Sinks P3: a unified "sight" multiplier for the chart's watch ring — the active oil's viewMul (Spyglass
      // Tonic / the new Wraithlight Draught) × the Lookout's Spyglass charm's +minimap range. Render-only cue,
      // never the network AOI.
      sightMul: (buffOf(net.self?.oilBuff ?? 0)?.viewMul ?? 1) * amuletMinimapMul(net.gacha?.equippedAmulet),
      bottles: net.bottles.size ? [...net.bottles.values()] : undefined,
      whale: net.whale ? { x: net.whale.x, y: net.whale.y } : null,
      eye: net.eye,
      digX: net.digX,
      homeBase: homeBaseXY(),
      cashInAt,
      spectral: net.self?.spectral, // v54 Wraithlight ghost-sight: rich wrecks + ghost fleet whispers
    });
  }

  requestAnimationFrame(loop);
}

// periodic ping for RTT
setInterval(() => net.sendPing(), 1000);

/* --------------------------------- menu --------------------------------- */

let menuEl: HTMLElement | null = null;
let menuRaf = 0;
let menuLast = performance.now();
let menuArmed = false;
// Game title. The hero crest art is deliberately text-free, so changing this one
// constant rebrands the whole menu without regenerating any art. Token ticker: $SOS.
const GAME_NAME = 'Sea of Solana';

// the cinematic backdrop loop: drives the renderer's hero-galleon showcase behind the menu DOM
function menuLoop(now: number): void {
  if (!menuEl) return;
  const dt = Math.min(0.05, (now - menuLast) / 1000); menuLast = now;
  renderer.showcase(now / 1000, dt);
  if (menuArmed && renderer.consumeThunder()) audio.thunder(0.5); // crack on the lightning flashes
  menuRaf = requestAnimationFrame(menuLoop);
}

function showMenu(): void {
  const auto = autoStart();
  if (auto.auto) { startGame(auto.name); return; }
  telemetryMenuShown(); // P1 funnel: the title menu was presented (a real human, not an ?auto=1 headless boot)
  if (!document.getElementById('menu-style')) {
    const st = document.createElement('style'); st.id = 'menu-style';
    st.textContent = `
      @font-face{ font-family:'Cinzel'; font-style:normal; font-weight:700; font-display:swap; src:url('/fonts/cinzel-700.woff2') format('woff2'); }
      @font-face{ font-family:'Cinzel'; font-style:normal; font-weight:900; font-display:swap; src:url('/fonts/cinzel-900.woff2') format('woff2'); }
      @keyframes ccIn { 0%{ opacity:0; transform:translateY(12px) } 100%{ opacity:1; transform:none } }
      @keyframes ccCrestGlow { 0%,100%{ filter:drop-shadow(0 8px 22px rgba(0,0,0,.7)) drop-shadow(0 0 16px rgba(0,200,5,.3)) } 50%{ filter:drop-shadow(0 8px 22px rgba(0,0,0,.7)) drop-shadow(0 0 30px rgba(0,200,5,.55)) } }
      @keyframes ccBtnPulse { 0%,100%{ box-shadow:0 8px 24px rgba(0,0,0,.5), 0 0 0 0 rgba(0,200,5,.5) } 70%{ box-shadow:0 8px 24px rgba(0,0,0,.5), 0 0 0 18px rgba(0,200,5,0) } }
      #menu .cc-crest{ width:min(44vw,180px); object-fit:contain; animation:ccIn .6s ease-out, ccCrestGlow 5s ease-in-out infinite .6s; }
      #menu .cc-name{ font-family:'Cinzel',Georgia,serif; font-weight:900; font-size:clamp(38px,8.4vw,74px); line-height:.98; letter-spacing:.03em; margin:6px 0 0; background:linear-gradient(180deg,#fff6d8,#f2c94c 46%,#c8901e); -webkit-background-clip:text; background-clip:text; color:transparent; filter:drop-shadow(0 3px 10px #000) drop-shadow(0 0 16px rgba(0,200,5,.26)); animation:ccIn .6s ease-out .08s both; }
      #menu .cc-tag{ font-size:11.5px; letter-spacing:.32em; color:#bfe9cf; text-transform:uppercase; margin-top:9px; text-shadow:0 2px 6px #000; animation:ccIn .6s ease-out .16s both; }
      #menu .cc-form{ display:flex; flex-direction:column; align-items:center; gap:12px; width:min(86vw,318px); margin-top:22px; animation:ccIn .6s ease-out .24s both; }
      #menu #name{ padding:14px 16px; border-radius:12px; border:1px solid rgba(0,200,5,.3); background:rgba(6,16,20,.62); color:#fff; font-size:16px; width:100%; box-sizing:border-box; text-align:center; backdrop-filter:blur(6px); }
      #menu #name::placeholder{ color:#9fb2c8; }
      #menu #name:focus{ outline:none; border-color:#00c805; box-shadow:0 0 0 3px rgba(0,200,5,.22); }
      #menu #play{ padding:15px 0; width:100%; border-radius:13px; border:0; background:linear-gradient(180deg,#00e006,#00a804); color:#032109; font-weight:800; font-size:18px; letter-spacing:.06em; font-family:'Cinzel',Georgia,serif; cursor:pointer; animation:ccBtnPulse 2.4s ease-out infinite; transition:transform .12s ease,filter .12s ease; }
      #menu #play:hover{ transform:translateY(-2px) scale(1.02); filter:brightness(1.08); }
      #menu #play:active{ transform:translateY(1px) scale(.98); }
      #menu #status{ opacity:.85; font-size:13px; height:18px; text-shadow:0 2px 6px #000; }
      #menu .cc-load{ width:100%; margin-top:2px; display:none; flex-direction:column; align-items:center; gap:6px; animation:ccIn .6s ease-out .3s both; }
      #menu .cc-load .tip{ font-family:Georgia,'Times New Roman',serif; font-size:12.5px; line-height:1.34; color:#f2e7c6; letter-spacing:.01em; text-shadow:0 1px 3px #000; max-width:min(86vw,320px); min-height:34px; display:flex; align-items:center; justify-content:center; gap:7px; text-align:center; transition:opacity .38s ease; }
      #menu .cc-load .tip b{ color:#ffdd82; font-weight:700; }
      #menu .cc-load .tip .ti{ font-size:16px; flex:0 0 auto; filter:drop-shadow(0 1px 2px rgba(0,0,0,.55)); }
      #menu .cc-load .lbl{ font-family:Georgia,'Times New Roman',serif; font-style:italic; font-size:11.5px; color:#cdbda0; letter-spacing:.03em; text-shadow:0 1px 3px #000; opacity:.9; }
      #menu .cc-load .trk{ width:80%; height:5px; border-radius:3px; background:rgba(18,24,18,.55); box-shadow:inset 0 1px 2px rgba(0,0,0,.55); overflow:hidden; }
      #menu .cc-load .fil{ height:100%; width:0; background:linear-gradient(90deg,#c8901e,#f2d98c); transition:width .28s ease; }
      #menu .cc-legal{ margin-top:14px; font-size:11px; letter-spacing:.02em; color:#8ea6bd; text-shadow:0 2px 6px #000; animation:ccIn .6s ease-out .34s both; }
      #menu .cc-legal a{ color:#cfe3f2; text-decoration:underline; text-underline-offset:2px; }
      #menu .cc-legal a:hover{ color:#fff; }
      #menu #play:disabled{ animation:none; filter:grayscale(.5) brightness(.66); opacity:.6; cursor:not-allowed; box-shadow:0 6px 16px rgba(0,0,0,.4); }
      #menu #play:disabled:hover{ transform:none; filter:grayscale(.5) brightness(.66); }
      #menu .cc-agree{ display:flex; align-items:flex-start; gap:9px; width:min(86vw,318px); margin-top:14px; font-size:12px; line-height:1.42; color:#cfe0ef; text-align:left; text-shadow:0 2px 6px #000; animation:ccIn .6s ease-out .3s both; cursor:pointer; }
      #menu .cc-agree input{ appearance:auto; margin:1px 0 0; width:18px; height:18px; flex:none; accent-color:#00c805; cursor:pointer; }
      #menu .cc-agree a{ color:#cfe3f2; text-decoration:underline; text-underline-offset:2px; }
      #menu .cc-agree a:hover{ color:#fff; }
      #menu .cc-crewline{ margin-top:10px; font-size:13px; color:#bfe9cf; letter-spacing:.02em; text-shadow:0 2px 6px #000; animation:ccIn .6s ease-out .2s both; }
      #menu .cc-crewline b{ color:#f2d98c; }`;
    document.head.appendChild(st);
  }
  const m = document.createElement('div');
  m.id = 'menu';
  // First-session-only backup heads-up (discreet, one-time): the captain will live in this browser, and can
  // be backed up with a recovery code from the Captain's Book → Settings → Account.
  let showBackup = false;
  try { showBackup = !localStorage.getItem('sor:backupNoticeSeen'); } catch { showBackup = false; }
  const backupLine = showBackup
    ? `<p class="cc-legal cc-backup">⚓ Your captain will live in this browser — back it up later from the Book (⚙ Settings → Account).</p>`
    : '';
  // Age gate (13+ for the game). Frictionless: one checkbox folds the age declaration into the existing
  // Terms & Privacy consent. Persisted in localStorage (`sor:ageOk`) so it is asked exactly once — returning
  // captains who predate this gate get it on their next visit. Headless `?auto=1` boots never reach here
  // (they return above), so loadbots are unaffected.
  let ageOk = false;
  try { ageOk = localStorage.getItem('sor:ageOk') === '1'; } catch { ageOk = false; }
  const legalLine = ageOk
    ? `<p class="cc-legal">By setting sail you agree to our <a href="/site/terms.html" target="_blank" rel="noopener">Terms</a> &amp; <a href="/site/privacy.html" target="_blank" rel="noopener">Privacy</a>.</p>`
    : `<label class="cc-agree"><input type="checkbox" id="agecheck" /><span>I'm 13 or older and I agree to the <a href="/site/terms.html" target="_blank" rel="noopener">Terms</a> &amp; <a href="/site/privacy.html" target="_blank" rel="noopener">Privacy</a>.</span></label>`;
  // cinematic: a soft top + strong bottom vignette, letting the live 3D storm showcase be the hero
  m.style.cssText = 'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:5vh 16px;box-sizing:border-box;color:#eaf6ff;font-family:inherit;z-index:10;background:linear-gradient(180deg,rgba(2,8,16,.42) 0%,rgba(2,8,16,0) 26%,rgba(2,8,16,.12) 58%,rgba(2,8,16,.82) 100%);';
  // Deep-link "sail with me": a discreet line when the captain arrived via a friend's invite link (?crew=).
  const crew = crewParam();
  const escCrew = crew.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));
  const crewLine = crew ? `<div class="cc-crewline">⚓ Joining <b>${escCrew}</b>'s waters…</div>` : '';
  m.innerHTML = `
    <img class="cc-crest" src="/menu/crest.webp" alt="" />
    <h1 class="cc-name">${GAME_NAME}</h1>
    <div class="cc-tag">☠ Naval Battle Royale · Solana ☠</div>
    ${crewLine}
    <div class="cc-form">
      <input id="name" placeholder="Captain name" maxlength="20" autocomplete="off" />
      <button id="play">⚓ SET SAIL</button>
      <div id="status"></div>
      <div id="loadbar" class="cc-load"><div class="tip"></div><div class="lbl"></div><div class="trk"><div class="fil"></div></div></div>
    </div>
    ${legalLine}${backupLine}`;
  if (showBackup) { try { localStorage.setItem('sor:backupNoticeSeen', '1'); } catch { /* private mode */ } }
  document.body.appendChild(m);
  hideBootSplash(); // the title menu is up — retire the instant loading splash
  maybeWebviewBanner(); // discreet "open in your browser" nudge if we're in an in-app webview that DID render
  menuEl = m;
  loadBarEl = m.querySelector('#loadbar') as HTMLElement;
  updateLoadBar(); // sync any progress already made while the menu style was building
  // first interaction arms audio (browsers gate it): the menu storm bed + thunder kick in
  m.addEventListener('pointerdown', () => { if (menuArmed) return; menuArmed = true; armAudio(); audio.setStorm(0.5); }, { passive: true });
  const input = m.querySelector('#name') as HTMLInputElement;
  // Name persistence (bug fix — "the name won't save"): a returning captain sees the name they last set sail
  // with, pre-filled from localStorage, so they never have to retype it; a fresh device gets a fun random
  // default. `nameChosen` tracks whether the captain actually PICKED this name — a restored saved name counts,
  // an untouched random default does NOT. On SET SAIL an untouched default is sent as '' so the server keeps
  // the captain's existing profile name (never clobbers a returning captain with a throwaway "Captain####").
  let savedName = '';
  try { savedName = localStorage.getItem('sor:name') || ''; } catch { savedName = ''; }
  input.value = seedMenuName(savedName);
  let nameChosen = !!savedName;
  input.addEventListener('input', () => { nameChosen = true; });
  // Age gate wiring: until the captain is confirmed 13+, SET SAIL is inert and the checkbox is its key.
  const playBtn = m.querySelector('#play') as HTMLButtonElement;
  const ageChk = m.querySelector('#agecheck') as HTMLInputElement | null;
  if (ageChk) {
    playBtn.disabled = true;
    ageChk.addEventListener('change', () => { playBtn.disabled = !ageChk.checked; });
    // Reading the Terms/Privacy must NOT count as consent: keep link taps from toggling the label's checkbox.
    m.querySelectorAll('.cc-agree a').forEach((a) => a.addEventListener('click', (e) => e.stopPropagation()));
  }
  const sail = (): void => {
    if (!ageOk && !(ageChk?.checked)) { ageChk?.focus(); return; } // gate not cleared — no-op
    if (!ageOk) { try { localStorage.setItem('sor:ageOk', '1'); } catch { /* private mode */ } ageOk = true; }
    const typed = input.value.trim();
    // Only a name the captain actually chose is sent (and cached) as a rename; an untouched random default is
    // sent as '' so the server keeps any existing profile name (see the server-side RENAME note in room.ts).
    if (shouldPersistName(nameChosen, typed)) { try { localStorage.setItem('sor:name', typed); } catch { /* private mode */ } }
    startGame(nameToSend(nameChosen, typed));
  };
  playBtn.addEventListener('click', sail);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') sail(); });
  // start the cinematic backdrop
  menuLast = performance.now();
  menuRaf = requestAnimationFrame(menuLoop);
}
// Asset loading progress → a discreet parchment-gold bar on the menu. Assets start streaming at
// renderer construction (before the menu exists), so we cache the latest counts and sync on menu build.
let loadBarEl: HTMLElement | null = null;
let assetLoaded = 0, assetTotal = 0;
// A live rotation of short EN game tips while the sea charts in — turns the wait into a teachable moment
// (retention lever) instead of a bare progress bar. All strings are in-game English (owner law). Kept punchy;
// the payoff word/number rides <b> so it pops gold. The rotator only ticks while the bar is visible.
const MENU_TIPS: { i: string; t: string }[] = [
  { i: '🌬️', t: 'Sail <b>with the wind</b> at your back for up to <b>+2.5× speed</b>.' },
  { i: '🎯', t: 'Broadsides hit hardest from the <b>side</b> — turn to bring your guns to bear.' },
  { i: '💰', t: 'Treasure chests pay <b>120 doubloons</b> — dig fast, sail faster.' },
  { i: '⚔️', t: 'Every kill pays <b>50ⓓ + bounty</b> — win streaks make you <b>WANTED</b>.' },
  { i: '🛠️', t: 'Patch leaks the moment you take fire — a <b>flooded hull</b> is a sunk hull.' },
  { i: '⚓', t: 'Cash your haul at an <b>outpost ring</b> before a rival sinks you.' },
  { i: '🏴‍☠️', t: 'Sink a <b>WANTED</b> captain to seize the bounty on their head.' },
  { i: '🗺️', t: 'No lobby, no cap — up to <b>160 captains</b> share these waters.' },
];
let tipTimer: number | null = null;
let tipIdx = (Math.random() * MENU_TIPS.length) | 0;
function paintTip(): void {
  const tip = loadBarEl?.querySelector('.tip') as HTMLElement | null;
  if (!tip) return;
  const t = MENU_TIPS[tipIdx % MENU_TIPS.length]!;
  tip.innerHTML = `<span class="ti">${t.i}</span><span>${t.t}</span>`;
}
function startTipRotation(): void {
  if (tipTimer != null) return;
  paintTip();
  tipTimer = window.setInterval(() => {
    const tip = loadBarEl?.querySelector('.tip') as HTMLElement | null;
    if (!tip) return;
    tip.style.opacity = '0';
    window.setTimeout(() => { tipIdx = (tipIdx + 1) % MENU_TIPS.length; paintTip(); tip.style.opacity = '1'; }, 380);
  }, 4200);
}
function stopTipRotation(): void { if (tipTimer != null) { clearInterval(tipTimer); tipTimer = null; } }
function updateLoadBar(): void {
  if (!loadBarEl) return;
  const lbl = loadBarEl.querySelector('.lbl') as HTMLElement;
  const fil = loadBarEl.querySelector('.fil') as HTMLElement;
  if (assetTotal > 0 && assetLoaded < assetTotal) {
    loadBarEl.style.display = 'flex';
    const pct = Math.round((assetLoaded / assetTotal) * 100);
    lbl.textContent = `⚓ Charting the seas… ${pct}%`;
    fil.style.width = `${pct}%`;
    startTipRotation();
  } else {
    loadBarEl.style.display = 'none'; // not started yet, or every asset is in
    stopTipRotation();
  }
}
renderer.onAssetProgress = (loaded, total) => { assetLoaded = loaded; assetTotal = total; updateLoadBar(); };

function hideMenu(): void {
  if (menuRaf) { cancelAnimationFrame(menuRaf); menuRaf = 0; }
  stopTipRotation();
  menuEl?.remove(); menuEl = null;
  loadBarEl = null;
  renderer.endShowcase();
  if (menuArmed) audio.setStorm(0); // drop the menu storm bed once we're in the game
}
function showStatus(s: string): void { const el = menuEl?.querySelector('#status') as HTMLElement | undefined; if (el) el.textContent = s; }

window.addEventListener('resize', () => renderer.resize());
// expose for headless smoke test
(window as unknown as { __cc: unknown }).__cc = { net, renderer, prediction, audio, get portScene() { return portScene; }, get houseScene() { return houseScene; }, capitalLayout: layoutForOutpost(1), settings: { get: getSettings, set: setSetting }, openHudEdit: () => { void openHudForge(); },
  // Smart auto-sail (E2E/headless): read .route/.destination/.active, or lay a course exactly like a chart
  // tap would (same waypoint path + engage bookkeeping) — proves routing/arrival without a pointer.
  autosail: autoSail, sailTo: (x: number, y: number) => { if (hud) { hud.waypoint = { x, y }; hud.onWaypointSet?.(x, y); } },
  // Dev/E2E only: pin the whole world's time-of-day (sea sky + walkable city + house interior) to `p` (0..1),
  // or null to resume the live wall-clock cycle. All three read the SAME phase, so this proves they agree.
  setCityPhase: (p: number | null) => { if (!(import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) return; renderer.setSkyPhase(p); portScene?.setPhaseOverride(p); houseScene?.setPhaseOverride(p); },
  // 🌌 Dev/E2E: pin the aurora display (0..1) and its breakup beat, or null to resume the live schedule.
  // The night + storm gates still apply — pair with setCityPhase(0) to photograph one at midnight.
  setAurora: (amt: number | null, breakup: number | null = null) => renderer.setAurora(amt, breakup),
  aurora: () => renderer.aurora,
  stats: () => ({ ...renderer.stats(), connected: net.connected, ships: renderer.stats().ships }),
  // Onda wallet-fix (verification, text-only): prove the sea→port dispose frees GPU memory (memInfo before/after
  // releaseSeaAssets), confirm light mode is engaged, and exercise the release/restore round-trip headlessly.
  memInfo: () => renderer.memInfo(),
  lightMode: () => lightModeActive,
  releaseSeaAssets: () => renderer.releaseSeaAssets(),
  restoreSeaAssets: () => renderer.restoreSeaAssets(),
  contextLost: () => renderer.contextLost,
  // Onda Voz: per-peer WebRTC voice diagnostics (ICE/connection state, TURN armed?) — text-only, no screenshot needed.
  voiceStats: () => voiceEngine?.voiceStats() ?? null,
  voice: () => voiceEngine,
  // A Draga (E2E/visual proof): DRIVE the reveal ritual with a canned roll (pure client-side theatre — grants
  // nothing; the server owns every real grant). kind/ref default to a matching cosmetic per rarity.
  playDraga: (rarity = 0, kind = 0, ref = 0, dup = false, shards = 0) => dragaScene?.play({ rarity, kind, ref, dup, shardsGained: shards }, () => {}),
  draga: () => dragaScene,
  // Onboarding v4 (E2E/visual proof): reach the Helm Coach + the Primer manual — restart/open are the same
  // paths the Book buttons use (client-only theatre; nothing persists from an ephemeral replay).
  get coach() { return coach; },
  get primer() { return primer; },
  // Fish icons (E2E/visual proof): the catch-reveal card is pure client theatre — DRIVE it with a species id
  // (MaterialId 19..22), and reach the live HUD / Ship's Stores to prove the inventory <img> icons.
  get hud() { return hud; },
  fishReveal: (species = MaterialId.HARBOR_MACKEREL) => hud?.fishReveal(species),
  // Contextual hints (E2E/visual proof): force the emergency bail / weigh-anchor nudge on screen — pure theatre,
  // it drives no gameplay and never touches the gate's persisted lifetime counter. clearHint() tears it down.
  get uxHints() { return uxHints; },
  previewHint: (kind: 'repair' | 'anchor' | 'cargo' = 'repair') => uxHints?.previewHint(kind),
  get storesMenu() { return storesMenu; },
  // Cinematic Kill-Cam (E2E / visual + render proof): DRIVE a highlight capture off the current rolling buffer
  // (freezes the tape, composes a director cut, plays + records). `killcam()` exposes the live player; `debug()`
  // reports the composed cut (beats / mood / music / bullet-time / clip-ready) — all text, no screenshot needed.
  triggerHighlight: (heroId?: number) => killcam?.triggerNow(heroId) ?? false,
  // Kill-Cam TUNING harness (Phase 1): dump the CURRENT rolling tape as a serializable KillTape (the exact frozen
  // window the director consumes + measured feature labels) for the offline bench in tools/killcam-bench. Debug hook
  // only — zero boot cost, invisible to players, never touches wire/sim. e.g. copy(JSON.stringify(__cc.dumpKill())).
  dumpKill: (heroId?: number) => killcam?.dumpTape(heroId) ?? null,
  killcam: () => killcam,
  killcamDebug: () => killcam?.debug() ?? null,
  // Seed + play a scripted kill (cannon = bullet-time / ram / flood) over the live sea — E2E + render proof.
  // `opts.dist` stages CLOSE-range (e.g. 6) vs long-range kills; `opts.sinkDespawn` drops the victim after the
  // sink to reproduce the real despawn. e.g. __cc.killcamDemo('cannon', { dist: 6 }).
  killcamDemo: (kind: 'cannon' | 'ram' | 'flood' = 'cannon', opts: { dist?: number; sinkDespawn?: boolean } = {}) => {
    const c = net.selfAuth ? { x: net.selfAuth.x, y: net.selfAuth.y } : { x: net.worldSize / 2, y: net.worldSize / 2 };
    return killcam?.demo(kind, serverSeconds(), c.x, c.y, net.yourId, opts) ?? false;
  },
  // NUMERIC render-proof (no playback): compose the cut + walk the real camera over the real ocean, returning
  // duration / NaN / hull-clip / camera-fly / flip as text. e.g. __cc.killcamAnalyze('cannon', { dist: 3 }).
  killcamAnalyze: (kind: 'cannon' | 'ram' | 'flood' = 'cannon', opts: { dist?: number; sinkDespawn?: boolean } = {}) => {
    const c = net.selfAuth ? { x: net.selfAuth.x, y: net.selfAuth.y } : { x: net.worldSize / 2, y: net.worldSize / 2 };
    return killcam?.analyze(kind, serverSeconds(), c.x, c.y, net.yourId, opts) ?? null;
  },

  // Kill-cam STYLE preset (parametric director, Phase 2): Cinematic (default, more overhead) / Hype (punchy, lower) /
  // Tactical (almost all overhead, drama minimal). Same engine, different weights. e.g. __cc.killcamStyle('tactical').
  killcamStyle: (s?: KillcamStyle) => { if (s) setKillcamStyle(s); return getKillcamStyle(); },

  // Play a real kill TAPE (an acervo corpus tape or a `dumpKill()` export) through the real renderer + parametric
  // director — WATCH a real kill (owner feel check / headless render-proof). e.g. __cc.killcamPlayTape(tapeObj).
  killcamPlayTape: (tape: KillTape) => killcam?.playTape(tape) ?? false };

// Creative Mode — the OWNER editors. Activated ONLY by `?editor=…`, lazy-loaded so the player bundle never
// grows and nothing here runs in the normal game. Three modes, each its own chunk:
//   ?editor=world → the whole-sea World Editor (offline, from the shared world generator)
//   ?editor=base  → the starter player-base Template Editor
//   ?editor=1 | village | capital → the walkable City/port Layout Editor (phase 1)
// All boot onto the shared renderer — no menu, no network, no wire.
const editorParam = new URL(location.href).searchParams.get('editor');
if (editorParam) {
  muteBtn.style.display = 'none'; // hide the sea's audio toggle behind the tool UI
  const p = editorParam.toLowerCase();
  const boot = p === 'world'
    ? import('./editor/worldeditor.js').then((m) => m.bootWorldEditor(renderer))
    : p === 'islands'
      ? import('./editor/islandeditor.js').then((m) => m.bootIslandEditor(renderer))
      : p === 'landmark'
        ? import('./editor/landmarkeditor.js').then((m) => m.bootLandmarkEditor(renderer))
        : p === 'base'
          ? import('./editor/baseeditor.js').then((m) => m.bootBaseEditor(renderer))
          : p === 'house'
            ? import('./editor/houseeditor.js').then((m) => m.bootHouseEditor(renderer))
            : import('./editor/layouteditor.js').then((m) => m.bootEditor(renderer, { layout: editorParam }));
  void boot.then(() => hideBootSplash()).catch((err) => { console.error('[editor] failed to boot', err); showMenu(); });
} else {
  initTelemetry(); telemetryBoot(); // P1 funnel analytics: session start (no wire, no PII — see telemetry.ts)
  showMenu();
}
